import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../assets/fonts/screenshot-fonts.css";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  FormGroup,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  SelectChangeEvent,
  Slider,
  Tab,
  Tabs,
  TextField,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  AutoAwesome as GenerateIcon,
  ExpandMore as ExpandMoreIcon,
  RestartAlt as ResetIcon,
  UploadFile as ImportIcon,
  IosShare as ExportIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteIcon,
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import AppService from "../services/AppService";
import AIService from "../services/AIService";
import ScreenshotService from "../services/ScreenshotService";
import type { App } from "../types/app";
import { buildAppContextLines } from "../utils/appContextPrompt";
import {
  AndroidDeviceFrameId,
  DeviceDimension,
  DEFAULT_STORE_TEMPLATE_STYLE,
  GeneratedStoreScreenshot,
  ScreenshotTargetPlatform,
  StoreGradientPresetId,
  StoreFontStyle,
  StorePreviewDeviceId,
  StoreBackgroundPattern,
  StoreTemplateLayoutId,
  WaveStyleVariant,
  SCREENSHOT_LOCALES,
  STORE_DEVICE_FRAME_PROFILES,
  STORE_SCREENSHOT_TEMPLATES,
  StoreScreenshotTemplateStyle,
  StoreScreenshotTemplate,
  StoreScreenshotGenerationMode,
  StoreScreenshotTemplateScreenSlot,
  StoredStoreScreenshotAsset,
  StoreScreenshotGenerationProgress,
  StoreScreenshotGenerationVersion,
  StoreScreenshotZipExportJob,
  StoreCompositionLayerKind,
  StoreCompositionMaskType,
  GooglePlayScreenshotPublishMode,
  GooglePlayScreenshotPublishResponse,
  GooglePlayScreenshotValidationResponse,
  ScreenshotPublishDestination,
} from "../types/screenshot";
import {
  filterStoreLocalesBySupport,
  type StoreLocaleSupportFilter,
} from "../utils/storeLocales";

type UploadedImage = {
  id: string;
  file: File;
  dataUrl: string;
  aiContext: string;
  manualHeading: string;
  manualSubtitle: string;
};

type ScreenSlotInput = {
  slotId: string;
  slotName: string;
  slotOrder: number;
  enabled: boolean;
  style: StoreScreenshotTemplateStyle;
  dataUrl: string;
  fileName: string;
  aiContext: string;
  manualHeading: string;
  manualSubtitle: string;
  overlayImages: Array<{
    id: string;
    dataUrl: string;
    fileName: string;
    xRatio: number;
    yRatio: number;
    widthRatio: number;
    heightRatio: number;
    opacity: number;
    borderRadiusRatio: number;
  }>;
};

type ScreenshotJobRecord = {
  id: string;
  appId: string;
  appName: string;
  templateId: string;
  templateName: string;
  targetPlatform: ScreenshotTargetPlatform;
  locales: string[];
  dimensionLabels: string[];
  sourceCount: number;
  expectedOutputCount: number;
  generatedCount: number;
  savedVersionId?: string;
  savedVersionNumber?: number;
  zipDownloadUrl?: string;
  zipFileName?: string;
  zipCreatedAt?: string;
  status: "queued" | "processing" | "completed" | "failed";
  progress: StoreScreenshotGenerationProgress | null;
  createdAt: string;
  updatedAt: string;
  error?: string;
};

type ScreenshotPublishSectionKey =
  | "overview"
  | "target"
  | "validate"
  | "confirm"
  | "danger"
  | "result";

type ScreenshotPreviewViewerState = {
  open: boolean;
  title: string;
  localeLabel: string;
  images: Array<{ url: string; sourceName?: string }>;
  currentIndex: number;
};

const formatScreenshotDeviceLabel = (
  dimension: Pick<DeviceDimension, "platform" | "name" | "width" | "height">,
) =>
  `${dimension.platform.toUpperCase()} - ${dimension.name} (${dimension.width}x${dimension.height})`;

const ZIP_JOB_STALE_MS = 10 * 60 * 1000;

const SCREENSHOT_JOBS_STORAGE_KEY = "translatorx.screenshotJobs";
const SCREENSHOT_JOBS_FILTER_STORAGE_KEY = "translatorx.screenshotJobsFilter";
const ALL_APPS_FILTER = "__all_apps__";
const ALL_VALIDATION_LOCALES_FILTER = "__all_locales__";
const GOOGLE_PLAY_LOCALE_REGEX = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;
const MAX_INLINE_GENERATED_SCREENSHOTS = 72;
const APP_STORE_BUNDLE_ID_REGEX =
  /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;

const SCREENSHOT_COPY_MODELS = [
  { value: "", label: `Default (${AIService.getDefaultModel()})` },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
];

const POLISHED_FONT_OPTIONS = [
  {
    label: "SF Pro Display",
    value: '"SF Pro Display", "Inter", "Segoe UI", sans-serif',
  },
  {
    label: "SF Pro Text",
    value: '"SF Pro Text", "Inter", "Segoe UI", sans-serif',
  },
  { label: "Inter", value: '"Inter", "Segoe UI", sans-serif' },
  { label: "Manrope", value: '"Manrope", "Inter", "Segoe UI", sans-serif' },
  {
    label: "Plus Jakarta Sans",
    value: '"Plus Jakarta Sans", "Inter", "Segoe UI", sans-serif',
  },
  { label: "Sora", value: '"Sora", "Inter", "Segoe UI", sans-serif' },
  {
    label: "General Sans",
    value: '"General Sans", "Inter", "Segoe UI", sans-serif',
  },
  { label: "DM Sans", value: '"DM Sans", "Inter", "Segoe UI", sans-serif' },
  { label: "Outfit", value: '"Outfit", "Inter", "Segoe UI", sans-serif' },
  { label: "Overpass", value: '"Overpass", "Inter", "Segoe UI", sans-serif' },
  { label: "Roboto", value: '"Roboto", "Inter", "Segoe UI", sans-serif' },
  { label: "Ubuntu", value: '"Ubuntu", "Inter", "Segoe UI", sans-serif' },
  {
    label: "Playfair Display",
    value: '"Playfair Display", "Inter", "Segoe UI", sans-serif',
  },
  {
    label: "Libre Caslon Text",
    value: '"Libre Caslon Text", "Inter", "Segoe UI", sans-serif',
  },
  { label: "Bungee", value: '"Bungee", "Inter", "Segoe UI", sans-serif' },
];
const COMPOSITION_MASK_OPTIONS: Array<{
  value: StoreCompositionMaskType;
  label: string;
}> = [
  { value: "rounded_rect", label: "Rounded Rectangle" },
  { value: "circle", label: "Circle" },
  { value: "squircle", label: "Squircle" },
  { value: "arch_wave_top", label: "Arch / Wave Top" },
  { value: "arch_bottom", label: "Arch Bottom" },
  { value: "diamond", label: "Diamond" },
  { value: "hexagon", label: "Hexagon" },
  { value: "shield", label: "Shield" },
  { value: "notched_rect", label: "Notched Corners" },
];
const LAYER_KIND_LABELS: Record<StoreCompositionLayerKind, string> = {
  background: "Background",
  main_image: "Main Image",
  overlay_image: "Overlay Image",
  text_heading: "Heading Text",
  text_subtitle: "Subtitle Text",
};

const BACKGROUND_PATTERN_OPTIONS: Array<{
  value: StoreBackgroundPattern;
  label: string;
}> = [
  { value: "dot_grid", label: "Dot Grid" },
  { value: "diagonal_lines", label: "Diagonal Lines" },
  { value: "soft_waves", label: "Soft Waves" },
  { value: "aurora_glow", label: "Aurora Glow" },
  { value: "diamond_weave", label: "Diamond Weave" },
];
const BACKGROUND_GRADIENT_OPTIONS: Array<{
  value: StoreGradientPresetId;
  label: string;
  css: string;
}> = [
  {
    value: "sunset_blush",
    label: "Sunset Blush",
    css: "linear-gradient(135deg, #FF9A9E 0%, #FAD0C4 52%, #FBC2EB 100%)",
  },
  {
    value: "hot_pink_glow",
    label: "Hot Pink Glow",
    css: "linear-gradient(145deg, #F24C9D 0%, #EE4FA8 56%, #F5C6CF 100%)",
  },
  {
    value: "ocean_depth",
    label: "Ocean Depth",
    css: "linear-gradient(140deg, #0F2027 0%, #203A43 48%, #2C5364 100%)",
  },
  {
    value: "mint_fresh",
    label: "Mint Fresh",
    css: "linear-gradient(130deg, #11998E 0%, #38EF7D 50%, #9EF7D4 100%)",
  },
  {
    value: "royal_plum",
    label: "Royal Plum",
    css: "linear-gradient(145deg, #42275A 0%, #734B6D 55%, #B08ABF 100%)",
  },
  {
    value: "citrus_pop",
    label: "Citrus Pop",
    css: "linear-gradient(135deg, #F7971E 0%, #FFD200 52%, #FFF1A8 100%)",
  },
  {
    value: "midnight_steel",
    label: "Midnight Steel",
    css: "linear-gradient(135deg, #232526 0%, #414345 56%, #6D6F73 100%)",
  },
  {
    value: "sky_lagoon",
    label: "Sky Lagoon",
    css: "linear-gradient(140deg, #00C6FF 0%, #0072FF 58%, #7FB9FF 100%)",
  },
  {
    value: "rose_gold",
    label: "Rose Gold",
    css: "linear-gradient(135deg, #EFB5AE 0%, #C39EA0 52%, #B48A78 100%)",
  },
  {
    value: "emerald_glow",
    label: "Emerald Glow",
    css: "linear-gradient(135deg, #134E5E 0%, #71B280 58%, #A0D9A0 100%)",
  },
  {
    value: "twilight_berry",
    label: "Twilight Berry",
    css: "linear-gradient(138deg, #1D2B64 0%, #D66D75 56%, #F8CDD0 100%)",
  },
  {
    value: "amber_sky",
    label: "Amber Sky",
    css: "linear-gradient(135deg, #F59E0B 0%, #F97316 52%, #FDE68A 100%)",
  },
  {
    value: "violet_dream",
    label: "Violet Dream",
    css: "linear-gradient(140deg, #5B21B6 0%, #8B5CF6 54%, #C4B5FD 100%)",
  },
  {
    value: "coral_reef",
    label: "Coral Reef",
    css: "linear-gradient(135deg, #FF6B6B 0%, #FF9A8B 52%, #FFD6A5 100%)",
  },
  {
    value: "arctic_mist",
    label: "Arctic Mist",
    css: "linear-gradient(135deg, #93C5FD 0%, #BFDBFE 54%, #E0F2FE 100%)",
  },
  {
    value: "lava_glow",
    label: "Lava Glow",
    css: "linear-gradient(138deg, #7F1D1D 0%, #DC2626 52%, #F59E0B 100%)",
  },
  {
    value: "forest_canopy",
    label: "Forest Canopy",
    css: "linear-gradient(138deg, #14532D 0%, #16A34A 54%, #86EFAC 100%)",
  },
  {
    value: "peach_bloom",
    label: "Peach Bloom",
    css: "linear-gradient(136deg, #FB7185 0%, #FDBA74 52%, #FFE4E6 100%)",
  },
  {
    value: "neon_night",
    label: "Neon Night",
    css: "linear-gradient(140deg, #0B1020 0%, #312E81 54%, #22D3EE 100%)",
  },
  {
    value: "cobalt_sunrise",
    label: "Cobalt Sunrise",
    css: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 52%, #F59E0B 100%)",
  },
  {
    value: "lavender_haze",
    label: "Lavender Haze",
    css: "linear-gradient(140deg, #7C3AED 0%, #A78BFA 54%, #F5D0FE 100%)",
  },
];
const WAVE_STYLE_OPTIONS: Array<{ value: WaveStyleVariant; label: string }> = [
  { value: "gentle", label: "Gentle" },
  { value: "balanced", label: "Balanced" },
  { value: "dramatic", label: "Dramatic" },
];
const BASIC_TEXT_COLOR_SWATCHES = [
  "#101828",
  "#FFFFFF",
  "#0F172A",
  "#2563EB",
  "#DC2626",
];
const PATTERN_DEFAULTS: Record<
  StoreBackgroundPattern,
  Pick<
    StoreScreenshotTemplateStyle,
    | "patternColor"
    | "patternOpacity"
    | "patternScale"
    | "dotSizeScale"
    | "waveStyle"
  >
> = {
  dot_grid: {
    patternColor: "#667085",
    patternOpacity: 0.42,
    patternScale: 1,
    dotSizeScale: 1,
    waveStyle: "balanced",
  },
  diagonal_lines: {
    patternColor: "#64748B",
    patternOpacity: 0.38,
    patternScale: 1,
    dotSizeScale: 1,
    waveStyle: "balanced",
  },
  soft_waves: {
    patternColor: "#A5B4FC",
    patternOpacity: 0.4,
    patternScale: 1,
    dotSizeScale: 1,
    waveStyle: "balanced",
  },
  aurora_glow: {
    patternColor: "#7DD3FC",
    patternOpacity: 0.48,
    patternScale: 1,
    dotSizeScale: 1,
    waveStyle: "balanced",
  },
  diamond_weave: {
    patternColor: "#94A3B8",
    patternOpacity: 0.38,
    patternScale: 1,
    dotSizeScale: 1,
    waveStyle: "balanced",
  },
};
const FONT_STYLE_OPTIONS: Array<{ value: StoreFontStyle; label: string }> = [
  { value: "normal", label: "Normal" },
  { value: "italic", label: "Italic" },
];

const ANDROID_DEVICE_FRAME_OPTIONS = STORE_DEVICE_FRAME_PROFILES.filter(
  (device) => device.platform === "android",
);

const TEXT_WEIGHT_OPTIONS = [
  { label: "Normal", value: 500 },
  { label: "Bold", value: 700 },
  { label: "Extra Bold", value: 800 },
];

const FONT_SIZE_PX_OPTIONS = Array.from(
  { length: 57 },
  (_, index) => 8 + index * 2,
);

export default function ScreenshotCreator() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [targetPlatform, setTargetPlatform] =
    useState<ScreenshotTargetPlatform>("both");
  const [selectedTemplate, setSelectedTemplate] =
    useState<StoreScreenshotTemplate>(
      cloneStoreTemplate(STORE_SCREENSHOT_TEMPLATES[0]),
    );
  const [customTemplates, setCustomTemplates] = useState<
    StoreScreenshotTemplate[]
  >([]);
  const [templatePreview, setTemplatePreview] = useState<string>("");
  const [previewHeading, setPreviewHeading] = useState(
    "Track Smarter Every Day",
  );
  const [previewSubtitle, setPreviewSubtitle] = useState("Build better habits");
  const [copyInputMode, setCopyInputMode] = useState<"ai" | "manual">("ai");
  const [useScreenshotContext, setUseScreenshotContext] = useState(true);
  const [copyModel, setCopyModel] = useState<string>("");
  const [previewDeviceId, setPreviewDeviceId] =
    useState<StorePreviewDeviceId>("iphone_15_pro_max");
  const [useCloud, setUseCloud] = useState(false);
  const [selectedLocales, setSelectedLocales] = useState<string[]>(["en"]);
  const [localeSupportFilter, setLocaleSupportFilter] =
    useState<StoreLocaleSupportFilter>("common");
  const [localeSearchQuery, setLocaleSearchQuery] = useState("");
  const [selectedDimensionKeys, setSelectedDimensionKeys] = useState<string[]>(
    [],
  );
  const [generationInputMode, setGenerationInputMode] =
    useState<StoreScreenshotGenerationMode>("single_source");
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [slotInputs, setSlotInputs] = useState<Record<string, ScreenSlotInput>>(
    {},
  );
  const [slotStyledPreviews, setSlotStyledPreviews] = useState<
    Record<string, string>
  >({});
  const [editorPreviewImageId, setEditorPreviewImageId] = useState<string>("");
  const [generatedScreenshots, setGeneratedScreenshots] = useState<
    GeneratedStoreScreenshot[]
  >([]);
  const [libraryAssets, setLibraryAssets] = useState<
    StoredStoreScreenshotAsset[]
  >([]);
  const [libraryVersions, setLibraryVersions] = useState<
    StoreScreenshotGenerationVersion[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [preGeneratingCopy, setPreGeneratingCopy] = useState(false);
  const [previewCopyLoading, setPreviewCopyLoading] = useState(false);
  const [templateCreateLoading, setTemplateCreateLoading] = useState(false);
  const [templateActionLoading, setTemplateActionLoading] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [zipBusyVersionId, setZipBusyVersionId] = useState<string | null>(null);
  const [zipBusyJobId, setZipBusyJobId] = useState<string | null>(null);
  const [clientZipBusyVersionId, setClientZipBusyVersionId] = useState<
    string | null
  >(null);
  const [clientZipBusyJobId, setClientZipBusyJobId] = useState<string | null>(
    null,
  );
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [zipExportJobs, setZipExportJobs] = useState<
    Record<string, StoreScreenshotZipExportJob>
  >({});
  const [generationProgress, setGenerationProgress] =
    useState<StoreScreenshotGenerationProgress | null>(null);
  const [activeGenerationJobId, setActiveGenerationJobId] = useState<
    string | null
  >(null);
  const [screenshotJobs, setScreenshotJobs] = useState<ScreenshotJobRecord[]>(
    [],
  );
  const [selectedJobsAppFilter, setSelectedJobsAppFilter] = useState("");
  const [persistedJobsLoading, setPersistedJobsLoading] = useState(false);
  const [persistedJobsReloadKey, setPersistedJobsReloadKey] = useState(0);
  const [expandedScreenshotJobDates, setExpandedScreenshotJobDates] = useState<
    Record<string, boolean>
  >({});
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishTargetJob, setPublishTargetJob] =
    useState<ScreenshotJobRecord | null>(null);
  const [publishDestination, setPublishDestination] =
    useState<ScreenshotPublishDestination>("google_play");
  const [publishMode, setPublishMode] =
    useState<GooglePlayScreenshotPublishMode>("all_locales");
  const [publishLocale, setPublishLocale] = useState("");
  const [publishSelectedLocales, setPublishSelectedLocales] = useState<
    string[]
  >([]);
  const [validatingGooglePlayPublish, setValidatingGooglePlayPublish] =
    useState(false);
  const [publishingToGooglePlay, setPublishingToGooglePlay] = useState(false);
  const [screenshotValidationResult, setScreenshotValidationResult] =
    useState<GooglePlayScreenshotValidationResponse | null>(null);
  const [screenshotPublishResult, setScreenshotPublishResult] =
    useState<GooglePlayScreenshotPublishResponse | null>(null);
  const [activePublishJobsByVersionId, setActivePublishJobsByVersionId] =
    useState<Record<string, GooglePlayScreenshotPublishResponse>>({});
  const [publishConfirmationChecked, setPublishConfirmationChecked] =
    useState(false);
  const [publishOverrideChecked, setPublishOverrideChecked] = useState(false);
  const [validationOverviewCopied, setValidationOverviewCopied] =
    useState(false);
  const [publishSections, setPublishSections] = useState<
    Record<ScreenshotPublishSectionKey, boolean>
  >({
    overview: true,
    target: true,
    validate: true,
    confirm: true,
    danger: false,
    result: false,
  });
  const [validationLocaleFilter, setValidationLocaleFilter] = useState(
    ALL_VALIDATION_LOCALES_FILTER,
  );
  const [expandedValidationLocales, setExpandedValidationLocales] = useState<
    Record<string, boolean>
  >({});
  const [expandedValidationGroups, setExpandedValidationGroups] = useState<
    Record<string, boolean>
  >({});
  const [screenshotPreviewViewer, setScreenshotPreviewViewer] =
    useState<ScreenshotPreviewViewerState>({
      open: false,
      title: "",
      localeLabel: "",
      images: [],
      currentIndex: 0,
    });
  const [templateCreatorPreview, setTemplateCreatorPreview] = useState("");
  const [templateManagerBusyId, setTemplateManagerBusyId] = useState<
    string | null
  >(null);
  const [templateRenameDrafts, setTemplateRenameDrafts] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState<string | null>(null);
  useToastStateSync({ error });
  const [styleStatus, setStyleStatus] = useState<
    "idle" | "loading" | "saved" | "error"
  >("idle");
  const [styleReady, setStyleReady] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({
    input: true,
    locales: false,
    devices: false,
  });
  const filteredScreenshotLocales = useMemo(() => {
    const normalizedQuery = localeSearchQuery.trim().toLowerCase();

    return filterStoreLocalesBySupport(SCREENSHOT_LOCALES, localeSupportFilter)
      .filter((locale) => {
        if (!normalizedQuery) {
          return true;
        }

        const searchableText = `${locale.code} ${locale.name}`.toLowerCase();
        return searchableText.includes(normalizedQuery);
      })
      .sort((left, right) =>
        left.name.localeCompare(right.name, undefined, {
          sensitivity: "base",
        }),
      );
  }, [localeSearchQuery, localeSupportFilter]);
  const [expandedGeneratedPlatforms, setExpandedGeneratedPlatforms] = useState<
    Record<string, boolean>
  >({});
  const [expandedGeneratedLocales, setExpandedGeneratedLocales] = useState<
    Record<string, boolean>
  >({});
  const [expandedTemplatePhases, setExpandedTemplatePhases] = useState({
    setup: true,
    preview: false,
    background: false,
    typography: false,
    layout: false,
  });
  const [templateCreateMode, setTemplateCreateMode] = useState<"manual" | "ai">(
    "ai",
  );
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateDescription, setNewTemplateDescription] = useState("");
  const [newTemplateLayout, setNewTemplateLayout] =
    useState<StoreTemplateLayoutId>("simple_heading_device");
  const [newTemplateGenerationMode, setNewTemplateGenerationMode] =
    useState<StoreScreenshotGenerationMode>("single_source");
  const [newTemplateSlotCount, setNewTemplateSlotCount] = useState(8);
  const [aiTemplatePrompt, setAiTemplatePrompt] = useState("");
  const [draftTemplateStyle, setDraftTemplateStyle] =
    useState<StoreScreenshotTemplateStyle>(
      cloneStoreTemplate(STORE_SCREENSHOT_TEMPLATES[0]).style,
    );
  const [activeTemplateSlotId, setActiveTemplateSlotId] = useState<string>("");
  const [bulkTargetSlotIds, setBulkTargetSlotIds] = useState<string[]>([]);
  const [templateSlotPreviewImages, setTemplateSlotPreviewImages] = useState<
    Record<string, { dataUrl: string; fileName: string }>
  >({});

  const expectedDimensions = useMemo(
    () =>
      ScreenshotService.getGeneratorDimensionsByTargetPlatform(targetPlatform),
    [targetPlatform],
  );
  const previewDimension = useMemo(() => {
    const profile = STORE_DEVICE_FRAME_PROFILES.find(
      (item) => item.id === previewDeviceId,
    );
    return profile?.previewDimension || expectedDimensions[0];
  }, [previewDeviceId, expectedDimensions]);
  const templateScreenSlots = useMemo(
    () =>
      (selectedTemplate.screenSlots || [])
        .filter((slot) => slot.enabled !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [selectedTemplate.screenSlots],
  );
  const isSlotDesignMode =
    selectedTemplate.generationMode === "screen_slots" &&
    templateScreenSlots.length > 0;
  const activeTemplateSlot = useMemo(
    () =>
      isSlotDesignMode
        ? templateScreenSlots.find(
            (slot) => slot.slotId === activeTemplateSlotId,
          ) || templateScreenSlots[0]
        : null,
    [activeTemplateSlotId, isSlotDesignMode, templateScreenSlots],
  );
  const editorStyle = useMemo(
    () =>
      isSlotDesignMode
        ? activeTemplateSlot?.style || selectedTemplate.style
        : selectedTemplate.style,
    [activeTemplateSlot?.style, isSlotDesignMode, selectedTemplate.style],
  );
  const activeSlotOverlayImages = useMemo(
    () =>
      activeTemplateSlot
        ? slotInputs[activeTemplateSlot.slotId]?.overlayImages || []
        : [],
    [activeTemplateSlot, slotInputs],
  );
  const compositionLayerStack = useMemo(() => {
    const baseStack = Array.isArray(editorStyle.layerStack)
      ? editorStyle.layerStack.map((layer) => ({
          id: layer.id,
          kind: layer.kind,
          visible: layer.visible !== false,
          zIndex: Number(layer.zIndex) || 0,
          refId: layer.refId,
        }))
      : [];
    const hasKind = (kind: StoreCompositionLayerKind) =>
      baseStack.some((layer) => layer.kind === kind);
    if (!hasKind("background")) {
      baseStack.push({
        id: "background",
        kind: "background",
        visible: true,
        zIndex: 0,
        refId: undefined,
      });
    }
    if (!hasKind("main_image")) {
      baseStack.push({
        id: "main_image",
        kind: "main_image",
        visible: true,
        zIndex: 1,
        refId: undefined,
      });
    }
    if (!hasKind("text_heading")) {
      baseStack.push({
        id: "text_heading",
        kind: "text_heading",
        visible: true,
        zIndex: 2,
        refId: undefined,
      });
    }
    if (!hasKind("text_subtitle")) {
      baseStack.push({
        id: "text_subtitle",
        kind: "text_subtitle",
        visible: true,
        zIndex: 3,
        refId: undefined,
      });
    }

    const existingOverlayIds = new Set(
      baseStack
        .filter((layer) => layer.kind === "overlay_image" && layer.refId)
        .map((layer) => layer.refId as string),
    );
    activeSlotOverlayImages.forEach((overlay, index) => {
      if (!existingOverlayIds.has(overlay.id)) {
        baseStack.push({
          id: `overlay_layer_${overlay.id}`,
          kind: "overlay_image",
          visible: true,
          zIndex: 100 + index,
          refId: overlay.id,
        });
      }
    });

    return baseStack
      .map((layer, index) => ({
        ...layer,
        zIndex: index,
      }))
      .sort((left, right) => left.zIndex - right.zIndex);
  }, [activeSlotOverlayImages, editorStyle.layerStack]);
  const headingWeightPresetValue = useMemo(
    () =>
      getPresetSelectValue(
        editorStyle.headingWeight,
        TEXT_WEIGHT_OPTIONS.map((item) => item.value),
      ),
    [editorStyle.headingWeight],
  );
  const subtitleWeightPresetValue = useMemo(
    () =>
      getPresetSelectValue(
        editorStyle.subtitleWeight,
        TEXT_WEIGHT_OPTIONS.map((item) => item.value),
      ),
    [editorStyle.subtitleWeight],
  );
  const headingFontSizePx = useMemo(
    () => ratioToFontPx(editorStyle.headingSizeRatio, previewDimension.width),
    [editorStyle.headingSizeRatio, previewDimension.width],
  );
  const subtitleFontSizePx = useMemo(
    () => ratioToFontPx(editorStyle.subtitleSizeRatio, previewDimension.width),
    [editorStyle.subtitleSizeRatio, previewDimension.width],
  );
  const isScreenSlotsInputMode =
    generationInputMode === "screen_slots" && templateScreenSlots.length > 0;
  const filledSlotInputs = useMemo(
    () =>
      templateScreenSlots
        .map((slot) => slotInputs[slot.slotId])
        .filter((slot) => Boolean(slot?.dataUrl)),
    [templateScreenSlots, slotInputs],
  );
  const activeSourceCount = isScreenSlotsInputMode
    ? filledSlotInputs.length
    : uploadedImages.length;
  const sourcesForCopyValidation = isScreenSlotsInputMode
    ? filledSlotInputs
    : uploadedImages;
  const allTemplateOptions = useMemo(
    () => [
      ...STORE_SCREENSHOT_TEMPLATES.map(cloneStoreTemplate),
      ...customTemplates.map(cloneStoreTemplate),
    ],
    [customTemplates],
  );
  const groupedGeneratedScreenshots = useMemo(() => {
    const platformOrder: Array<"ios" | "android"> = ["ios", "android"];
    const grouped: Record<
      string,
      Record<string, GeneratedStoreScreenshot[]>
    > = {};

    generatedScreenshots.forEach((screenshot) => {
      if (!grouped[screenshot.platform]) grouped[screenshot.platform] = {};
      if (!grouped[screenshot.platform][screenshot.locale])
        grouped[screenshot.platform][screenshot.locale] = [];
      grouped[screenshot.platform][screenshot.locale].push(screenshot);
    });

    return platformOrder
      .filter((platform) => grouped[platform])
      .map((platform) => ({
        platform,
        locales: Object.keys(grouped[platform])
          .sort((a, b) => a.localeCompare(b))
          .map((locale) => ({
            locale,
            items: grouped[platform][locale],
          })),
      }));
  }, [generatedScreenshots]);
  const libraryVersionGroups = useMemo(() => {
    const platformOrder: Array<"ios" | "android"> = ["ios", "android"];
    const assetsByVersion = new Map<string, StoredStoreScreenshotAsset[]>();

    libraryAssets.forEach((asset) => {
      const existing = assetsByVersion.get(asset.versionId) || [];
      existing.push(asset);
      assetsByVersion.set(asset.versionId, existing);
    });

    return [...libraryVersions]
      .sort(
        (a, b) =>
          (b.versionNumber || 0) - (a.versionNumber || 0) ||
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .map((version) => {
        const versionAssets = assetsByVersion.get(version.versionId) || [];
        const grouped: Record<
          string,
          Record<string, StoredStoreScreenshotAsset[]>
        > = {};

        versionAssets.forEach((asset) => {
          if (!grouped[asset.platform]) grouped[asset.platform] = {};
          if (!grouped[asset.platform][asset.locale]) {
            grouped[asset.platform][asset.locale] = [];
          }
          grouped[asset.platform][asset.locale].push(asset);
        });

        return {
          version,
          totalAssets: versionAssets.length,
          platforms: platformOrder
            .filter((platform) => grouped[platform])
            .map((platform) => ({
              platform,
              locales: Object.keys(grouped[platform])
                .sort((a, b) => a.localeCompare(b))
                .map((locale) => ({
                  locale,
                  items: grouped[platform][locale].sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime(),
                  ),
                })),
            })),
        };
      });
  }, [libraryAssets, libraryVersions]);
  const visibleScreenshotJobs = useMemo(() => {
    if (!selectedJobsAppFilter) {
      return [];
    }

    return screenshotJobs
      .filter((job) =>
        selectedJobsAppFilter === ALL_APPS_FILTER
          ? true
          : job.appId === selectedJobsAppFilter,
      )
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
  }, [screenshotJobs, selectedJobsAppFilter]);
  const formatPublishDate = useCallback((value?: Date | string | null) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  const groupedVisibleScreenshotJobs = useMemo(() => {
    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        jobs: ScreenshotJobRecord[];
        timestamp: number;
      }
    >();

    visibleScreenshotJobs.forEach((job) => {
      const parsed = new Date(job.updatedAt || job.createdAt);
      const groupDate = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
      const key = groupDate.toISOString().slice(0, 10);
      const existing = groups.get(key);
      if (existing) {
        existing.jobs.push(job);
        existing.timestamp = Math.max(existing.timestamp, groupDate.getTime());
        return;
      }
      groups.set(key, {
        key,
        label: formatPublishDate(groupDate) || key,
        jobs: [job],
        timestamp: groupDate.getTime(),
      });
    });

    return Array.from(groups.values()).sort(
      (a, b) => b.timestamp - a.timestamp,
    );
  }, [formatPublishDate, visibleScreenshotJobs]);

  const getLocaleDisplayLabel = useCallback((localeCode: string) => {
    const normalized = String(localeCode || "").trim();
    if (!normalized) return "Unknown locale";
    const match = SCREENSHOT_LOCALES.find(
      (item) => item.code.toLowerCase() === normalized.toLowerCase(),
    );
    return match ? `${match.code.toUpperCase()} - ${match.name}` : normalized;
  }, []);

  const getAppForJob = useCallback(
    (job: ScreenshotJobRecord | null) =>
      apps.find((app) => app.appId === job?.appId) || null,
    [apps],
  );

  const getScreenshotPublishValidation = useCallback(
    (job: ScreenshotJobRecord | null) => {
      const blockers: string[] = [];
      const warnings: string[] = [];
      const app = getAppForJob(job);
      const packageName = String(app?.googlePlayPackageName || "").trim();
      const localeCandidates = Array.isArray(job?.locales)
        ? job?.locales || []
        : [];
      const locales = localeCandidates.filter(
        (item) => typeof item === "string" && item.trim().length > 0,
      );
      const publishableLocales = locales.filter((locale) =>
        GOOGLE_PLAY_LOCALE_REGEX.test(locale),
      );
      const skippedLocales = locales.filter(
        (locale) => !GOOGLE_PLAY_LOCALE_REGEX.test(locale),
      );

      if (!job) {
        blockers.push("Choose a screenshot job to publish.");
      } else if (job.targetPlatform === "ios") {
        blockers.push(
          "Google Play screenshot publishing is only available for Android jobs.",
        );
      }

      if (!app) {
        blockers.push("This screenshot job is not linked to a registered app.");
      }

      if (!job?.savedVersionId) {
        blockers.push(
          "Only saved screenshot jobs can be published to Google Play.",
        );
      }

      if (!packageName) {
        blockers.push(
          "Add a Google Play package name in App Registration before publishing screenshots.",
        );
      }

      if (publishableLocales.length === 0 && skippedLocales.length > 0) {
        blockers.push(
          "This screenshot job does not contain any Google Play-supported locales.",
        );
      }

      if (skippedLocales.length > 0) {
        warnings.push(
          `Unsupported Google Play locales will be skipped: ${skippedLocales.join(", ")}.`,
        );
      }

      return {
        app,
        packageName,
        publishableLocales,
        skippedLocales,
        blockers,
        warnings,
      };
    },
    [getAppForJob],
  );

  const getAppStoreConnectScreenshotPublishValidation = useCallback(
    (job: ScreenshotJobRecord | null) => {
      const blockers: string[] = [];
      const warnings: string[] = [];
      const app = getAppForJob(job);
      const bundleId = String(app?.storeId || "").trim();
      const hasIosUrl = Boolean(String(app?.iosStoreUrl || "").trim());
      const localeCandidates = Array.isArray(job?.locales)
        ? job?.locales || []
        : [];
      const publishableLocales = localeCandidates
        .filter((item) => typeof item === "string" && item.trim().length > 0)
        .map((item) => item.trim());

      if (!job) {
        blockers.push("Choose a screenshot job to publish.");
      } else if (job.targetPlatform === "android") {
        blockers.push(
          "App Store Connect screenshot publishing is only available for iOS jobs.",
        );
      }

      if (!app) {
        blockers.push("This screenshot job is not linked to a registered app.");
      }

      if (!job?.savedVersionId) {
        blockers.push(
          "Only saved screenshot jobs can be published to App Store Connect.",
        );
      }

      if (!bundleId && !hasIosUrl) {
        blockers.push(
          "Add app storeId as iOS bundle ID or add iOS Store URL in App Registration before publishing.",
        );
      } else if (bundleId && !APP_STORE_BUNDLE_ID_REGEX.test(bundleId)) {
        warnings.push(
          "storeId does not look like a bundle ID. iOS Store URL lookup fallback will be used.",
        );
      }

      if (publishableLocales.length === 0) {
        blockers.push(
          "This screenshot job does not contain iOS locales to publish.",
        );
      }

      return {
        app,
        packageName: bundleId,
        publishableLocales,
        skippedLocales: [],
        blockers,
        warnings,
      };
    },
    [getAppForJob],
  );

  const openPublishDialogForJob = useCallback(
    (job: ScreenshotJobRecord) => {
      const defaultDestination: ScreenshotPublishDestination =
        job.targetPlatform === "ios" ? "app_store_connect" : "google_play";
      const publishableLocales =
        defaultDestination === "app_store_connect"
          ? getAppStoreConnectScreenshotPublishValidation(job)
              .publishableLocales
          : getScreenshotPublishValidation(job).publishableLocales;
      const nextLocale = publishableLocales[0] || "";
      setPublishTargetJob(job);
      setPublishDialogOpen(true);
      setPublishDestination(defaultDestination);
      setPublishMode("all_locales");
      setPublishLocale(nextLocale);
      setPublishSelectedLocales(nextLocale ? [nextLocale] : []);
      setScreenshotValidationResult(null);
      setScreenshotPublishResult(null);
      setPublishConfirmationChecked(false);
      setPublishOverrideChecked(false);
      setValidationOverviewCopied(false);
      setValidationLocaleFilter(ALL_VALIDATION_LOCALES_FILTER);
      setExpandedValidationLocales({});
      setExpandedValidationGroups({});
      setPublishSections({
        overview: true,
        target: true,
        validate: true,
        confirm: true,
        danger: false,
        result: false,
      });
      setError(null);
    },
    [
      getAppStoreConnectScreenshotPublishValidation,
      getScreenshotPublishValidation,
    ],
  );

  const closePublishDialog = useCallback(() => {
    setPublishDialogOpen(false);
    setPublishTargetJob(null);
    setPublishDestination("google_play");
    setPublishMode("all_locales");
    setPublishLocale("");
    setPublishSelectedLocales([]);
    setScreenshotValidationResult(null);
    setScreenshotPublishResult(null);
    setPublishConfirmationChecked(false);
    setPublishOverrideChecked(false);
    setValidationOverviewCopied(false);
    setValidationLocaleFilter(ALL_VALIDATION_LOCALES_FILTER);
    setExpandedValidationLocales({});
    setExpandedValidationGroups({});
    setPublishSections({
      overview: true,
      target: true,
      validate: true,
      confirm: true,
      danger: false,
      result: false,
    });
  }, []);

  useEffect(() => {
    if (!screenshotValidationResult) {
      return;
    }

    setValidationLocaleFilter(ALL_VALIDATION_LOCALES_FILTER);
    setExpandedValidationLocales(
      screenshotValidationResult.results.reduce<Record<string, boolean>>(
        (accumulator, result) => {
          accumulator[result.locale] = true;
          return accumulator;
        },
        {},
      ),
    );
    setExpandedValidationGroups(
      screenshotValidationResult.results.reduce<Record<string, boolean>>(
        (accumulator, result) => {
          result.assetGroups.forEach((group) => {
            accumulator[`${result.locale}:${group.imageType}`] = true;
          });
          return accumulator;
        },
        {},
      ),
    );
  }, [screenshotValidationResult]);

  const handleReturnToScreenshotJobs = useCallback(() => {
    closePublishDialog();
    setTabValue(5);
  }, [closePublishDialog]);

  useEffect(() => {
    const availableKeys = expectedDimensions.map((dimension) =>
      dimensionKey(dimension),
    );
    const defaultKeys = expectedDimensions
      .filter(
        (dimension) =>
          dimension.platform === "android" ||
          (dimension.platform === "ios" &&
            (dimension.name === 'iPhone 6.5"' ||
              dimension.name === 'iPad Pro 12.9"')),
      )
      .map((dimension) => dimensionKey(dimension));
    setSelectedDimensionKeys((prev) => {
      const preserved = prev.filter((key) => availableKeys.includes(key));
      if (preserved.length > 0) {
        return preserved;
      }
      return defaultKeys.length > 0 ? defaultKeys : availableKeys;
    });
  }, [expectedDimensions]);

  useEffect(() => {
    setExpandedGeneratedPlatforms((prev) => {
      const next = { ...prev };
      groupedGeneratedScreenshots.forEach((platformGroup) => {
        if (next[platformGroup.platform] === undefined) {
          next[platformGroup.platform] = true;
        }
      });
      return next;
    });

    setExpandedGeneratedLocales((prev) => {
      const next = { ...prev };
      groupedGeneratedScreenshots.forEach((platformGroup) => {
        platformGroup.locales.forEach((localeGroup) => {
          const key = `${platformGroup.platform}_${localeGroup.locale}`;
          if (next[key] === undefined) {
            next[key] = true;
          }
        });
      });
      return next;
    });
  }, [groupedGeneratedScreenshots]);

  useEffect(() => {
    if (!user?.email) {
      setScreenshotJobs([]);
      return;
    }
    try {
      const raw = localStorage.getItem(
        `${SCREENSHOT_JOBS_STORAGE_KEY}:${user.email}`,
      );
      if (!raw) {
        setScreenshotJobs([]);
        return;
      }
      const parsed = JSON.parse(raw);
      setScreenshotJobs(Array.isArray(parsed) ? parsed : []);
    } catch {
      setScreenshotJobs([]);
    }
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    localStorage.setItem(
      `${SCREENSHOT_JOBS_STORAGE_KEY}:${user.email}`,
      JSON.stringify(screenshotJobs),
    );
  }, [screenshotJobs, user?.email]);

  useEffect(() => {
    if (!user?.email) {
      setSelectedJobsAppFilter("");
      return;
    }
    const raw = localStorage.getItem(
      `${SCREENSHOT_JOBS_FILTER_STORAGE_KEY}:${user.email}`,
    );
    setSelectedJobsAppFilter(raw || "");
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    localStorage.setItem(
      `${SCREENSHOT_JOBS_FILTER_STORAGE_KEY}:${user.email}`,
      selectedJobsAppFilter,
    );
  }, [selectedJobsAppFilter, user?.email]);

  useEffect(() => {
    let active = true;

    const loadPersistedScreenshotJobs = async () => {
      if (!user?.email || !selectedJobsAppFilter) {
        if (active) setPersistedJobsLoading(false);
        return;
      }

      const targetApps =
        selectedJobsAppFilter === ALL_APPS_FILTER
          ? apps
          : apps.filter((app) => app.appId === selectedJobsAppFilter);

      if (targetApps.length === 0) {
        if (active) setPersistedJobsLoading(false);
        return;
      }

      if (active) setPersistedJobsLoading(true);

      try {
        const results = await Promise.all(
          targetApps.map(async (app) => {
            const [versionsResult, assetsResult, cloudJobsResult] =
              await Promise.allSettled([
                ScreenshotService.getScreenshotVersionsByAppId(
                  app.appId,
                  user.email,
                ),
                ScreenshotService.getStoredScreenshotsByAppId(
                  app.appId,
                  user.email,
                ),
                ScreenshotService.getScreenshotJobsByAppId(
                  app.appId,
                  user.email,
                ),
              ]);

            const versions =
              versionsResult.status === "fulfilled" ? versionsResult.value : [];
            const assets =
              assetsResult.status === "fulfilled" ? assetsResult.value : [];
            const cloudJobs =
              cloudJobsResult.status === "fulfilled"
                ? cloudJobsResult.value
                : [];

            if (versionsResult.status === "rejected") {
              console.error(
                `Failed to load screenshot versions for app ${app.appId}:`,
                versionsResult.reason,
              );
            }
            if (assetsResult.status === "rejected") {
              console.error(
                `Failed to load stored screenshots for app ${app.appId}:`,
                assetsResult.reason,
              );
            }
            if (cloudJobsResult.status === "rejected") {
              console.error(
                `Failed to load screenshot jobs for app ${app.appId}:`,
                cloudJobsResult.reason,
              );
            }

            const assetsByVersion = new Map<
              string,
              StoredStoreScreenshotAsset[]
            >();
            assets.forEach((asset) => {
              const existing = assetsByVersion.get(asset.versionId) || [];
              existing.push(asset);
              assetsByVersion.set(asset.versionId, existing);
            });

            return [
              versions
                .map((version) => {
                  const versionAssets =
                    assetsByVersion.get(version.versionId) || [];
                  if (
                    (version.status === "failed" ||
                      version.status === "processing") &&
                    versionAssets.length === 0
                  ) {
                    return null;
                  }
                  const locales = [
                    ...new Set(versionAssets.map((asset) => asset.locale)),
                  ];
                  const dimensionLabels = [
                    ...new Set(
                      versionAssets.map((asset) =>
                        formatScreenshotDeviceLabel(asset.dimension),
                      ),
                    ),
                  ];
                  const sourceCount = new Set(
                    versionAssets.map((asset) => asset.sourceName),
                  ).size;

                  return {
                    id: `persisted_${version.versionId}`,
                    appId: app.appId,
                    appName: app.name,
                    templateId: version.templateId,
                    templateName: version.templateName,
                    targetPlatform:
                      versionAssets.length > 0 &&
                      versionAssets.every((asset) => asset.platform === "ios")
                        ? "ios"
                        : versionAssets.length > 0 &&
                            versionAssets.every(
                              (asset) => asset.platform === "android",
                            )
                          ? "android"
                          : "both",
                    locales,
                    dimensionLabels,
                    sourceCount,
                    expectedOutputCount:
                      version.expectedAssetCount || versionAssets.length,
                    generatedCount: versionAssets.length,
                    savedVersionId: version.versionId,
                    savedVersionNumber: version.versionNumber,
                    zipDownloadUrl: version.zipDownloadUrl,
                    zipFileName: version.zipFileName,
                    zipCreatedAt: version.zipCreatedAt,
                    status:
                      version.status === "processing"
                        ? ("processing" as const)
                        : version.status === "partial" ||
                            version.status === "failed"
                          ? ("failed" as const)
                          : ("completed" as const),
                    progress: {
                      stage:
                        version.status === "processing"
                          ? ("saving" as const)
                          : ("complete" as const),
                      message:
                        version.status === "processing"
                          ? `Saving version v${version.versionNumber} to the content library`
                          : version.status === "partial"
                            ? `Version v${version.versionNumber} was partially saved before the run stopped`
                            : version.status === "failed"
                              ? `Version v${version.versionNumber} failed`
                              : `Saved version v${version.versionNumber} to the content library`,
                      completed: versionAssets.length,
                      total: version.expectedAssetCount || versionAssets.length,
                      percent:
                        (version.expectedAssetCount || versionAssets.length) > 0
                          ? Math.round(
                              (versionAssets.length /
                                (version.expectedAssetCount ||
                                  versionAssets.length)) *
                                100,
                            )
                          : 0,
                    },
                    createdAt: version.createdAt,
                    updatedAt: version.zipCreatedAt || version.createdAt,
                  } satisfies ScreenshotJobRecord;
                })
                .filter(Boolean) as ScreenshotJobRecord[],
              cloudJobs,
            ];
          }),
        );

        const allJobs = results.flat(2);

        if (!active) return;

        setScreenshotJobs((prev) => {
          const persistedIds = new Set(allJobs.map((job) => job.id));
          const withoutOldPersisted = prev.filter(
            (job) =>
              !job.id.startsWith("persisted_") && !job.id.startsWith("ss_job_"),
          );
          const merged = [...withoutOldPersisted];

          allJobs.forEach((persistedJob) => {
            const localIndex = merged.findIndex(
              (job) =>
                (job.savedVersionId &&
                  job.savedVersionId === persistedJob.savedVersionId) ||
                job.id === persistedJob.id,
            );

            if (localIndex >= 0) {
              const localJob = merged[localIndex];
              const keepLocalProgress =
                localJob.status === "queued" ||
                localJob.status === "processing";
              const keepLocalFailureState =
                localJob.status === "failed" &&
                persistedJob.status === "failed";
              merged[localIndex] = {
                ...localJob,
                ...persistedJob,
                status:
                  keepLocalProgress || keepLocalFailureState
                    ? localJob.status
                    : persistedJob.status,
                progress:
                  keepLocalProgress || keepLocalFailureState
                    ? localJob.progress || persistedJob.progress
                    : persistedJob.progress,
                savedVersionId: persistedJob.savedVersionId,
                savedVersionNumber: persistedJob.savedVersionNumber,
                zipDownloadUrl:
                  persistedJob.zipDownloadUrl || localJob.zipDownloadUrl,
                zipFileName: persistedJob.zipFileName || localJob.zipFileName,
                zipCreatedAt:
                  persistedJob.zipCreatedAt || localJob.zipCreatedAt,
                error:
                  keepLocalFailureState && localJob.error
                    ? localJob.error
                    : persistedJob.error || localJob.error,
              };
            } else if (!persistedIds.has(persistedJob.id)) {
              merged.push(persistedJob);
            } else {
              merged.push(persistedJob);
            }
          });

          return merged;
        });
      } catch (error) {
        console.error(error);
      } finally {
        if (active) setPersistedJobsLoading(false);
      }
    };

    loadPersistedScreenshotJobs();

    return () => {
      active = false;
    };
  }, [apps, persistedJobsReloadKey, selectedJobsAppFilter, user?.email]);

  useEffect(() => {
    if (tabValue !== 5) {
      return;
    }

    setExpandedScreenshotJobDates((prev) => {
      const next: Record<string, boolean> = {};
      groupedVisibleScreenshotJobs.forEach((group, index) => {
        next[group.key] =
          prev[group.key] !== undefined ? prev[group.key] : index === 0;
      });
      return next;
    });
  }, [groupedVisibleScreenshotJobs, tabValue]);

  useEffect(() => {
    setGenerationInputMode(selectedTemplate.generationMode || "single_source");
  }, [selectedTemplate.generationMode]);

  useEffect(() => {
    if (!isSlotDesignMode) {
      setActiveTemplateSlotId("");
      setBulkTargetSlotIds([]);
      return;
    }
    const ids = templateScreenSlots.map((slot) => slot.slotId);
    if (!ids.includes(activeTemplateSlotId)) {
      setActiveTemplateSlotId(ids[0] || "");
    }
    setBulkTargetSlotIds((previous) =>
      previous.filter((slotId) => ids.includes(slotId)),
    );
  }, [activeTemplateSlotId, isSlotDesignMode, templateScreenSlots]);

  useEffect(() => {
    const slotIds = new Set(templateScreenSlots.map((slot) => slot.slotId));
    setTemplateSlotPreviewImages((previous) => {
      const next: Record<string, { dataUrl: string; fileName: string }> = {};
      Object.entries(previous).forEach(([slotId, value]) => {
        if (slotIds.has(slotId)) {
          next[slotId] = value;
        }
      });
      return next;
    });
  }, [templateScreenSlots]);

  useEffect(() => {
    if (templateScreenSlots.length === 0) {
      setSlotInputs({});
      return;
    }
    setSlotInputs((previous) => {
      const next: Record<string, ScreenSlotInput> = {};
      templateScreenSlots.forEach((slot) => {
        const existing = previous[slot.slotId];
        next[slot.slotId] = {
          slotId: slot.slotId,
          slotName: slot.name,
          slotOrder: slot.order,
          enabled: slot.enabled !== false,
          style: slot.style || selectedTemplate.style,
          dataUrl: existing?.dataUrl || "",
          fileName: existing?.fileName || "",
          aiContext: existing?.aiContext || "",
          manualHeading: existing?.manualHeading || "",
          manualSubtitle: existing?.manualSubtitle || "",
          overlayImages: existing?.overlayImages || [],
        };
      });
      return next;
    });
  }, [templateScreenSlots, selectedTemplate.style]);

  useEffect(() => {
    let active = true;
    const renderPreview = async () => {
      if (!previewDimension) return;
      const activeSlotInput = activeTemplateSlot
        ? slotInputs[activeTemplateSlot.slotId]
        : undefined;
      const activeSlotPreviewImage = activeTemplateSlot
        ? templateSlotPreviewImages[activeTemplateSlot.slotId]
        : undefined;
      const selectedPreviewImage = isScreenSlotsInputMode
        ? undefined
        : uploadedImages.find((image) => image.id === editorPreviewImageId);
      const selectedSlotImage = isScreenSlotsInputMode
        ? activeSlotInput
        : undefined;
      const sourceImageDataUrl =
        (isSlotDesignMode ? activeSlotPreviewImage?.dataUrl : undefined) ||
        selectedSlotImage?.dataUrl ||
        activeSlotInput?.dataUrl ||
        selectedPreviewImage?.dataUrl ||
        uploadedImages[0]?.dataUrl ||
        createPlaceholderDataUrl(
          previewDimension.width,
          previewDimension.height,
        );
      try {
        const preview = await ScreenshotService.renderStoreTemplatePreview({
          appName: selectedApp?.name || "Your App",
          template: {
            ...selectedTemplate,
            style: { ...editorStyle },
          },
          dimension: previewDimension,
          previewDeviceId,
          sourceImageDataUrl,
          overlayImages: (activeSlotInput?.overlayImages || []).map(
            (overlay) => ({
              id: overlay.id,
              dataUrl: overlay.dataUrl,
              xRatio: overlay.xRatio,
              yRatio: overlay.yRatio,
              widthRatio: overlay.widthRatio,
              heightRatio: overlay.heightRatio,
              opacity: overlay.opacity,
              borderRadiusRatio: overlay.borderRadiusRatio,
            }),
          ),
          heading: previewHeading,
          subtitle: previewSubtitle,
        });
        if (active) setTemplatePreview(preview);
      } catch {
        if (active) setTemplatePreview("");
      }
    };
    renderPreview();
    return () => {
      active = false;
    };
  }, [
    previewDimension,
    uploadedImages,
    editorPreviewImageId,
    slotInputs,
    isScreenSlotsInputMode,
    templateScreenSlots,
    activeTemplateSlot,
    isSlotDesignMode,
    editorStyle,
    selectedTemplate,
    selectedApp?.name,
    previewHeading,
    previewSubtitle,
    previewDeviceId,
    templateSlotPreviewImages,
  ]);

  useEffect(() => {
    if (isScreenSlotsInputMode) {
      return;
    }
    if (uploadedImages.length === 0) {
      setEditorPreviewImageId("");
      return;
    }
    if (!uploadedImages.some((image) => image.id === editorPreviewImageId)) {
      setEditorPreviewImageId(uploadedImages[0].id);
    }
  }, [uploadedImages, editorPreviewImageId, isScreenSlotsInputMode]);

  useEffect(() => {
    if (!isScreenSlotsInputMode || !previewDimension) {
      setSlotStyledPreviews({});
      return;
    }

    let active = true;
    const renderSlotPreviews = async () => {
      const previewEntries = await Promise.all(
        templateScreenSlots.map(async (slot) => {
          const input = slotInputs[slot.slotId];
          if (!input?.dataUrl) {
            return [slot.slotId, ""] as const;
          }
          try {
            const preview = await ScreenshotService.renderStoreTemplatePreview({
              appName: selectedApp?.name || "Your App",
              template: {
                ...selectedTemplate,
                style: slot.style || selectedTemplate.style,
              },
              dimension: previewDimension,
              previewDeviceId,
              sourceImageDataUrl: input.dataUrl,
              heading: input.manualHeading || previewHeading,
              subtitle: input.manualSubtitle || previewSubtitle,
            });
            return [slot.slotId, preview] as const;
          } catch {
            return [slot.slotId, input.dataUrl] as const;
          }
        }),
      );

      if (!active) return;
      const next: Record<string, string> = {};
      previewEntries.forEach(([slotId, preview]) => {
        if (preview) {
          next[slotId] = preview;
        }
      });
      setSlotStyledPreviews(next);
    };

    renderSlotPreviews();
    return () => {
      active = false;
    };
  }, [
    isScreenSlotsInputMode,
    previewDimension,
    previewDeviceId,
    previewHeading,
    previewSubtitle,
    selectedApp?.name,
    selectedTemplate,
    slotInputs,
    templateScreenSlots,
  ]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    try {
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load registered apps.");
    }
  }, [user?.email]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    let active = true;
    const loadCustomTemplates = async () => {
      if (!selectedApp?.appId || !user?.email) {
        if (active) setCustomTemplates([]);
        return;
      }
      try {
        const templates = await ScreenshotService.getCustomStoreTemplates(
          user.email,
          selectedApp.appId,
        );
        if (active) setCustomTemplates(templates);
      } catch (templateError) {
        console.error(templateError);
        if (active) setCustomTemplates([]);
      }
    };
    loadCustomTemplates();
    return () => {
      active = false;
    };
  }, [selectedApp?.appId, user?.email]);

  const loadLibraryAssets = useCallback(async () => {
    if (!selectedApp?.appId || !user?.email) {
      setLibraryAssets([]);
      setLibraryVersions([]);
      return;
    }
    setLibraryLoading(true);
    try {
      const [assets, versions] = await Promise.all([
        ScreenshotService.getStoredScreenshotsByAppId(
          selectedApp.appId,
          user.email,
        ),
        ScreenshotService.getScreenshotVersionsByAppId(
          selectedApp.appId,
          user.email,
        ),
      ]);
      setLibraryAssets(assets);
      setLibraryVersions(versions);
    } catch (libraryError: any) {
      console.error(libraryError);
      setError(
        libraryError?.message || "Failed to load screenshot content library.",
      );
    } finally {
      setLibraryLoading(false);
    }
  }, [selectedApp?.appId, user?.email]);

  useEffect(() => {
    loadLibraryAssets();
  }, [loadLibraryAssets]);

  useEffect(() => {
    if (!activeGenerationJobId) {
      return;
    }

    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const syncActiveGenerationJob = async () => {
      try {
        const latestJob = await ScreenshotService.getScreenshotJob(
          activeGenerationJobId,
        );
        if (!active || !latestJob) {
          return;
        }

        setScreenshotJobs((prev) => {
          const exists = prev.some((job) => job.id === latestJob.id);
          if (exists) {
            return prev.map((job) =>
              job.id === latestJob.id ? latestJob : job,
            );
          }
          return [latestJob, ...prev];
        });

        if (latestJob.progress) {
          setGenerationProgress(latestJob.progress);
        }

        if (latestJob.status === "completed") {
          setActiveGenerationJobId(null);
          await loadLibraryAssets();
          setPersistedJobsReloadKey((current) => current + 1);
          return;
        }

        if (latestJob.status === "failed") {
          setActiveGenerationJobId(null);
          setError(latestJob.error || "Cloud screenshot generation failed.");
          return;
        }
      } catch (jobError) {
        if (active) {
          console.error(jobError);
        }
      }

      if (active) {
        timer = setTimeout(syncActiveGenerationJob, 3000);
      }
    };

    syncActiveGenerationJob();

    return () => {
      active = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [activeGenerationJobId, loadLibraryAssets]);

  useEffect(() => {
    setTemplateRenameDrafts((previous) => {
      const next: Record<string, string> = {};
      customTemplates.forEach((template) => {
        next[template.id] = previous[template.id] ?? template.name;
      });
      return next;
    });
  }, [customTemplates]);

  useEffect(() => {
    let active = true;
    const loadStoredStyle = async () => {
      if (!selectedApp?.appId || !user?.email) return;
      if (selectedTemplate.isCustom) {
        setStyleReady(true);
        setStyleStatus("idle");
        return;
      }
      setStyleReady(false);
      setStyleStatus("loading");
      try {
        const savedStyle = await ScreenshotService.getStoreTemplateStyle(
          user.email,
          selectedApp.appId,
          selectedTemplate.id,
        );
        if (!active) return;
        const style = savedStyle
          ? { ...DEFAULT_STORE_TEMPLATE_STYLE, ...savedStyle }
          : { ...DEFAULT_STORE_TEMPLATE_STYLE };
        setSelectedTemplate((previous) => ({
          ...previous,
          style,
          generationMode: previous.generationMode || "single_source",
          screenSlots: previous.screenSlots || [],
        }));
        setStyleStatus("idle");
      } catch (styleError) {
        console.error(styleError);
        if (active) setStyleStatus("error");
      } finally {
        if (active) setStyleReady(true);
      }
    };
    loadStoredStyle();
    return () => {
      active = false;
    };
  }, [selectedApp?.appId, selectedTemplate.id, user?.email]);

  useEffect(() => {
    if (
      allTemplateOptions.some((template) => template.id === selectedTemplate.id)
    ) {
      return;
    }
    setSelectedTemplate(cloneStoreTemplate(STORE_SCREENSHOT_TEMPLATES[0]));
  }, [allTemplateOptions, selectedTemplate.id]);

  useEffect(() => {
    const baseTemplate =
      STORE_SCREENSHOT_TEMPLATES.find(
        (template) => template.layout === newTemplateLayout,
      ) || STORE_SCREENSHOT_TEMPLATES[0];
    setDraftTemplateStyle((previous) => ({
      ...baseTemplate.style,
      ...previous,
      androidDeviceFrameId:
        previous.androidDeviceFrameId ||
        baseTemplate.style.androidDeviceFrameId,
    }));
  }, [newTemplateLayout]);

  useEffect(() => {
    let active = true;
    const renderTemplateCreatorPreview = async () => {
      if (!previewDimension) return;
      const selectedPreviewImage = uploadedImages.find(
        (image) => image.id === editorPreviewImageId,
      );
      const sourceImageDataUrl =
        selectedPreviewImage?.dataUrl ||
        uploadedImages[0]?.dataUrl ||
        createPlaceholderDataUrl(
          previewDimension.width,
          previewDimension.height,
        );

      const draftTemplate: StoreScreenshotTemplate = {
        id: `draft_${newTemplateLayout}`,
        layout: newTemplateLayout,
        name: newTemplateName || "Draft Template",
        description: newTemplateDescription || "Template preview",
        style: draftTemplateStyle,
      };

      try {
        const preview = await ScreenshotService.renderStoreTemplatePreview({
          appName: selectedApp?.name || "Your App",
          template: draftTemplate,
          dimension: previewDimension,
          previewDeviceId,
          sourceImageDataUrl,
          heading: previewHeading,
          subtitle: previewSubtitle,
        });
        if (active) setTemplateCreatorPreview(preview);
      } catch {
        if (active) setTemplateCreatorPreview("");
      }
    };
    renderTemplateCreatorPreview();
    return () => {
      active = false;
    };
  }, [
    draftTemplateStyle,
    newTemplateLayout,
    newTemplateName,
    newTemplateDescription,
    selectedApp?.name,
    previewDimension,
    previewDeviceId,
    uploadedImages,
    editorPreviewImageId,
    previewHeading,
    previewSubtitle,
  ]);

  const handleSaveTemplate = async () => {
    if (!styleReady || !selectedApp?.appId || !user?.email) return;
    try {
      if (selectedTemplate.isCustom) {
        await ScreenshotService.updateCustomStoreTemplate(
          user.email,
          selectedTemplate.id,
          {
            name: selectedTemplate.name,
            description: selectedTemplate.description,
            style: selectedTemplate.style,
            layout: selectedTemplate.layout,
            generationMode: generationInputMode,
            screenSlots:
              generationInputMode === "screen_slots"
                ? selectedTemplate.screenSlots || []
                : [],
          },
        );
        const templates = await ScreenshotService.getCustomStoreTemplates(
          user.email,
          selectedApp.appId,
        );
        setCustomTemplates(templates);
      } else {
        await ScreenshotService.saveStoreTemplateStyle(
          user.email,
          selectedApp.appId,
          selectedTemplate.id,
          selectedTemplate.style,
        );
      }
      setStyleStatus("saved");
    } catch (saveError) {
      console.error(saveError);
      setStyleStatus("error");
    }
  };

  const getNormalizedInputSources = useCallback(() => {
    if (isScreenSlotsInputMode) {
      return templateScreenSlots
        .map((slot) => {
          const input = slotInputs[slot.slotId];
          if (!input?.dataUrl) return null;
          return {
            name: formatSlotSourceName(slot.order, slot.name),
            dataUrl: input.dataUrl,
            aiContext: input.aiContext,
            manualHeading: input.manualHeading,
            manualSubtitle: input.manualSubtitle,
            slotId: slot.slotId,
            slotName: slot.name,
            slotOrder: slot.order,
            slotStyle: slot.style,
            overlayImages: undefined,
          };
        })
        .filter(Boolean) as Array<{
        name: string;
        dataUrl: string;
        aiContext: string;
        manualHeading: string;
        manualSubtitle: string;
        slotId: string;
        slotName: string;
        slotOrder: number;
        slotStyle: StoreScreenshotTemplateStyle;
        overlayImages?: undefined;
      }>;
    }

    return uploadedImages.map((image) => ({
      name: image.file.name,
      dataUrl: image.dataUrl,
      aiContext: image.aiContext,
      manualHeading: image.manualHeading,
      manualSubtitle: image.manualSubtitle,
      slotId: undefined,
      slotName: undefined,
      slotOrder: undefined,
      slotStyle: undefined,
      overlayImages: undefined,
    }));
  }, [isScreenSlotsInputMode, slotInputs, templateScreenSlots, uploadedImages]);

  const handleSelectApp = (event: SelectChangeEvent) => {
    const app = apps.find((item) => item.appId === event.target.value) || null;
    setSelectedApp(app);
    setTargetPlatform("both");
    setGeneratedScreenshots([]);
  };

  const handleExportTemplateConfig = () => {
    if (!selectedApp) return;
    const payload = {
      version: 2,
      templateId: selectedTemplate.id,
      appId: selectedApp.appId,
      exportedAt: new Date().toISOString(),
      generationMode: generationInputMode,
      screenSlots:
        generationInputMode === "screen_slots"
          ? selectedTemplate.screenSlots || []
          : [],
      style: selectedTemplate.style,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedApp.name.replace(/[^a-zA-Z0-9-_]/g, "_")}-${
      selectedTemplate.id
    }-template-config.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportTemplateConfig = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const importedStyle = sanitizeImportedStyle(parsed?.style);
      if (!importedStyle) {
        throw new Error("Invalid template config file.");
      }
      const importedMode =
        parsed?.generationMode === "screen_slots"
          ? "screen_slots"
          : "single_source";
      const importedSlots = sanitizeImportedScreenSlots(parsed?.screenSlots);
      setStyleStatus("idle");
      setSelectedTemplate((previous) => ({
        ...previous,
        style: importedStyle,
        generationMode: importedMode,
        screenSlots: importedSlots,
      }));
      setGenerationInputMode(importedMode);
    } catch (importError) {
      console.error(importError);
      setError(
        "Failed to import template config. Ensure it is a valid exported JSON file.",
      );
    } finally {
      event.target.value = "";
    }
  };

  const setStyleField = <K extends keyof StoreScreenshotTemplateStyle>(
    field: K,
    value: StoreScreenshotTemplateStyle[K],
  ) => {
    setStyleStatus("idle");
    setSelectedTemplate((previous) => {
      if (isSlotDesignMode && activeTemplateSlotId) {
        return {
          ...previous,
          screenSlots: (previous.screenSlots || []).map((slot) =>
            slot.slotId === activeTemplateSlotId
              ? {
                  ...slot,
                  style: {
                    ...(slot.style || previous.style),
                    [field]: value,
                  },
                }
              : slot,
          ),
        };
      }
      return {
        ...previous,
        style: {
          ...previous.style,
          [field]: value,
        },
      };
    });
  };

  const setEditorStyle = (style: StoreScreenshotTemplateStyle) => {
    setStyleStatus("idle");
    setSelectedTemplate((previous) => {
      if (isSlotDesignMode && activeTemplateSlotId) {
        return {
          ...previous,
          screenSlots: (previous.screenSlots || []).map((slot) =>
            slot.slotId === activeTemplateSlotId
              ? { ...slot, style: { ...style } }
              : slot,
          ),
        };
      }
      return {
        ...previous,
        style: { ...style },
      };
    });
  };

  const resetStyleField = <K extends keyof StoreScreenshotTemplateStyle>(
    field: K,
  ) => {
    setStyleField(field, DEFAULT_STORE_TEMPLATE_STYLE[field]);
  };

  const resetEditorStyle = () => {
    setEditorStyle({ ...DEFAULT_STORE_TEMPLATE_STYLE });
  };

  const copyActiveStyleToTargetSlots = () => {
    if (!activeTemplateSlot || bulkTargetSlotIds.length === 0) {
      return;
    }
    const sourceStyle = { ...(activeTemplateSlot.style || editorStyle) };
    setStyleStatus("idle");
    setSelectedTemplate((previous) => ({
      ...previous,
      screenSlots: (previous.screenSlots || []).map((slot) =>
        bulkTargetSlotIds.includes(slot.slotId)
          ? {
              ...slot,
              style: { ...sourceStyle },
            }
          : slot,
      ),
    }));
  };

  const updateLayerStack = (
    updater: (
      current: StoreScreenshotTemplateStyle["layerStack"],
    ) => StoreScreenshotTemplateStyle["layerStack"],
  ) => {
    const current = Array.isArray(compositionLayerStack)
      ? compositionLayerStack
      : [];
    const next = updater(current).map((layer, index) => ({
      ...layer,
      zIndex: index,
    }));
    setStyleField("layerStack", next);
  };

  const moveLayer = (layerId: string, direction: -1 | 1) => {
    updateLayerStack((current) => {
      const index = current.findIndex((item) => item.id === layerId);
      if (index < 0) return current;
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const toggleLayerVisibility = (layerId: string, visible: boolean) => {
    updateLayerStack((current) =>
      current.map((layer) =>
        layer.id === layerId ? { ...layer, visible } : layer,
      ),
    );
  };

  const moveActiveTemplateSlot = (delta: -1 | 1) => {
    if (!isSlotDesignMode || templateScreenSlots.length === 0) return;
    const currentIndex = templateScreenSlots.findIndex(
      (slot) => slot.slotId === activeTemplateSlotId,
    );
    const safeCurrent = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = Math.min(
      templateScreenSlots.length - 1,
      Math.max(0, safeCurrent + delta),
    );
    setActiveTemplateSlotId(templateScreenSlots[nextIndex].slotId);
  };

  const handleUploadScreenshots = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const uploaded = await Promise.all(
      files.map(async (file) => {
        const dataUrl = await fileToDataUrl(file);
        return {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          file,
          dataUrl,
          aiContext: "",
          manualHeading: "",
          manualSubtitle: "",
        } as UploadedImage;
      }),
    );

    setUploadedImages((previous) => [...previous, ...uploaded]);
    setGeneratedScreenshots([]);
    event.target.value = "";
  };

  const handleUploadSlotScreenshot = async (
    slot: StoreScreenshotTemplateScreenSlot,
    file: File,
  ) => {
    const dataUrl = await fileToDataUrl(file);
    setSlotInputs((previous) => ({
      ...previous,
      [slot.slotId]: {
        slotId: slot.slotId,
        slotName: slot.name,
        slotOrder: slot.order,
        enabled: slot.enabled !== false,
        style: slot.style || selectedTemplate.style,
        dataUrl,
        fileName: file.name,
        aiContext: previous[slot.slotId]?.aiContext || "",
        manualHeading: previous[slot.slotId]?.manualHeading || "",
        manualSubtitle: previous[slot.slotId]?.manualSubtitle || "",
        overlayImages: previous[slot.slotId]?.overlayImages || [],
      },
    }));
    setGeneratedScreenshots([]);
  };

  const handleUploadTemplateSlotPreview = async (
    slotId: string,
    file: File,
  ) => {
    const dataUrl = await fileToDataUrl(file);
    setTemplateSlotPreviewImages((previous) => ({
      ...previous,
      [slotId]: {
        dataUrl,
        fileName: file.name,
      },
    }));
  };

  const removeTemplateSlotPreview = (slotId: string) => {
    setTemplateSlotPreviewImages((previous) => {
      if (!previous[slotId]) return previous;
      const next = { ...previous };
      delete next[slotId];
      return next;
    });
  };

  const removeSlotScreenshot = (slotId: string) => {
    setSlotInputs((previous) => ({
      ...previous,
      [slotId]: {
        ...previous[slotId],
        dataUrl: "",
        fileName: "",
      },
    }));
    setGeneratedScreenshots([]);
  };

  const updateSlotInput = (
    slotId: string,
    key: "aiContext" | "manualHeading" | "manualSubtitle",
    value: string,
  ) => {
    setSlotInputs((previous) => ({
      ...previous,
      [slotId]: {
        ...previous[slotId],
        [key]: value,
      },
    }));
    setGeneratedScreenshots([]);
  };

  const removeUploadedImage = (id: string) => {
    setUploadedImages((previous) =>
      previous.filter((image) => image.id !== id),
    );
    setGeneratedScreenshots([]);
  };

  const updateUploadedImage = (
    id: string,
    key: "aiContext" | "manualHeading" | "manualSubtitle",
    value: string,
  ) => {
    setUploadedImages((previous) =>
      previous.map((image) =>
        image.id === id ? { ...image, [key]: value } : image,
      ),
    );
    setGeneratedScreenshots([]);
  };

  const applyPatternDefaults = (pattern: StoreBackgroundPattern) => {
    const defaults = PATTERN_DEFAULTS[pattern];
    if (!defaults) return;
    setStyleField("patternColor", defaults.patternColor);
    setStyleField("patternOpacity", defaults.patternOpacity);
    setStyleField("patternScale", defaults.patternScale);
    setStyleField("dotSizeScale", defaults.dotSizeScale);
    setStyleField("waveStyle", defaults.waveStyle);
  };

  const handleGeneratePreviewCopy = async () => {
    if (!previewDimension) return;

    setPreviewCopyLoading(true);
    setError(null);
    try {
      const activeSlotInput = activeTemplateSlot
        ? slotInputs[activeTemplateSlot.slotId]
        : undefined;
      const activeSlotPreviewImage = activeTemplateSlot
        ? templateSlotPreviewImages[activeTemplateSlot.slotId]
        : undefined;
      const selectedPreviewImage = isScreenSlotsInputMode
        ? activeSlotInput
        : uploadedImages.find((image) => image.id === editorPreviewImageId);
      const generatedCopy = await ScreenshotService.generateTemplatePreviewCopy(
        {
          appName: selectedApp?.name || "Your App",
          appContext: selectedApp,
          targetPlatform,
          locale: "en",
          sourceImageDataUrl:
            activeSlotPreviewImage?.dataUrl || selectedPreviewImage?.dataUrl,
          context:
            activeSlotInput?.aiContext || selectedPreviewImage?.aiContext,
          dimensions: [previewDimension],
          model: copyModel || AIService.getDefaultModel(),
        },
      );

      setPreviewHeading(generatedCopy.heading);
      setPreviewSubtitle(generatedCopy.subtitle);
    } catch (generationError: any) {
      console.error(generationError);
      setError(generationError?.message || "Failed to generate preview copy.");
    } finally {
      setPreviewCopyLoading(false);
    }
  };

  const handlePreGenerateAiCopy = async () => {
    if (!selectedApp) {
      setError("Select an app first.");
      return;
    }
    const normalizedSources = getNormalizedInputSources();
    if (!normalizedSources.length) {
      setError(
        isScreenSlotsInputMode
          ? "Upload at least one screen slot image first."
          : "Upload at least one screenshot image first.",
      );
      return;
    }

    const selectedDimensions = expectedDimensions.filter((dimension) =>
      selectedDimensionKeys.includes(dimensionKey(dimension)),
    );
    if (selectedDimensions.length === 0) {
      setError("Select at least one device type before generating AI copy.");
      return;
    }

    try {
      setPreGeneratingCopy(true);
      setError(null);

      for (let index = 0; index < normalizedSources.length; index += 1) {
        const image = normalizedSources[index];
        setGenerationProgress({
          stage: "copy",
          message: `Generating AI heading and subtitle for ${image.name} (${index + 1}/${normalizedSources.length})`,
          completed: index,
          total: normalizedSources.length,
          percent: Math.round((index / normalizedSources.length) * 100),
        });

        const generatedCopy =
          await ScreenshotService.generateStoreScreenshotSourceCopy({
            appName: selectedApp.name,
            appContext: selectedApp,
            targetPlatform,
            sourceName: image.name,
            sourceImageDataUrl: image.dataUrl,
            useScreenshotContext,
            context: image.aiContext,
            dimensions: selectedDimensions,
            model: copyModel || AIService.getDefaultModel(),
          });

        if (isScreenSlotsInputMode && image.slotId) {
          updateSlotInput(image.slotId, "manualHeading", generatedCopy.heading);
          updateSlotInput(
            image.slotId,
            "manualSubtitle",
            generatedCopy.subtitle,
          );
        } else {
          setUploadedImages((previous) =>
            previous.map((item) =>
              item.file.name === image.name
                ? {
                    ...item,
                    manualHeading: generatedCopy.heading,
                    manualSubtitle: generatedCopy.subtitle,
                  }
                : item,
            ),
          );
        }
      }

      setGenerationProgress({
        stage: "complete",
        message: `Generated AI heading and subtitle suggestions for ${normalizedSources.length} screenshot${
          normalizedSources.length === 1 ? "" : "s"
        }. Review them before final generation.`,
        completed: normalizedSources.length,
        total: normalizedSources.length,
        percent: 100,
      });
    } catch (generationError: any) {
      console.error(generationError);
      setError(
        generationError?.message ||
          "Failed to pre-generate screenshot heading and subtitle suggestions.",
      );
    } finally {
      setPreGeneratingCopy(false);
    }
  };

  const handleGenerateTemplateWithAI = async () => {
    if (!selectedApp) {
      setError("Select a registered app first.");
      return;
    }
    setTemplateCreateLoading(true);
    setError(null);
    try {
      const schema = {
        type: "object",
        properties: {
          name: { type: "string" },
          description: { type: "string" },
          style: {
            type: "object",
            properties: {
              backgroundType: {
                type: "string",
                enum: ["color", "pattern", "gradient"],
              },
              backgroundColor: { type: "string" },
              backgroundPattern: {
                type: "string",
                enum: [
                  "dot_grid",
                  "diagonal_lines",
                  "soft_waves",
                  "aurora_glow",
                  "diamond_weave",
                ],
              },
              backgroundGradient: {
                type: "string",
                enum: [
                  "sunset_blush",
                  "hot_pink_glow",
                  "ocean_depth",
                  "mint_fresh",
                  "royal_plum",
                  "citrus_pop",
                  "midnight_steel",
                  "sky_lagoon",
                  "rose_gold",
                  "emerald_glow",
                  "twilight_berry",
                  "amber_sky",
                  "violet_dream",
                  "coral_reef",
                  "arctic_mist",
                  "lava_glow",
                  "forest_canopy",
                  "peach_bloom",
                  "neon_night",
                  "cobalt_sunrise",
                  "lavender_haze",
                ],
              },
              patternColor: { type: "string" },
              patternOpacity: { type: "number" },
              patternScale: { type: "number" },
              dotSizeScale: { type: "number" },
              waveStyle: {
                type: "string",
                enum: ["gentle", "balanced", "dramatic"],
              },
              headingColor: { type: "string" },
              subtitleColor: { type: "string" },
              headingFontFamily: { type: "string" },
              subtitleFontFamily: { type: "string" },
              headingFontStyle: { type: "string", enum: ["normal", "italic"] },
              subtitleFontStyle: { type: "string", enum: ["normal", "italic"] },
              showSubtitle: { type: "boolean" },
              headingWeight: { type: "number" },
              subtitleWeight: { type: "number" },
              headingSizeRatio: { type: "number" },
              subtitleSizeRatio: { type: "number" },
            },
          },
        },
        required: ["name", "description", "style"],
      };

      const prompt = [
        "Create a high-conversion app store screenshot template style for this app.",
        ...buildAppContextLines(selectedApp),
        `Target layout: ${newTemplateLayout}`,
        `Extra creative direction: ${
          aiTemplatePrompt.trim() ||
          "Use modern, clean and conversion-focused design choices."
        }`,
        "Do not suggest or modify positioning and frame layout values. Those are fixed by layout defaults.",
        "Return practical values for visual hierarchy, readability, and premium look.",
      ].join("\n");

      const result = await AIService.generateJSON<{
        name?: string;
        description?: string;
        style?: Partial<StoreScreenshotTemplateStyle>;
      }>(prompt, {
        model: copyModel || AIService.getDefaultModel(),
        temperature: 0.4,
        structuredOutputSchema: schema,
        structuredOutputName: "screenshot_template_style",
      });

      const baseTemplate =
        STORE_SCREENSHOT_TEMPLATES.find(
          (template) => template.layout === newTemplateLayout,
        ) || STORE_SCREENSHOT_TEMPLATES[0];
      const merged = sanitizeImportedStyle({
        ...baseTemplate.style,
        ...(result.style || {}),
        headingTopRatio: baseTemplate.style.headingTopRatio,
        subtitleOffsetRatio: baseTemplate.style.subtitleOffsetRatio,
        frameWidthRatio: baseTemplate.style.frameWidthRatio,
        frameHeightRatio: baseTemplate.style.frameHeightRatio,
        frameTopRatio: baseTemplate.style.frameTopRatio,
        androidDeviceFrameId: baseTemplate.style.androidDeviceFrameId,
      });
      if (!merged) {
        throw new Error("AI template style response was invalid.");
      }

      setDraftTemplateStyle(merged);
      if (result.name?.trim()) setNewTemplateName(result.name.trim());
      if (result.description?.trim())
        setNewTemplateDescription(result.description.trim());
    } catch (generationError: any) {
      console.error(generationError);
      setError(
        generationError?.message ||
          "Failed to generate template style with AI.",
      );
    } finally {
      setTemplateCreateLoading(false);
    }
  };

  const handleCreateCustomTemplate = async () => {
    if (!selectedApp?.appId || !user?.email) {
      setError("Select a registered app first.");
      return;
    }
    if (!newTemplateName.trim()) {
      setError("Template name is required.");
      return;
    }
    setTemplateActionLoading(true);
    setError(null);
    try {
      const nextSlots =
        newTemplateGenerationMode === "screen_slots"
          ? buildDefaultScreenSlots(draftTemplateStyle, newTemplateSlotCount)
          : [];
      const created = await ScreenshotService.createCustomStoreTemplate(
        user.email,
        selectedApp.appId,
        {
          layout: newTemplateLayout,
          name: newTemplateName.trim(),
          description: newTemplateDescription.trim() || "Custom template",
          style: draftTemplateStyle,
          generationMode: newTemplateGenerationMode,
          screenSlots: nextSlots,
        },
      );
      setCustomTemplates((prev) => [created, ...prev]);
      setSelectedTemplate(cloneStoreTemplate(created));
      setTabValue(1);
    } catch (createError: any) {
      console.error(createError);
      setError(createError?.message || "Failed to create custom template.");
    } finally {
      setTemplateActionLoading(false);
    }
  };

  const handleDeleteCustomTemplate = async (templateId: string) => {
    if (!user?.email || !selectedApp?.appId) return;
    setTemplateActionLoading(true);
    setError(null);
    try {
      await ScreenshotService.deleteCustomStoreTemplate(user.email, templateId);
      const updated = await ScreenshotService.getCustomStoreTemplates(
        user.email,
        selectedApp.appId,
      );
      setCustomTemplates(updated);
      if (selectedTemplate.id === templateId) {
        setSelectedTemplate(cloneStoreTemplate(STORE_SCREENSHOT_TEMPLATES[0]));
      }
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete custom template.");
    } finally {
      setTemplateActionLoading(false);
    }
  };

  const handleRenameCustomTemplate = async (templateId: string) => {
    if (!user?.email || !selectedApp?.appId) return;
    const nextName = (templateRenameDrafts[templateId] || "").trim();
    if (!nextName) {
      setError("Template name cannot be empty.");
      return;
    }
    setTemplateManagerBusyId(templateId);
    setError(null);
    try {
      await ScreenshotService.updateCustomStoreTemplate(
        user.email,
        templateId,
        {
          name: nextName,
        },
      );
      const updated = await ScreenshotService.getCustomStoreTemplates(
        user.email,
        selectedApp.appId,
      );
      setCustomTemplates(updated);
      if (selectedTemplate.id === templateId) {
        const selected = updated.find((template) => template.id === templateId);
        if (selected) {
          setSelectedTemplate(cloneStoreTemplate(selected));
        }
      }
    } catch (renameError: any) {
      console.error(renameError);
      setError(renameError?.message || "Failed to rename template.");
    } finally {
      setTemplateManagerBusyId(null);
    }
  };

  const handleGenerate = async () => {
    if (!selectedApp) {
      setError("Select an app first.");
      return;
    }
    const normalizedSources = getNormalizedInputSources();
    if (!normalizedSources.length) {
      setError(
        isScreenSlotsInputMode
          ? "Upload at least one screen slot image."
          : "Upload at least one screenshot image.",
      );
      return;
    }
    const selectedDimensions = expectedDimensions.filter((dimension) =>
      selectedDimensionKeys.includes(dimensionKey(dimension)),
    );
    if (selectedDimensions.length === 0) {
      setError("Select at least one device type.");
      return;
    }
    if (selectedLocales.length === 0) {
      setError("Select at least one locale.");
      return;
    }
    if (
      (copyInputMode === "manual" || copyInputMode === "ai") &&
      normalizedSources.some(
        (image) => !image.manualHeading.trim() || !image.manualSubtitle.trim(),
      )
    ) {
      setError(
        copyInputMode === "ai"
          ? "Run AI pre-generate and review the suggested heading and subtitle for every uploaded screenshot before final generation."
          : "For manual mode, add heading and subtitle for every uploaded screenshot.",
      );
      return;
    }

    setLoading(true);
    setError(null);
    setActiveGenerationJobId(null);
    const initialProgress: StoreScreenshotGenerationProgress = {
      stage: "preparing",
      message: "Preparing screenshot generation",
      completed: 0,
      total: 1,
      percent: 0,
    };
    setGenerationProgress(initialProgress);
    const jobId = `ss_job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const createdAt = new Date().toISOString();
    const newJob: ScreenshotJobRecord = {
      id: jobId,
      appId: selectedApp.appId,
      appName: selectedApp.name,
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      targetPlatform,
      locales: selectedLocales,
      dimensionLabels: selectedDimensions.map((dimension) =>
        formatScreenshotDeviceLabel(dimension),
      ),
      sourceCount: normalizedSources.length,
      expectedOutputCount:
        normalizedSources.length *
        selectedLocales.length *
        selectedDimensions.length,
      generatedCount: 0,
      status: "queued",
      progress: initialProgress,
      createdAt,
      updatedAt: createdAt,
    };
    setScreenshotJobs((prev) => [newJob, ...prev]);

    const updateJob = (
      updates: Partial<ScreenshotJobRecord>,
      nextProgress?: StoreScreenshotGenerationProgress | null,
    ) => {
      const updatedAt = new Date().toISOString();
      setScreenshotJobs((prev) =>
        prev.map((job) =>
          job.id === jobId
            ? {
                ...job,
                ...updates,
                progress:
                  nextProgress !== undefined ? nextProgress : job.progress,
                updatedAt,
              }
            : job,
        ),
      );
      if (nextProgress !== undefined) {
        setGenerationProgress(nextProgress);
      }
    };

    const templateForGeneration = cloneStoreTemplate(selectedTemplate);
    const totalExpectedOutputs =
      normalizedSources.length *
      selectedLocales.length *
      selectedDimensions.length;
    const shouldCaptureGeneratedResults =
      totalExpectedOutputs <= MAX_INLINE_GENERATED_SCREENSHOTS;
    let persistedVersion: { versionId: string; versionNumber: number } | null =
      null;
    let persistedAssetCount = 0;

    try {
      updateJob({
        status: "processing",
        templateName: templateForGeneration.name,
      });

      if (user?.email && !useCloud) {
        const createdVersion =
          await ScreenshotService.createScreenshotGenerationVersion({
            userId: user.email,
            appId: selectedApp.appId,
            templateId: templateForGeneration.id,
            templateName: templateForGeneration.name,
            initialAssetCount: 0,
            expectedAssetCount: totalExpectedOutputs,
          });
        persistedVersion = {
          versionId: createdVersion.versionId,
          versionNumber: createdVersion.versionNumber,
        };
        updateJob({
          savedVersionId: createdVersion.versionId,
          savedVersionNumber: createdVersion.versionNumber,
        });
      }

      let generated: GeneratedStoreScreenshot[] = [];

      if (useCloud) {
        // Cloud generation
        await ScreenshotService.generateScreenshotsCloud({
          userId: user?.email || "",
          jobId,
          appId: selectedApp.appId,
          appName: selectedApp.name,
          templateId: templateForGeneration.id,
          templateName: templateForGeneration.name,
          targetPlatform,
          locales: selectedLocales,
          dimensions: selectedDimensions,
          sourceImages: normalizedSources.map((image) => ({
            sourceName: image.name,
            sourceImageUrl: image.dataUrl,
            heading: image.manualHeading,
            subtitle: image.manualSubtitle,
            slotId: image.slotId,
            slotName: image.slotName,
            slotOrder: image.slotOrder,
            slotStyle: image.slotStyle,
            overlayImages: [],
          })),
          template: templateForGeneration,
          onProgress: (progress) => {
            updateJob({}, progress);
          },
        });
        updateJob(
          {
            status: "processing",
            generatedCount: 0,
          },
          {
            stage: "preparing",
            message:
              "Cloud generation queued. Track progress in Screenshot Jobs.",
            completed: 0,
            total: totalExpectedOutputs,
            percent: 0,
          },
        );
        setActiveGenerationJobId(jobId);
        setGeneratedScreenshots([]);
        return;
      } else {
        // Client generation
        generated = await ScreenshotService.generateStoreScreenshots({
          appId: selectedApp.appId,
          appName: selectedApp.name,
          appContext: selectedApp,
          targetPlatform,
          template: templateForGeneration,
          sourceImages: normalizedSources.map((image) => ({
            name: image.name,
            dataUrl: image.dataUrl,
            aiContext: image.aiContext,
            manualHeading: image.manualHeading,
            manualSubtitle: image.manualSubtitle,
            slotId: image.slotId,
            slotName: image.slotName,
            slotOrder: image.slotOrder,
            slotStyle: image.slotStyle,
            overlayImages: image.overlayImages,
          })),
          dimensions: selectedDimensions,
          model: copyModel || AIService.getDefaultModel(),
          copyMode: copyInputMode,
          useScreenshotContext,
          locales: selectedLocales,
          previewDeviceId,
          captureGeneratedResults: shouldCaptureGeneratedResults,
          onLocaleGenerated: async ({ screenshots }) => {
            if (!user?.email || !persistedVersion || screenshots.length === 0) {
              return;
            }

            const savedAssets =
              await ScreenshotService.appendGeneratedScreenshotsToVersion({
                userId: user.email,
                appId: selectedApp.appId,
                versionId: persistedVersion.versionId,
                versionNumber: persistedVersion.versionNumber,
                templateId: templateForGeneration.id,
                templateName: templateForGeneration.name,
                screenshots,
                existingAssetCount: persistedAssetCount,
                totalExpectedAssets: totalExpectedOutputs,
                onProgress: (progress) => {
                  updateJob(
                    {
                      status: "processing",
                      generatedCount: progress.completed,
                      savedVersionId: persistedVersion?.versionId,
                      savedVersionNumber: persistedVersion?.versionNumber,
                    },
                    progress,
                  );
                },
              });
            persistedAssetCount += savedAssets.length;
            updateJob({
              generatedCount: persistedAssetCount,
              savedVersionId: persistedVersion.versionId,
              savedVersionNumber: persistedVersion.versionNumber,
            });
          },
          onProgress: (progress) => {
            updateJob(
              {
                status: "processing",
                generatedCount:
                  progress.stage === "render" || progress.stage === "complete"
                    ? Math.min(
                        normalizedSources.length *
                          selectedLocales.length *
                          selectedDimensions.length,
                        progress.completed,
                      )
                    : 0,
              },
              progress,
            );
          },
        });
      }
      const shouldRenderInline =
        shouldCaptureGeneratedResults && generated.length > 0;
      setGeneratedScreenshots(shouldRenderInline ? generated : []);
      updateJob({
        status: "processing",
        generatedCount: user?.email ? persistedAssetCount : generated.length,
      });
      if (user?.email && persistedVersion) {
        await ScreenshotService.finalizeScreenshotGenerationVersion({
          versionId: persistedVersion.versionId,
          status: "completed",
          assetCount: persistedAssetCount,
          expectedAssetCount: totalExpectedOutputs,
        });
        const completedProgress: StoreScreenshotGenerationProgress = {
          stage: "complete",
          message: `Saved version v${persistedVersion.versionNumber} to the content library`,
          completed: persistedAssetCount,
          total: totalExpectedOutputs,
          percent: 100,
        };
        updateJob(
          {
            status: "completed",
            generatedCount: persistedAssetCount,
            savedVersionId: persistedVersion.versionId,
            savedVersionNumber: persistedVersion.versionNumber,
          },
          completedProgress,
        );
        await loadLibraryAssets();
        setPersistedJobsReloadKey((current) => current + 1);
      } else {
        updateJob({
          status: "completed",
          generatedCount: generated.length,
        });
      }
    } catch (generationError: any) {
      console.error(generationError);
      setError(generationError?.message || "Failed to generate screenshots.");
      if (persistedVersion) {
        try {
          await ScreenshotService.finalizeScreenshotGenerationVersion({
            versionId: persistedVersion.versionId,
            status: persistedAssetCount > 0 ? "partial" : "failed",
            assetCount: persistedAssetCount,
            expectedAssetCount: totalExpectedOutputs,
          });
          await loadLibraryAssets();
          setPersistedJobsReloadKey((current) => current + 1);
        } catch (finalizeError) {
          console.error(finalizeError);
        }
      }
      updateJob(
        {
          status: "failed",
          generatedCount: persistedAssetCount,
          savedVersionId: persistedVersion?.versionId,
          savedVersionNumber: persistedVersion?.versionNumber,
          error: generationError?.message || "Failed to generate screenshots.",
        },
        null,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadOne = async (screenshot: GeneratedStoreScreenshot) => {
    if (!selectedApp) return;
    await ScreenshotService.downloadGeneratedScreenshot(
      screenshot,
      selectedApp.name,
    );
  };

  const handleDownloadAll = async () => {
    if (!selectedApp || !generatedScreenshots.length) return;
    await ScreenshotService.downloadGeneratedScreenshotsAsZip(
      generatedScreenshots,
      selectedApp.name,
    );
  };

  const handleDownloadStoredAsset = async (
    asset: StoredStoreScreenshotAsset,
  ) => {
    const link = document.createElement("a");
    link.href = asset.downloadUrl;
    link.download = `${asset.platform}-${asset.locale}-${asset.dimension.name}-${asset.dimension.width}x${asset.dimension.height}-v${asset.versionNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteStoredAsset = async (assetId: string) => {
    if (!user?.email) return;
    try {
      await ScreenshotService.deleteStoredScreenshotAsset(user.email, assetId);
      await loadLibraryAssets();
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete screenshot asset.");
    }
  };

  const handleDownloadVersionZip = async (
    version: StoreScreenshotGenerationVersion,
  ) => {
    if (!selectedApp?.appId || !user?.email) return;
    try {
      if (version.zipDownloadUrl) {
        window.open(version.zipDownloadUrl, "_blank", "noopener,noreferrer");
        return;
      }
      setError(null);
      setZipBusyVersionId(version.versionId);
      const existingZipJob = zipExportJobs[version.versionId];
      const exportJob = isZipJobInProgress(existingZipJob)
        ? existingZipJob
        : await ScreenshotService.requestStoreScreenshotsZipExportJob(
            selectedApp.appId,
            version.versionId,
            user.email,
          );
      setZipExportJobs((prev) => ({
        ...prev,
        [version.versionId]: exportJob,
      }));

      const finishedJob = await waitForZipExportCompletion(
        version.versionId,
        exportJob,
      );
      if (!finishedJob?.downloadUrl) {
        setError(
          "ZIP export is still running in the background. Progress will continue to update here.",
        );
        return;
      }
      window.open(finishedJob.downloadUrl, "_blank", "noopener,noreferrer");
      await loadLibraryAssets();
    } catch (zipError: any) {
      console.error(zipError);
      setError(
        zipError?.message || "Failed to prepare the screenshot ZIP export.",
      );
    } finally {
      setZipBusyVersionId(null);
    }
  };

  const handleDownloadVersionZipInBrowser = async (
    version: StoreScreenshotGenerationVersion,
  ) => {
    if (!selectedApp?.name) return;

    try {
      setError(null);
      setClientZipBusyVersionId(version.versionId);
      const versionAssets = libraryAssets.filter(
        (asset) => asset.versionId === version.versionId,
      );

      await ScreenshotService.downloadStoredScreenshotsAsZip(
        versionAssets,
        selectedApp.name,
        { versionNumber: version.versionNumber },
      );
    } catch (zipError: any) {
      console.error(zipError);
      setError(zipError?.message || "Failed to create the browser ZIP export.");
    } finally {
      setClientZipBusyVersionId(null);
    }
  };

  const handleOpenScreenshotJobs = () => {
    if (selectedApp?.appId) {
      setSelectedJobsAppFilter(selectedApp.appId);
    }
    setTabValue(5);
  };

  const waitForZipExportCompletion = async (
    versionKey: string,
    exportJob: StoreScreenshotZipExportJob,
    maxWaitMs = 2 * 60 * 1000,
  ): Promise<StoreScreenshotZipExportJob | null> => {
    const startedAt = Date.now();
    let latestJob: StoreScreenshotZipExportJob | null = exportJob;

    while (Date.now() - startedAt < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      latestJob = await ScreenshotService.getStoreScreenshotsZipExportJob(
        exportJob.jobId,
      );

      if (!latestJob) {
        continue;
      }

      const polledJob = latestJob;
      setZipExportJobs((prev) => ({
        ...prev,
        [versionKey]: polledJob,
      }));

      if (polledJob.status === "completed" && polledJob.downloadUrl) {
        return polledJob;
      }

      if (polledJob.status === "failed") {
        throw new Error(
          polledJob.error ||
            polledJob.progress.message ||
            "Failed to prepare the screenshot ZIP export.",
        );
      }
    }

    return null;
  };

  const getScreenshotPublishJob = useCallback(
    async (
      jobId: string,
      store: ScreenshotPublishDestination,
    ): Promise<GooglePlayScreenshotPublishResponse | null> =>
      store === "app_store_connect"
        ? ScreenshotService.getAppStoreConnectScreenshotPublishJob(jobId)
        : ScreenshotService.getGooglePlayScreenshotPublishJob(jobId),
    [],
  );

  const waitForScreenshotPublishCompletion = async (
    jobId: string,
    initialResult: GooglePlayScreenshotPublishResponse,
    store: ScreenshotPublishDestination,
    maxWaitMs = 30 * 60 * 1000,
  ): Promise<GooglePlayScreenshotPublishResponse> => {
    const startedAt = Date.now();
    let latestResult: GooglePlayScreenshotPublishResponse = initialResult;

    while (Date.now() - startedAt < maxWaitMs) {
      if (
        latestResult.status === "completed" ||
        latestResult.status === "partial" ||
        latestResult.status === "failed"
      ) {
        return latestResult;
      }

      await new Promise((resolve) => setTimeout(resolve, 4000));
      const polledResult = latestResult.jobId
        ? await getScreenshotPublishJob(jobId, store)
        : null;

      if (!polledResult) {
        continue;
      }

      latestResult = polledResult;
      setScreenshotPublishResult(polledResult);
    }

    return latestResult;
  };

  useEffect(() => {
    if (
      !publishDialogOpen ||
      !screenshotPublishResult?.jobId ||
      !(
        screenshotPublishResult.status === "queued" ||
        screenshotPublishResult.status === "processing"
      )
    ) {
      return;
    }

    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      const polledResult = await getScreenshotPublishJob(
        screenshotPublishResult.jobId!,
        (screenshotPublishResult.store as ScreenshotPublishDestination) ||
          publishDestination,
      );

      if (!polledResult || cancelled) {
        return;
      }

      setScreenshotPublishResult(polledResult);
    }, 4000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    getScreenshotPublishJob,
    publishDestination,
    publishDialogOpen,
    screenshotPublishResult?.jobId,
    screenshotPublishResult?.store,
    screenshotPublishResult?.status,
  ]);

  useEffect(() => {
    const activeJobs = Object.entries(zipExportJobs).filter(([, zipJob]) =>
      isZipJobInProgress(zipJob),
    );

    if (activeJobs.length === 0) {
      return;
    }

    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      const updates = await Promise.all(
        activeJobs.map(async ([versionKey, zipJob]) => ({
          versionKey,
          zipJob: await ScreenshotService.getStoreScreenshotsZipExportJob(
            zipJob.jobId,
          ),
        })),
      );

      if (cancelled) {
        return;
      }

      let shouldRefreshLibrary = false;
      setZipExportJobs((prev) => {
        const next = { ...prev };
        updates.forEach(({ versionKey, zipJob }) => {
          if (zipJob) {
            next[versionKey] = zipJob;
            if (zipJob.status === "completed" && zipJob.downloadUrl) {
              shouldRefreshLibrary = true;
            }
          }
        });
        return next;
      });

      setScreenshotJobs((prev) =>
        prev.map((job) => {
          const savedVersionId = job.savedVersionId;
          if (!savedVersionId) {
            return job;
          }
          const updatedZipJob = updates.find(
            (item) => item.versionKey === savedVersionId,
          )?.zipJob;
          if (!updatedZipJob || !updatedZipJob.downloadUrl) {
            return job;
          }
          return {
            ...job,
            zipDownloadUrl: updatedZipJob.downloadUrl,
            zipFileName: updatedZipJob.fileName,
            zipCreatedAt: updatedZipJob.zipCreatedAt,
          };
        }),
      );

      if (shouldRefreshLibrary && selectedApp?.appId) {
        await loadLibraryAssets();
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [zipExportJobs, selectedApp?.appId, loadLibraryAssets]);

  useEffect(() => {
    if (tabValue !== 5 || !selectedJobsAppFilter) {
      return;
    }

    let cancelled = false;

    const syncActivePublishJobs = async () => {
      try {
        const appId =
          selectedJobsAppFilter === ALL_APPS_FILTER
            ? undefined
            : selectedJobsAppFilter;
        const [googlePlayJobs, appStoreConnectJobs] = await Promise.all([
          ScreenshotService.listGooglePlayScreenshotPublishJobs(
            appId ? { appId } : undefined,
          ),
          ScreenshotService.listAppStoreConnectScreenshotPublishJobs(
            appId ? { appId } : undefined,
          ),
        ]);
        const jobs = [...googlePlayJobs, ...appStoreConnectJobs].sort(
          (left, right) => {
            const leftTime = new Date(
              left.updatedAt || left.createdAt || 0,
            ).getTime();
            const rightTime = new Date(
              right.updatedAt || right.createdAt || 0,
            ).getTime();
            return rightTime - leftTime;
          },
        );

        if (cancelled) {
          return;
        }

        const visibleVersionIds = new Set(
          visibleScreenshotJobs
            .map((job) => String(job.savedVersionId || "").trim())
            .filter(Boolean),
        );
        const nextMap: Record<string, GooglePlayScreenshotPublishResponse> = {};

        jobs.forEach((job) => {
          const versionId = String(job.versionId || "").trim();
          if (!versionId || !visibleVersionIds.has(versionId)) {
            return;
          }
          if (job.status !== "queued" && job.status !== "processing") {
            return;
          }
          if (!nextMap[versionId]) {
            nextMap[versionId] = job;
          }
        });

        setActivePublishJobsByVersionId(nextMap);
      } catch (error) {
        console.error("Failed to sync active screenshot publish jobs:", error);
        if (!cancelled) {
          setActivePublishJobsByVersionId({});
        }
      }
    };

    syncActivePublishJobs();
    const intervalId = window.setInterval(syncActivePublishJobs, 6000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [tabValue, selectedJobsAppFilter, visibleScreenshotJobs]);

  const handleDownloadJobAssets = async (job: ScreenshotJobRecord) => {
    const savedVersionId = job.savedVersionId;
    if (!savedVersionId || !user?.email) return;
    try {
      if (job.zipDownloadUrl) {
        window.open(job.zipDownloadUrl, "_blank", "noopener,noreferrer");
        return;
      }
      setError(null);
      setZipBusyJobId(job.id);
      const existingZipJob = zipExportJobs[savedVersionId];
      const exportJob = isZipJobInProgress(existingZipJob)
        ? existingZipJob
        : await ScreenshotService.requestStoreScreenshotsZipExportJob(
            job.appId,
            savedVersionId,
            user.email,
          );
      setZipExportJobs((prev) => ({
        ...prev,
        [savedVersionId]: exportJob,
      }));

      const finishedJob = await waitForZipExportCompletion(
        savedVersionId,
        exportJob,
      );
      if (!finishedJob?.downloadUrl) {
        setError(
          "ZIP export is still running in the background. Progress will continue to update here.",
        );
        return;
      }
      setScreenshotJobs((prev) =>
        prev.map((item) =>
          item.id === job.id
            ? {
                ...item,
                zipDownloadUrl: finishedJob.downloadUrl,
                zipFileName: finishedJob.fileName,
                zipCreatedAt: finishedJob.zipCreatedAt,
              }
            : item,
        ),
      );
      window.open(finishedJob.downloadUrl, "_blank", "noopener,noreferrer");
      if (selectedApp?.appId === job.appId) {
        await loadLibraryAssets();
      }
    } catch (zipError: any) {
      console.error(zipError);
      setError(
        zipError?.message || "Failed to prepare the screenshot job ZIP export.",
      );
    } finally {
      setZipBusyJobId(null);
    }
  };

  const handleDownloadJobAssetsInBrowser = async (job: ScreenshotJobRecord) => {
    const savedVersionId = job.savedVersionId;
    if (!savedVersionId || !user?.email) return;

    try {
      setError(null);
      setClientZipBusyJobId(job.id);

      let versionAssets = libraryAssets.filter(
        (asset) =>
          asset.versionId === savedVersionId && asset.appId === job.appId,
      );

      if (!versionAssets.length) {
        const appAssets = await ScreenshotService.getStoredScreenshotsByAppId(
          job.appId,
          user.email,
        );
        versionAssets = appAssets.filter(
          (asset) => asset.versionId === savedVersionId,
        );
      }

      await ScreenshotService.downloadStoredScreenshotsAsZip(
        versionAssets,
        job.appName,
        { versionNumber: job.savedVersionNumber },
      );
    } catch (zipError: any) {
      console.error(zipError);
      setError(zipError?.message || "Failed to create the browser ZIP export.");
    } finally {
      setClientZipBusyJobId(null);
    }
  };

  const handleDeleteScreenshotJob = async (job: ScreenshotJobRecord) => {
    if (!user?.email) return;

    const isSavedVersionJob = Boolean(job.savedVersionId);
    const isFailedUnsavedJob = job.status === "failed" && !job.savedVersionId;
    if (!isSavedVersionJob && !isFailedUnsavedJob) return;

    const confirmed = window.confirm(
      isSavedVersionJob
        ? `Delete screenshot job for ${job.appName} (${job.templateName})?\n\nThis will permanently remove the saved screenshot version, its Firestore records, generated ZIP exports, and all screenshot files from Storage.`
        : `Delete failed screenshot job for ${job.appName} (${job.templateName})?\n\nThis will remove the failed job record from Firestore.`,
    );
    if (!confirmed) {
      return;
    }

    setDeletingJobId(job.id);
    setError(null);

    try {
      if (job.savedVersionId) {
        await ScreenshotService.deleteScreenshotGenerationVersion(
          user.email,
          job.savedVersionId,
        );
      } else {
        await ScreenshotService.deleteScreenshotJobRecord(user.email, job.id);
      }
      setScreenshotJobs((prev) => prev.filter((item) => item.id !== job.id));
      if (job.savedVersionId) {
        setZipExportJobs((prev) => {
          const next = { ...prev };
          delete next[job.savedVersionId!];
          return next;
        });
      }
      if (selectedApp?.appId === job.appId) {
        await loadLibraryAssets();
      }
      setPersistedJobsReloadKey((current) => current + 1);
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete screenshot job.");
    } finally {
      setDeletingJobId(null);
    }
  };

  const renderScreenshotJobCard = (job: ScreenshotJobRecord) => {
    const activePublishJob = job.savedVersionId
      ? activePublishJobsByVersionId[job.savedVersionId]
      : undefined;

    return (
      <Card key={job.id} variant="outlined">
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
              mb: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {job.appName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {job.templateName} • {job.targetPlatform.toUpperCase()}
              </Typography>
            </Box>
            <Chip
              label={job.status}
              color={getScreenshotJobStatusColor(job.status)}
              size="small"
            />
          </Box>

          {activePublishJob && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  {activePublishJob.message ||
                    "A screenshot publish is currently running for this saved version."}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleTrackGooglePlayScreenshotPublish(job)}
                >
                  Track Publish
                </Button>
              </Box>
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1,
              mb: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Locales:{" "}
              {job.locales.map((locale) => locale.toUpperCase()).join(", ")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Devices: {job.dimensionLabels.join(", ")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sources: {job.sourceCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expected outputs: {job.expectedOutputCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generated: {job.generatedCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Updated: {new Date(job.updatedAt).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Job ID: {job.id}
            </Typography>
          </Box>

          {job.progress ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                {job.progress.message}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, job.progress.percent))}
                sx={{ height: 8, borderRadius: 999 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                {job.progress.stage.toUpperCase()} • {job.progress.completed}/
                {job.progress.total}
              </Typography>
            </Box>
          ) : (
            <Alert
              severity={job.status === "failed" ? "error" : "info"}
              sx={{ mb: 1.5 }}
            >
              {job.error || "No active progress available for this job."}
            </Alert>
          )}

          {job.savedVersionNumber &&
            (() => {
              const zipJobState = job.savedVersionId
                ? zipExportJobs[job.savedVersionId]
                : undefined;
              const zipProgressPercent = Math.max(
                0,
                Math.min(100, Math.round(zipJobState?.progress.percent || 0)),
              );
              const zipStatusLabel = job.zipDownloadUrl
                ? "ZIP Ready"
                : isZipJobStale(zipJobState)
                  ? "ZIP Stalled"
                  : zipJobState?.status === "failed"
                    ? "ZIP Failed"
                    : isZipJobInProgress(zipJobState)
                      ? `ZIP Preparing ${zipProgressPercent}%`
                      : "ZIP Not Started";
              const zipStatusColor = job.zipDownloadUrl
                ? "success"
                : isZipJobStale(zipJobState)
                  ? "warning"
                  : zipJobState?.status === "failed"
                    ? "error"
                    : isZipJobInProgress(zipJobState)
                      ? "info"
                      : "default";

              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Saved to content library as version v
                      {job.savedVersionNumber}
                    </Typography>
                    <Chip
                      size="small"
                      label={zipStatusLabel}
                      color={zipStatusColor}
                      variant={job.zipDownloadUrl ? "filled" : "outlined"}
                    />
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<UploadIcon />}
                        disabled={!job.savedVersionId}
                        onClick={() => openPublishDialogForJob(job)}
                      >
                        Publish
                      </Button>
                      {activePublishJob && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={() =>
                            handleTrackGooglePlayScreenshotPublish(job)
                          }
                        >
                          Track Publish
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          zipBusyJobId === job.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        disabled={
                          !job.savedVersionId || zipBusyJobId === job.id
                        }
                        onClick={() => handleDownloadJobAssets(job)}
                      >
                        {zipBusyJobId === job.id
                          ? `Preparing ZIP ${zipProgressPercent}%`
                          : job.zipDownloadUrl
                            ? "Download All Assets"
                            : "Prepare Assets ZIP"}
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={
                          clientZipBusyJobId === job.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        disabled={
                          !job.savedVersionId || clientZipBusyJobId === job.id
                        }
                        onClick={() => handleDownloadJobAssetsInBrowser(job)}
                      >
                        {clientZipBusyJobId === job.id
                          ? "Creating Browser ZIP..."
                          : "ZIP In Browser"}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={
                          deletingJobId === job.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteIcon />
                          )
                        }
                        disabled={
                          (job.status !== "failed" && !job.savedVersionId) ||
                          deletingJobId === job.id
                        }
                        onClick={() => handleDeleteScreenshotJob(job)}
                      >
                        {deletingJobId === job.id
                          ? "Deleting..."
                          : "Delete Job"}
                      </Button>
                    </Box>
                  </Box>

                  {(zipJobState || job.zipDownloadUrl) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.75 }}
                    >
                      {job.zipDownloadUrl
                        ? "ZIP export is ready for download."
                        : isZipJobStale(zipJobState)
                          ? "The previous ZIP export stalled. Start a new ZIP export to retry with the current backend fix."
                          : zipJobState?.status === "failed"
                            ? zipJobState.error ||
                              zipJobState.progress.message ||
                              "ZIP export failed."
                            : zipJobState?.progress.message ||
                              "ZIP export has not started yet."}
                    </Typography>
                  )}

                  {zipBusyJobId === job.id && zipJobState && (
                    <Box sx={{ mt: 1.25 }}>
                      <LinearProgress
                        variant="determinate"
                        value={zipProgressPercent}
                        sx={{ height: 6, borderRadius: 999, mb: 0.75 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {zipJobState.progress.message}
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()}

          {!job.savedVersionNumber && job.status === "failed" && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                size="small"
                color="error"
                variant="text"
                startIcon={
                  deletingJobId === job.id ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DeleteIcon />
                  )
                }
                disabled={deletingJobId === job.id}
                onClick={() => handleDeleteScreenshotJob(job)}
              >
                {deletingJobId === job.id ? "Deleting..." : "Delete Failed Job"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleValidateGooglePlayScreenshots = async () => {
    if (!publishTargetJob?.savedVersionId) {
      setError("This screenshot job does not have a saved version to publish.");
      return;
    }

    try {
      setValidatingGooglePlayPublish(true);
      setError(null);
      setScreenshotPublishResult(null);
      setValidationOverviewCopied(false);

      const response =
        publishDestination === "app_store_connect"
          ? await ScreenshotService.validateAppStoreConnectScreenshots({
              appId: publishTargetJob.appId,
              versionId: publishTargetJob.savedVersionId,
              mode: publishMode,
              ...(publishMode === "single_locale"
                ? { locale: publishLocale }
                : {}),
              ...(publishMode === "selected_locales"
                ? { locales: publishSelectedLocales }
                : {}),
            })
          : await ScreenshotService.validateGooglePlayScreenshots({
              appId: publishTargetJob.appId,
              versionId: publishTargetJob.savedVersionId,
              mode: publishMode,
              ...(publishMode === "single_locale"
                ? { locale: publishLocale }
                : {}),
              ...(publishMode === "selected_locales"
                ? { locales: publishSelectedLocales }
                : {}),
            });

      setScreenshotValidationResult(response);
    } catch (validationError: any) {
      console.error(validationError);
      const responseData = validationError?.responseData as
        | GooglePlayScreenshotValidationResponse
        | undefined;
      if (responseData?.packageName) {
        setScreenshotValidationResult(responseData);
      }
      setError(
        responseData?.message ||
          validationError?.message ||
          publishDestination === "app_store_connect"
          ? "Failed to validate App Store Connect screenshots."
          : "Failed to validate Google Play screenshots.",
      );
    } finally {
      setValidatingGooglePlayPublish(false);
    }
  };

  const handlePublishGooglePlayScreenshots = async () => {
    if (!publishTargetJob?.savedVersionId) {
      setError("This screenshot job does not have a saved version to publish.");
      return;
    }

    try {
      setPublishingToGooglePlay(true);
      setError(null);
      setScreenshotPublishResult(null);

      const response =
        publishDestination === "app_store_connect"
          ? await ScreenshotService.publishAppStoreConnectScreenshots({
              appId: publishTargetJob.appId,
              versionId: publishTargetJob.savedVersionId,
              mode: publishMode,
              ...(publishMode === "single_locale"
                ? { locale: publishLocale }
                : {}),
              ...(publishMode === "selected_locales"
                ? { locales: publishSelectedLocales }
                : {}),
            })
          : await ScreenshotService.publishGooglePlayScreenshots({
              appId: publishTargetJob.appId,
              versionId: publishTargetJob.savedVersionId,
              mode: publishMode,
              ...(publishMode === "single_locale"
                ? { locale: publishLocale }
                : {}),
              ...(publishMode === "selected_locales"
                ? { locales: publishSelectedLocales }
                : {}),
            });

      setScreenshotPublishResult(response);
      setPublishSections((prev) => ({
        ...prev,
        result: true,
      }));

      const finalResponse =
        response.jobId &&
        (response.status === "queued" || response.status === "processing")
          ? await waitForScreenshotPublishCompletion(
              response.jobId,
              response,
              (response.store as ScreenshotPublishDestination) ||
                publishDestination,
            )
          : response;

      if (
        publishDestination === "google_play" &&
        finalResponse.lastPublishedAt
      ) {
        const publishedAt = new Date(finalResponse.lastPublishedAt);
        setApps((prev) =>
          prev.map((app) =>
            app.appId === publishTargetJob.appId
              ? { ...app, googlePlayLastPublishedAt: publishedAt }
              : app,
          ),
        );
      }

      setScreenshotPublishResult(finalResponse);
    } catch (publishError: any) {
      console.error(publishError);
      const responseData = publishError?.responseData as
        | GooglePlayScreenshotPublishResponse
        | undefined;
      if (responseData?.packageName) {
        setScreenshotPublishResult(responseData);
      }
      setError(
        publishError?.message ||
          (publishDestination === "app_store_connect"
            ? "Failed to publish screenshots to App Store Connect."
            : "Failed to publish screenshots to Google Play."),
      );
    } finally {
      setPublishingToGooglePlay(false);
    }
  };

  const handleTrackGooglePlayScreenshotPublish = useCallback(
    async (job: ScreenshotJobRecord) => {
      const versionId = String(job.savedVersionId || "").trim();
      if (!versionId) {
        setError("This screenshot job does not have a saved version to track.");
        return;
      }

      const activeJob = activePublishJobsByVersionId[versionId];
      if (!activeJob?.jobId) {
        setError(
          "No active screenshot publish job is currently running for this version.",
        );
        return;
      }

      const latestJob =
        (await getScreenshotPublishJob(
          activeJob.jobId,
          (activeJob.store as ScreenshotPublishDestination) || "google_play",
        )) || activeJob;

      setPublishTargetJob(job);
      setPublishDialogOpen(true);
      setPublishDestination(
        (latestJob.store as ScreenshotPublishDestination) || "google_play",
      );
      setPublishMode("all_locales");
      setPublishLocale("");
      setPublishSelectedLocales([]);
      setScreenshotValidationResult(null);
      setScreenshotPublishResult(latestJob);
      setPublishConfirmationChecked(false);
      setPublishOverrideChecked(false);
      setValidationOverviewCopied(false);
      setValidationLocaleFilter(ALL_VALIDATION_LOCALES_FILTER);
      setExpandedValidationLocales({});
      setExpandedValidationGroups({});
      setPublishSections({
        overview: false,
        target: false,
        validate: false,
        confirm: false,
        danger: false,
        result: true,
      });
      setError(null);
    },
    [activePublishJobsByVersionId, getScreenshotPublishJob],
  );

  const handlePublishSectionToggle =
    (section: ScreenshotPublishSectionKey) =>
    (_event: React.SyntheticEvent, expanded: boolean) => {
      setPublishSections((prev) => ({
        ...prev,
        [section]: expanded,
      }));
    };

  const renderRegisteredAppSelector = () => (
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>Select a registered app</InputLabel>
      <Select
        value={selectedApp?.appId || ""}
        label="Select a registered app"
        onChange={handleSelectApp}
      >
        <MenuItem value="">
          <em>Select a registered app</em>
        </MenuItem>
        {apps.map((app) => (
          <MenuItem key={app.appId} value={app.appId}>
            {app.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderScreenshotJobsAppSelector = () => (
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel>Filter jobs by app</InputLabel>
      <Select
        value={selectedJobsAppFilter}
        label="Filter jobs by app"
        onChange={(event) => setSelectedJobsAppFilter(event.target.value)}
      >
        <MenuItem value="">
          <em>No app selected</em>
        </MenuItem>
        <MenuItem value={ALL_APPS_FILTER}>All apps</MenuItem>
        {apps.map((app) => (
          <MenuItem key={app.appId} value={app.appId}>
            {app.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const screenshotPublishValidation =
    publishDestination === "app_store_connect"
      ? getAppStoreConnectScreenshotPublishValidation(publishTargetJob)
      : getScreenshotPublishValidation(publishTargetJob);
  const publishDestinationLabel =
    publishDestination === "app_store_connect"
      ? "App Store Connect"
      : "Google Play";
  const publishTargetLocales =
    publishMode === "single_locale"
      ? publishLocale
        ? [publishLocale]
        : []
      : publishMode === "selected_locales"
        ? publishSelectedLocales.filter((locale) =>
            screenshotPublishValidation.publishableLocales.includes(locale),
          )
        : screenshotPublishValidation.publishableLocales;
  const validatedLocales =
    screenshotValidationResult?.results
      .filter((item) => item.status !== "skipped")
      .map((item) => item.locale)
      .sort() || [];
  const selectedTargetLocales = [...publishTargetLocales].sort();
  const validationMatchesCurrentSelection =
    JSON.stringify(validatedLocales) === JSON.stringify(selectedTargetLocales);
  const validationHasErrors = Boolean(
    screenshotValidationResult?.results.some((item) => item.status === "error"),
  );
  const validationHasOnlyMissingOrSuccess = Boolean(
    screenshotValidationResult?.results.length &&
    screenshotValidationResult.results.every(
      (item) =>
        item.status === "success" ||
        item.status === "missing" ||
        item.status === "skipped",
    ),
  );
  const successfulScreenshotPublishCount =
    screenshotPublishResult?.results.filter((item) => item.status === "success")
      .length || 0;
  const skippedScreenshotPublishCount =
    screenshotPublishResult?.results.filter((item) => item.status === "skipped")
      .length || 0;
  const failedScreenshotPublishCount =
    screenshotPublishResult?.results.filter((item) => item.status === "error")
      .length || 0;
  const screenshotPublishBatchStates = (
    screenshotPublishResult?.batchStates || []
  ).slice();
  const activeScreenshotPublishBatch = screenshotPublishBatchStates.find(
    (batch) => batch.status === "processing",
  );
  const screenshotPublishStatus = screenshotPublishResult?.status || "";
  const screenshotPublishPercent = screenshotPublishResult?.totalBatches
    ? Math.round(
        ((screenshotPublishResult.completedBatches || 0) /
          screenshotPublishResult.totalBatches) *
          100,
      )
    : 0;
  const isScreenshotPublishRunning =
    screenshotPublishStatus === "queued" ||
    screenshotPublishStatus === "processing";
  const isScreenshotPublishCommitted =
    screenshotPublishResult?.commitStatus === "committed";
  const isScreenshotPublishFinished =
    screenshotPublishStatus === "completed" ||
    screenshotPublishStatus === "partial" ||
    screenshotPublishStatus === "failed" ||
    (!screenshotPublishStatus && isScreenshotPublishCommitted);
  const screenshotPublishEta = useMemo(() => {
    if (
      !isScreenshotPublishRunning ||
      !screenshotPublishResult?.createdAt ||
      !screenshotPublishResult?.totalBatches ||
      !screenshotPublishResult?.completedBatches
    ) {
      return "";
    }

    const startedAt = new Date(screenshotPublishResult.createdAt).getTime();
    if (!Number.isFinite(startedAt) || startedAt <= 0) {
      return "";
    }

    const completedBatches = screenshotPublishResult.completedBatches || 0;
    const totalBatches = screenshotPublishResult.totalBatches || 0;
    if (completedBatches <= 0 || totalBatches <= completedBatches) {
      return completedBatches >= totalBatches ? "Almost done" : "";
    }

    const elapsedMs = Date.now() - startedAt;
    if (elapsedMs <= 0) {
      return "";
    }

    const averageBatchMs = elapsedMs / completedBatches;
    const remainingBatches = totalBatches - completedBatches;
    const remainingMs = averageBatchMs * remainingBatches;
    const totalSeconds = Math.max(0, Math.round(remainingMs / 1000));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `About ${hours}h ${minutes}m remaining`;
    }

    if (minutes > 0) {
      return `About ${minutes}m ${seconds}s remaining`;
    }

    return `About ${seconds}s remaining`;
  }, [
    isScreenshotPublishRunning,
    screenshotPublishResult?.createdAt,
    screenshotPublishResult?.completedBatches,
    screenshotPublishResult?.totalBatches,
  ]);
  const filteredValidationResults = screenshotValidationResult?.results.filter(
    (result) =>
      validationLocaleFilter === ALL_VALIDATION_LOCALES_FILTER ||
      result.locale === validationLocaleFilter,
  );
  const canPublishScreenshots =
    screenshotPublishValidation.blockers.length === 0 &&
    selectedTargetLocales.length > 0 &&
    Boolean(screenshotValidationResult) &&
    validationMatchesCurrentSelection &&
    !validationHasErrors &&
    publishConfirmationChecked;
  const canOverridePublishScreenshots =
    screenshotPublishValidation.blockers.length === 0 &&
    selectedTargetLocales.length > 0 &&
    publishOverrideChecked;
  const publishTargetApp = getAppForJob(publishTargetJob);
  const publishTargetLastPublishedDate = formatPublishDate(
    publishTargetApp?.googlePlayLastPublishedAt,
  );
  const validationOverviewItems =
    screenshotValidationResult?.results.filter(
      (item) => item.status !== "success",
    ) || [];
  const validationOverviewErrors = validationOverviewItems.filter(
    (item) => item.status === "error",
  );
  const validationOverviewMissing = validationOverviewItems.filter(
    (item) => item.status === "missing",
  );
  const validationOverviewSummary = validationOverviewItems
    .map((item) => {
      const localeLabel = getLocaleDisplayLabel(item.locale);
      const playSuffix =
        item.playLocale && item.playLocale !== item.locale
          ? ` -> ${item.playLocale}`
          : "";
      return `${localeLabel}${playSuffix}: ${item.message}`;
    })
    .join("\n");
  const screenshotValidationStatus = canPublishScreenshots
    ? {
        label: "Ready to publish",
        color: "success" as const,
      }
    : canOverridePublishScreenshots
      ? {
          label: "Danger override armed",
          color: "warning" as const,
        }
      : screenshotPublishValidation.blockers.length > 0
        ? {
            label: "Blocked",
            color: "warning" as const,
          }
        : screenshotValidationResult
          ? validationMatchesCurrentSelection &&
            !validationHasErrors &&
            validationHasOnlyMissingOrSuccess
            ? {
                label: "Validated",
                color: "success" as const,
              }
            : {
                label: "Review validation",
                color: "warning" as const,
              }
          : {
              label: "Validation required",
              color: "info" as const,
            };
  const showPublishInProgressScreen =
    publishingToGooglePlay || isScreenshotPublishRunning;
  const handleOpenScreenshotPreviewViewer = useCallback(
    (
      images: Array<{ url: string; sourceName?: string }>,
      startIndex: number,
      title: string,
      localeLabel: string,
    ) => {
      if (!images.length) return;
      setScreenshotPreviewViewer({
        open: true,
        title,
        localeLabel,
        images,
        currentIndex: Math.max(0, Math.min(startIndex, images.length - 1)),
      });
    },
    [],
  );
  const handleCloseScreenshotPreviewViewer = useCallback(() => {
    setScreenshotPreviewViewer((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);
  const handleShiftScreenshotPreviewViewer = useCallback(
    (direction: -1 | 1) => {
      setScreenshotPreviewViewer((prev) => {
        if (!prev.images.length) return prev;
        const nextIndex =
          (prev.currentIndex + direction + prev.images.length) %
          prev.images.length;
        return {
          ...prev,
          currentIndex: nextIndex,
        };
      });
    },
    [],
  );
  const handleCopyValidationOverview = useCallback(async () => {
    if (!validationOverviewSummary) return;
    try {
      await navigator.clipboard.writeText(validationOverviewSummary);
      setValidationOverviewCopied(true);
      window.setTimeout(() => setValidationOverviewCopied(false), 1800);
    } catch (error) {
      console.error(error);
      setError("Failed to copy validation overview.");
    }
  }, [setError, validationOverviewSummary]);
  const renderScreenshotGroupPreview = (
    title: string,
    images: Array<{ url: string; sourceName?: string }> | undefined,
    emptyLabel: string,
    localeLabel: string,
    highlight = false,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 1.75,
        borderColor: highlight ? "primary.light" : "divider",
        bgcolor: highlight ? "rgba(25, 118, 210, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      {images && images.length > 0 ? (
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))",
            gap: 1,
          }}
        >
          {images.map((image, index) => (
            <Box key={`${title}-${image.url}-${index}`}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() =>
                  handleOpenScreenshotPreviewViewer(
                    images,
                    index,
                    title,
                    localeLabel,
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleOpenScreenshotPreviewViewer(
                      images,
                      index,
                      title,
                      localeLabel,
                    );
                  }
                }}
                sx={{
                  borderRadius: 1.25,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: highlight ? "primary.light" : "divider",
                  bgcolor: "grey.100",
                  aspectRatio: "9 / 16",
                  cursor: "pointer",
                  transition: "transform 180ms ease, box-shadow 180ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 20px rgba(15, 23, 42, 0.12)",
                  },
                  "&:focus-visible": {
                    outline: "2px solid #1976d2",
                    outlineOffset: 2,
                  },
                }}
              >
                <img
                  src={image.url}
                  alt={image.sourceName || `${title} ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {image.sourceName || `Image ${index + 1}`}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {emptyLabel}
        </Typography>
      )}
    </Paper>
  );

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        App Store Screenshot Creator
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload screenshots, select one template, choose target platform, and
        auto-generate framed store screenshots for all matching devices.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)}>
          <Tab label="Generator" />
          <Tab label="Template Editor" />
          <Tab label="Template Creator" />
          <Tab label="Template Manager" />
          <Tab label="Content Library" />
          <Tab label="Screenshot Jobs" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Screenshot Setup
              </Typography>

              <Accordion
                expanded={expandedPhases.input}
                onChange={(_, expanded) =>
                  setExpandedPhases((prev) => ({ ...prev, input: expanded }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    1. App & Content Setup
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select a registered app</InputLabel>
                    <Select
                      value={selectedApp?.appId || ""}
                      label="Select a registered app"
                      onChange={handleSelectApp}
                    >
                      <MenuItem value="">
                        <em>Select a registered app</em>
                      </MenuItem>
                      {apps.map((app) => (
                        <MenuItem key={app.appId} value={app.appId}>
                          {app.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Screenshot Target Platform</InputLabel>
                    <Select
                      value={targetPlatform}
                      label="Screenshot Target Platform"
                      onChange={(event) =>
                        setTargetPlatform(
                          event.target.value as ScreenshotTargetPlatform,
                        )
                      }
                    >
                      <MenuItem value="ios">iOS</MenuItem>
                      <MenuItem value="android">Android</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Screenshot Template</InputLabel>
                    <Select
                      value={selectedTemplate.id}
                      label="Screenshot Template"
                      onChange={(event) => {
                        const template = allTemplateOptions.find(
                          (item) => item.id === event.target.value,
                        );
                        if (template)
                          setSelectedTemplate(cloneStoreTemplate(template));
                      }}
                    >
                      {allTemplateOptions.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                          {template.isCustom ? " (Custom)" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Screenshot Content Creation Method</InputLabel>
                    <Select
                      value={copyInputMode}
                      label="Screenshot Content Creation Method"
                      onChange={(event) =>
                        setCopyInputMode(event.target.value as "ai" | "manual")
                      }
                    >
                      <MenuItem value="ai">Use AI</MenuItem>
                      <MenuItem value="manual">Manual</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={templateScreenSlots.length === 0}
                  >
                    <InputLabel>Screenshot Input Mode</InputLabel>
                    <Select
                      value={generationInputMode}
                      label="Screenshot Input Mode"
                      onChange={(event) =>
                        setGenerationInputMode(
                          event.target.value as StoreScreenshotGenerationMode,
                        )
                      }
                    >
                      <MenuItem value="single_source">
                        Single image list
                      </MenuItem>
                      <MenuItem value="screen_slots">
                        Template screen slots
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={copyInputMode !== "ai"}
                  >
                    <InputLabel>Choose AI Model for Screenshot Copy</InputLabel>
                    <Select
                      value={copyModel}
                      label="Choose AI Model for Screenshot Copy"
                      onChange={(event) => setCopyModel(event.target.value)}
                    >
                      {SCREENSHOT_COPY_MODELS.map((modelOption) => (
                        <MenuItem
                          key={modelOption.value || "default"}
                          value={modelOption.value}
                        >
                          {modelOption.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 2 }}
                  >
                    Current AI copy model:{" "}
                    {copyModel || AIService.getDefaultModel()}
                  </Typography>
                  <FormControlLabel
                    sx={{ mb: 2 }}
                    control={
                      <Checkbox
                        checked={useScreenshotContext}
                        onChange={(event) =>
                          setUseScreenshotContext(event.target.checked)
                        }
                        disabled={copyInputMode !== "ai"}
                      />
                    }
                    label="Use screenshot image context (AI visual analysis)"
                  />
                  {copyInputMode === "ai" && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1.25 }}
                      >
                        Enabled: AI analyzes each screenshot image + app context
                        to suggest the source heading and subtitle. Review and
                        edit those suggestions before final generation. The
                        reviewed copy is what will be localized across your
                        selected locales.
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={
                          preGeneratingCopy ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <GenerateIcon />
                          )
                        }
                        onClick={handlePreGenerateAiCopy}
                        disabled={
                          preGeneratingCopy ||
                          !selectedApp ||
                          activeSourceCount === 0 ||
                          selectedDimensionKeys.length === 0
                        }
                      >
                        {preGeneratingCopy
                          ? "Generating AI Copy Suggestions..."
                          : "Pre-generate Heading & Subtitle with AI"}
                      </Button>
                    </Box>
                  )}

                  {!isScreenSlotsInputMode && (
                    <Button
                      component="label"
                      fullWidth
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 1 }}
                    >
                      Upload Raw Screenshots
                      <input
                        hidden
                        multiple
                        accept="image/*"
                        type="file"
                        onChange={handleUploadScreenshots}
                      />
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sources: {activeSourceCount}{" "}
                {isScreenSlotsInputMode ? "slot image(s)" : "screenshot(s)"} |
                Selected devices: {selectedDimensionKeys.length} | Selected
                locales: {selectedLocales.length}
              </Typography>

              <Accordion
                expanded={expandedPhases.locales}
                onChange={(_, expanded) =>
                  setExpandedPhases((prev) => ({ ...prev, locales: expanded }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    2. Target Locales
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", gap: 1, mb: 1.2, flexWrap: "wrap" }}
                  >
                    <ToggleButtonGroup
                      value={localeSupportFilter}
                      exclusive
                      size="small"
                      sx={{ flexWrap: "wrap", gap: 1, mr: "auto" }}
                      onChange={(
                        _event,
                        value: StoreLocaleSupportFilter | null,
                      ) => {
                        if (value) {
                          setLocaleSupportFilter(value);
                        }
                      }}
                    >
                      <ToggleButton value="common">Common to both</ToggleButton>
                      <ToggleButton value="google_play">
                        Google only
                      </ToggleButton>
                      <ToggleButton value="app_store">Apple only</ToggleButton>
                      <ToggleButton value="all">All locales</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      size="small"
                      label="Find locale"
                      placeholder="Type locale name or code"
                      value={localeSearchQuery}
                      onChange={(event) =>
                        setLocaleSearchQuery(event.target.value)
                      }
                      sx={{ minWidth: { xs: "100%", md: 240 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setSelectedLocales(
                          Array.from(
                            new Set([
                              ...selectedLocales,
                              ...filteredScreenshotLocales.map(
                                (locale) => locale.code,
                              ),
                            ]),
                          ),
                        )
                      }
                    >
                      Select Visible
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        setSelectedLocales((prev) =>
                          prev.filter(
                            (code) =>
                              !filteredScreenshotLocales.some(
                                (locale) => locale.code === code,
                              ),
                          ),
                        )
                      }
                    >
                      Clear Visible
                    </Button>
                  </Box>
                  <FormGroup
                    sx={{
                      maxHeight: 220,
                      overflow: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {filteredScreenshotLocales.length === 0 ? (
                      <Alert severity="info">
                        No locales match the current filter.
                      </Alert>
                    ) : (
                      filteredScreenshotLocales.map((locale) => (
                        <FormControlLabel
                          key={locale.code}
                          control={
                            <Checkbox
                              checked={selectedLocales.includes(locale.code)}
                              onChange={() =>
                                setSelectedLocales((prev) =>
                                  prev.includes(locale.code)
                                    ? prev.filter(
                                        (item) => item !== locale.code,
                                      )
                                    : [...prev, locale.code],
                                )
                              }
                            />
                          }
                          label={`${locale.flag} ${locale.name}`}
                        />
                      ))
                    )}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedPhases.devices}
                onChange={(_, expanded) =>
                  setExpandedPhases((prev) => ({ ...prev, devices: expanded }))
                }
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    3. Target Device Types
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", gap: 1, mb: 1.2, flexWrap: "wrap" }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setSelectedDimensionKeys(
                          expectedDimensions.map((dimension) =>
                            dimensionKey(dimension),
                          ),
                        )
                      }
                    >
                      Select All Devices
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setSelectedDimensionKeys([])}
                    >
                      Clear Device Selection
                    </Button>
                  </Box>
                  <FormGroup>
                    {expectedDimensions.map((dimension) => {
                      const key = dimensionKey(dimension);
                      return (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              checked={selectedDimensionKeys.includes(key)}
                              onChange={() =>
                                setSelectedDimensionKeys((prev) =>
                                  prev.includes(key)
                                    ? prev.filter((item) => item !== key)
                                    : [...prev, key],
                                )
                              }
                            />
                          }
                          label={`${dimension.platform.toUpperCase()} - ${
                            dimension.name
                          } (${dimension.width}x${dimension.height})`}
                        />
                      );
                    })}
                  </FormGroup>
                </AccordionDetails>
              </Accordion>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={useCloud}
                    onChange={(e) => setUseCloud(e.target.checked)}
                  />
                }
                label="Use Cloud Rendering (faster, consistent)"
              />

              <Button
                fullWidth
                variant="contained"
                startIcon={<GenerateIcon />}
                disabled={
                  loading ||
                  preGeneratingCopy ||
                  !selectedApp ||
                  !styleReady ||
                  activeSourceCount === 0 ||
                  selectedDimensionKeys.length === 0 ||
                  selectedLocales.length === 0 ||
                  sourcesForCopyValidation.some(
                    (image) =>
                      !image.manualHeading.trim() ||
                      !image.manualSubtitle.trim(),
                  )
                }
                onClick={handleGenerate}
              >
                {loading ? "Generating..." : "Generate Store Screenshots"}
              </Button>

              {generationProgress && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    {generationProgress.message}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(
                      0,
                      Math.min(100, generationProgress.percent),
                    )}
                    sx={{ height: 8, borderRadius: 999 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.75 }}
                  >
                    {generationProgress.stage.toUpperCase()} •{" "}
                    {generationProgress.completed}/{generationProgress.total}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ mt: 0.75, px: 0 }}
                    onClick={handleOpenScreenshotJobs}
                  >
                    Open Screenshot Jobs
                  </Button>
                </Box>
              )}

              {generatedScreenshots.length > 0 && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ mt: 1.5 }}
                  onClick={handleDownloadAll}
                >
                  Download All as ZIP
                </Button>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {isScreenSlotsInputMode
                  ? "Template Screen Slots"
                  : "Uploaded Screenshots"}
              </Typography>
              {isScreenSlotsInputMode ? (
                templateScreenSlots.length === 0 ? (
                  <Alert severity="info">
                    This template has no configured screen slots. Switch to
                    Single image list mode or configure slots in the template.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {templateScreenSlots.map((slot) => {
                      const input = slotInputs[slot.slotId];
                      return (
                        <Grid item xs={12} sm={6} md={4} key={slot.slotId}>
                          <Card variant="outlined">
                            <Box sx={{ p: 1.25, pb: 0.5 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700 }}
                              >
                                {String(slot.order).padStart(2, "0")}.{" "}
                                {slot.name}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                aspectRatio: "9/16",
                                overflow: "hidden",
                                bgcolor: "action.hover",
                              }}
                            >
                              {input?.dataUrl ? (
                                <img
                                  src={
                                    slotStyledPreviews[slot.slotId] ||
                                    input.dataUrl
                                  }
                                  alt={input.fileName || slot.name}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                    fontSize: 13,
                                  }}
                                >
                                  No image uploaded
                                </Box>
                              )}
                            </Box>
                            <CardActions
                              sx={{ justifyContent: "space-between" }}
                            >
                              <Button
                                component="label"
                                size="small"
                                startIcon={<UploadIcon />}
                              >
                                {input?.dataUrl ? "Replace" : "Upload"}
                                <input
                                  hidden
                                  accept="image/*"
                                  type="file"
                                  onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (!file) return;
                                    await handleUploadSlotScreenshot(
                                      slot,
                                      file,
                                    );
                                    event.target.value = "";
                                  }}
                                />
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  removeSlotScreenshot(slot.slotId)
                                }
                                disabled={!input?.dataUrl}
                              >
                                Remove
                              </Button>
                            </CardActions>
                            <CardContent sx={{ pt: 0 }}>
                              {copyInputMode === "ai" ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    label="Optional AI Context"
                                    value={input?.aiContext || ""}
                                    onChange={(event) =>
                                      updateSlotInput(
                                        slot.slotId,
                                        "aiContext",
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="AI Suggested Heading"
                                    value={input?.manualHeading || ""}
                                    placeholder="Run AI pre-generate to fill this in"
                                    onChange={(event) =>
                                      updateSlotInput(
                                        slot.slotId,
                                        "manualHeading",
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="AI Suggested Subtitle"
                                    value={input?.manualSubtitle || ""}
                                    placeholder="Run AI pre-generate to fill this in"
                                    onChange={(event) =>
                                      updateSlotInput(
                                        slot.slotId,
                                        "manualSubtitle",
                                        event.target.value,
                                      )
                                    }
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Heading"
                                    value={input?.manualHeading || ""}
                                    onChange={(event) =>
                                      updateSlotInput(
                                        slot.slotId,
                                        "manualHeading",
                                        event.target.value,
                                      )
                                    }
                                  />
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Subtitle"
                                    value={input?.manualSubtitle || ""}
                                    onChange={(event) =>
                                      updateSlotInput(
                                        slot.slotId,
                                        "manualSubtitle",
                                        event.target.value,
                                      )
                                    }
                                  />
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )
              ) : uploadedImages.length === 0 ? (
                <Alert severity="info">Upload screenshots to start.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {uploadedImages.map((image) => (
                    <Grid item xs={12} sm={6} md={4} key={image.id}>
                      <Card variant="outlined">
                        <Box
                          sx={{
                            aspectRatio: "9/16",
                            overflow: "hidden",
                            bgcolor: "action.hover",
                          }}
                        >
                          <img
                            src={image.dataUrl}
                            alt={image.file.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                        <CardActions sx={{ justifyContent: "space-between" }}>
                          <Typography variant="caption">
                            {image.file.name}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeUploadedImage(image.id)}
                          >
                            Remove
                          </Button>
                        </CardActions>
                        <CardContent sx={{ pt: 0 }}>
                          {copyInputMode === "ai" ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                label="Optional AI Context"
                                value={image.aiContext}
                                onChange={(event) =>
                                  updateUploadedImage(
                                    image.id,
                                    "aiContext",
                                    event.target.value,
                                  )
                                }
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="AI Suggested Heading"
                                value={image.manualHeading}
                                placeholder="Run AI pre-generate to fill this in"
                                onChange={(event) =>
                                  updateUploadedImage(
                                    image.id,
                                    "manualHeading",
                                    event.target.value,
                                  )
                                }
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="AI Suggested Subtitle"
                                value={image.manualSubtitle}
                                placeholder="Run AI pre-generate to fill this in"
                                onChange={(event) =>
                                  updateUploadedImage(
                                    image.id,
                                    "manualSubtitle",
                                    event.target.value,
                                  )
                                }
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <TextField
                                fullWidth
                                size="small"
                                label="Heading"
                                value={image.manualHeading}
                                onChange={(event) =>
                                  updateUploadedImage(
                                    image.id,
                                    "manualHeading",
                                    event.target.value,
                                  )
                                }
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Subtitle"
                                value={image.manualSubtitle}
                                onChange={(event) =>
                                  updateUploadedImage(
                                    image.id,
                                    "manualSubtitle",
                                    event.target.value,
                                  )
                                }
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h6">
                  Generated Screenshots ({generatedScreenshots.length})
                </Typography>
                {generatedScreenshots.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAll}
                  >
                    Download All as ZIP
                  </Button>
                )}
              </Box>
              {generatedScreenshots.length === 0 ? (
                <Alert severity="info">
                  Generate screenshots to see output with device frame and
                  heading.
                </Alert>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  {groupedGeneratedScreenshots.map((platformGroup) => (
                    <Accordion
                      key={platformGroup.platform}
                      expanded={
                        expandedGeneratedPlatforms[platformGroup.platform] ??
                        true
                      }
                      onChange={(_, expanded) =>
                        setExpandedGeneratedPlatforms((prev) => ({
                          ...prev,
                          [platformGroup.platform]: expanded,
                        }))
                      }
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        overflow: "hidden",
                        "&:before": {
                          display: "none",
                        },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          {platformGroup.platform.toUpperCase()} (
                          {platformGroup.locales.reduce(
                            (count, localeGroup) =>
                              count + localeGroup.items.length,
                            0,
                          )}
                          )
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.8,
                        }}
                      >
                        {platformGroup.locales.map((localeGroup) => (
                          <Accordion
                            key={`${platformGroup.platform}_${localeGroup.locale}`}
                            expanded={
                              expandedGeneratedLocales[
                                `${platformGroup.platform}_${localeGroup.locale}`
                              ] ?? true
                            }
                            onChange={(_, expanded) =>
                              setExpandedGeneratedLocales((prev) => ({
                                ...prev,
                                [`${platformGroup.platform}_${localeGroup.locale}`]:
                                  expanded,
                              }))
                            }
                            disableGutters
                            elevation={0}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1.5,
                              overflow: "hidden",
                              "&:before": {
                                display: "none",
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                bgcolor: "rgba(15, 23, 42, 0.02)",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                              >
                                Locale: {localeGroup.locale.toUpperCase()} (
                                {localeGroup.items.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                {localeGroup.items.map((screenshot) => (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={screenshot.id}
                                  >
                                    <Card>
                                      <Box
                                        sx={{
                                          height: 330,
                                          overflow: "hidden",
                                          bgcolor: "action.hover",
                                        }}
                                      >
                                        <img
                                          src={screenshot.dataUrl}
                                          alt={`${screenshot.platform}-${screenshot.dimension.name}`}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </Box>
                                      <CardContent sx={{ pb: 1 }}>
                                        <Typography variant="subtitle2">
                                          {screenshot.dimension.name}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {screenshot.dimension.width} x{" "}
                                          {screenshot.dimension.height}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 1, fontWeight: 700 }}
                                        >
                                          {screenshot.heading}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: "block" }}
                                        >
                                          {screenshot.subtitle}
                                        </Typography>
                                      </CardContent>
                                      <CardActions>
                                        <Button
                                          size="small"
                                          startIcon={<DownloadIcon />}
                                          onClick={() =>
                                            handleDownloadOne(screenshot)
                                          }
                                        >
                                          Download
                                        </Button>
                                      </CardActions>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3 }}>
              {renderRegisteredAppSelector()}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Template Styling
              </Typography>

              <Accordion
                expanded={expandedTemplatePhases.setup}
                onChange={(_, expanded) =>
                  setExpandedTemplatePhases((prev) => ({
                    ...prev,
                    setup: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    1. Template Management
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Template Preset</InputLabel>
                    <Select
                      value={selectedTemplate.id}
                      label="Template Preset"
                      onChange={(event) => {
                        const template = allTemplateOptions.find(
                          (item) => item.id === event.target.value,
                        );
                        if (template)
                          setSelectedTemplate(cloneStoreTemplate(template));
                      }}
                    >
                      {allTemplateOptions.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                          {template.isCustom ? " (Custom)" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 1.2 }}>
                    <InputLabel>Android Device Frame (Generation)</InputLabel>
                    <Select
                      value={editorStyle.androidDeviceFrameId}
                      label="Android Device Frame (Generation)"
                      onChange={(event) =>
                        setStyleField(
                          "androidDeviceFrameId",
                          event.target.value as AndroidDeviceFrameId,
                        )
                      }
                    >
                      {ANDROID_DEVICE_FRAME_OPTIONS.map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          {device.brand} {device.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 2 }}
                  >
                    iOS uses iPhone 15 Pro Max frame. Android outputs use this
                    selected device frame.
                  </Typography>
                  {isSlotDesignMode && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        Slot Inspector
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1.2 }}
                      >
                        Editing:{" "}
                        {activeTemplateSlot
                          ? `${String(activeTemplateSlot.order).padStart(2, "0")}. ${
                              activeTemplateSlot.name
                            }`
                          : "No slot selected"}{" "}
                        (changes apply to this slot only).
                      </Typography>
                      <FormControl fullWidth sx={{ mb: 1.2 }}>
                        <InputLabel>Active Slot</InputLabel>
                        <Select
                          value={activeTemplateSlotId}
                          label="Active Slot"
                          onChange={(event) =>
                            setActiveTemplateSlotId(String(event.target.value))
                          }
                        >
                          {templateScreenSlots.map((slot) => (
                            <MenuItem key={slot.slotId} value={slot.slotId}>
                              {String(slot.order).padStart(2, "0")}. {slot.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <EditorGroup
                        title="Preview Source & Slot Context"
                        description="These controls affect only the currently active slot."
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Heading and subtitle are configured in the Generate
                          tab for each slot.
                        </Typography>
                        <Box
                          sx={{
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 1,
                            mb: 0.4,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.8 }}
                          >
                            Slot preview image (design-time only)
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              startIcon={<UploadIcon />}
                              disabled={!activeTemplateSlot}
                            >
                              {activeTemplateSlot &&
                              templateSlotPreviewImages[
                                activeTemplateSlot.slotId
                              ]
                                ? "Replace Preview"
                                : "Upload Preview"}
                              <input
                                hidden
                                accept="image/*"
                                type="file"
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  if (file && activeTemplateSlot) {
                                    handleUploadTemplateSlotPreview(
                                      activeTemplateSlot.slotId,
                                      file,
                                    );
                                  }
                                  event.target.value = "";
                                }}
                              />
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              disabled={
                                !activeTemplateSlot ||
                                !templateSlotPreviewImages[
                                  activeTemplateSlot.slotId
                                ]
                              }
                              onClick={() =>
                                activeTemplateSlot &&
                                removeTemplateSlotPreview(
                                  activeTemplateSlot.slotId,
                                )
                              }
                            >
                              Remove
                            </Button>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {activeTemplateSlot &&
                              templateSlotPreviewImages[
                                activeTemplateSlot.slotId
                              ]
                                ? templateSlotPreviewImages[
                                    activeTemplateSlot.slotId
                                  ].fileName
                                : "No preview image set for this slot"}
                            </Typography>
                          </Box>
                        </Box>
                      </EditorGroup>
                      <EditorGroup
                        title="Composition Controls"
                        description="Mask, transform, and layer stack for the active slot."
                      >
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 1.1,
                            mb: 0.4,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, mb: 0.8 }}
                          >
                            Composition
                          </Typography>
                          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel>Main Image Mask</InputLabel>
                            <Select
                              value={
                                editorStyle.mainImageMask?.type ||
                                "rounded_rect"
                              }
                              label="Main Image Mask"
                              onChange={(event) =>
                                setStyleField("mainImageMask", {
                                  ...editorStyle.mainImageMask,
                                  type: event.target
                                    .value as StoreCompositionMaskType,
                                })
                              }
                            >
                              {COMPOSITION_MASK_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <PrecisionSliderField
                            label="Mask Inset"
                            min={0}
                            max={0.28}
                            step={0.01}
                            value={editorStyle.mainImageMask?.insetRatio || 0}
                            formatValue={(value) => value.toFixed(2)}
                            onChange={(value) =>
                              setStyleField("mainImageMask", {
                                ...editorStyle.mainImageMask,
                                insetRatio: value,
                              })
                            }
                          />
                          <PrecisionSliderField
                            label="Corner Radius"
                            min={0}
                            max={0.5}
                            step={0.01}
                            value={editorStyle.mainImageMask?.radiusRatio || 0}
                            formatValue={(value) => value.toFixed(2)}
                            onChange={(value) =>
                              setStyleField("mainImageMask", {
                                ...editorStyle.mainImageMask,
                                radiusRatio: value,
                              })
                            }
                          />
                          {(editorStyle.mainImageMask?.type ===
                            "arch_wave_top" ||
                            editorStyle.mainImageMask?.type ===
                              "arch_bottom") && (
                            <>
                              <PrecisionSliderField
                                label="Wave Depth"
                                min={0}
                                max={0.35}
                                step={0.01}
                                value={
                                  editorStyle.mainImageMask?.waveDepthRatio ||
                                  0.1
                                }
                                formatValue={(value) => value.toFixed(2)}
                                onChange={(value) =>
                                  setStyleField("mainImageMask", {
                                    ...editorStyle.mainImageMask,
                                    waveDepthRatio: value,
                                  })
                                }
                              />
                            </>
                          )}

                          <PrecisionSliderField
                            label="Image X"
                            min={0}
                            max={1}
                            step={0.01}
                            value={
                              editorStyle.mainImageTransform?.xRatio || 0.5
                            }
                            formatValue={(value) => value.toFixed(2)}
                            onChange={(value) =>
                              setStyleField("mainImageTransform", {
                                ...editorStyle.mainImageTransform,
                                xRatio: value,
                              })
                            }
                          />
                          <PrecisionSliderField
                            label="Image Y"
                            min={0}
                            max={1}
                            step={0.01}
                            value={
                              editorStyle.mainImageTransform?.yRatio || 0.5
                            }
                            formatValue={(value) => value.toFixed(2)}
                            onChange={(value) =>
                              setStyleField("mainImageTransform", {
                                ...editorStyle.mainImageTransform,
                                yRatio: value,
                              })
                            }
                          />
                          <PrecisionSliderField
                            label="Image Scale"
                            min={0.2}
                            max={2.4}
                            step={0.01}
                            value={editorStyle.mainImageTransform?.scale || 1}
                            formatValue={(value) => `${value.toFixed(2)}x`}
                            onChange={(value) =>
                              setStyleField("mainImageTransform", {
                                ...editorStyle.mainImageTransform,
                                scale: value,
                              })
                            }
                          />
                          <PrecisionSliderField
                            label="Rotation"
                            min={-180}
                            max={180}
                            step={1}
                            value={
                              editorStyle.mainImageTransform?.rotationDeg || 0
                            }
                            formatValue={(value) => `${Math.round(value)}deg`}
                            onChange={(value) =>
                              setStyleField("mainImageTransform", {
                                ...editorStyle.mainImageTransform,
                                rotationDeg: value,
                              })
                            }
                          />
                          <PrecisionSliderField
                            label="Main Image Opacity"
                            min={0}
                            max={1}
                            step={0.01}
                            value={editorStyle.mainImageTransform?.opacity ?? 1}
                            formatValue={(value) =>
                              `${Math.round(value * 100)}%`
                            }
                            onChange={(value) =>
                              setStyleField("mainImageTransform", {
                                ...editorStyle.mainImageTransform,
                                opacity: value,
                              })
                            }
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.6, mb: 0.8 }}
                          >
                            Layer Stack (back to front)
                          </Typography>
                          <Box sx={{ display: "grid", gap: 0.6 }}>
                            {compositionLayerStack.map((layer, index) => {
                              const overlayName =
                                layer.kind === "overlay_image" && layer.refId
                                  ? activeSlotOverlayImages.find(
                                      (overlay) => overlay.id === layer.refId,
                                    )?.fileName
                                  : "";
                              return (
                                <Box
                                  key={layer.id}
                                  sx={{
                                    p: 0.8,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 0.8,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      {LAYER_KIND_LABELS[layer.kind]}
                                      {overlayName ? ` • ${overlayName}` : ""}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.4 }}>
                                      <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={layer.visible !== false}
                                            onChange={(event) =>
                                              toggleLayerVisibility(
                                                layer.id,
                                                event.target.checked,
                                              )
                                            }
                                          />
                                        }
                                        label=""
                                      />
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => moveLayer(layer.id, -1)}
                                        disabled={index === 0}
                                      >
                                        Up
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => moveLayer(layer.id, 1)}
                                        disabled={
                                          index ===
                                          compositionLayerStack.length - 1
                                        }
                                      >
                                        Down
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </EditorGroup>
                      <EditorGroup
                        title="Bulk Slot Style Actions"
                        description="Apply active slot style to multiple slots."
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={resetEditorStyle}
                          sx={{ mb: 1 }}
                        >
                          Reset Active Slot Style
                        </Button>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 0.8 }}
                        >
                          Copy active slot style to:
                        </Typography>
                        <FormGroup
                          sx={{ maxHeight: 140, overflow: "auto", mb: 1 }}
                        >
                          {templateScreenSlots
                            .filter(
                              (slot) => slot.slotId !== activeTemplateSlotId,
                            )
                            .map((slot) => (
                              <FormControlLabel
                                key={slot.slotId}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={bulkTargetSlotIds.includes(
                                      slot.slotId,
                                    )}
                                    onChange={() =>
                                      setBulkTargetSlotIds((previous) =>
                                        previous.includes(slot.slotId)
                                          ? previous.filter(
                                              (id) => id !== slot.slotId,
                                            )
                                          : [...previous, slot.slotId],
                                      )
                                    }
                                  />
                                }
                                label={`${String(slot.order).padStart(2, "0")}. ${slot.name}`}
                              />
                            ))}
                        </FormGroup>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={copyActiveStyleToTargetSlots}
                          disabled={
                            !activeTemplateSlot ||
                            bulkTargetSlotIds.length === 0
                          }
                        >
                          Copy Active Style
                        </Button>
                      </EditorGroup>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={resetEditorStyle}
                    >
                      {isSlotDesignMode
                        ? "Reset Active Slot Style"
                        : "Reset Entire Style"}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveTemplate}
                      disabled={
                        !styleReady || !selectedApp?.appId || !user?.email
                      }
                    >
                      Update Template
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {styleStatus === "loading" && "Loading style..."}
                      {styleStatus === "saved" && "Saved"}
                      {styleStatus === "error" && "Save error"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", gap: 1, mb: 0.5, flexWrap: "wrap" }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ExportIcon />}
                      onClick={handleExportTemplateConfig}
                      disabled={!selectedApp}
                    >
                      Export Template Config
                    </Button>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<ImportIcon />}
                    >
                      Import Template Config
                      <input
                        hidden
                        type="file"
                        accept="application/json,.json"
                        onChange={handleImportTemplateConfig}
                      />
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedTemplatePhases.preview}
                onChange={(_, expanded) =>
                  setExpandedTemplatePhases((prev) => ({
                    ...prev,
                    preview: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    2. Preview Content & Source
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Preview Device Frame</InputLabel>
                    <Select
                      value={previewDeviceId}
                      label="Preview Device Frame"
                      onChange={(event) =>
                        setPreviewDeviceId(
                          event.target.value as StorePreviewDeviceId,
                        )
                      }
                    >
                      {STORE_DEVICE_FRAME_PROFILES.map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          {device.brand} {device.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Sample Heading (Preview)"
                    value={previewHeading}
                    onChange={(event) => setPreviewHeading(event.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Sample Subheading (Preview)"
                    value={previewSubtitle}
                    onChange={(event) => setPreviewSubtitle(event.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GenerateIcon />}
                    onClick={handleGeneratePreviewCopy}
                    disabled={previewCopyLoading}
                    sx={{ mb: 2 }}
                  >
                    {previewCopyLoading
                      ? "Generating Preview Copy..."
                      : "Generate Sample Copy with AI"}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 2 }}
                  >
                    Generates new sample heading and subheading for preview
                    using app context and optional preview image.
                  </Typography>

                  {isSlotDesignMode ? (
                    <Alert severity="info">
                      Preview source uses the active slot preview image from
                      Slot Inspector first, then the active Generator slot
                      image. If neither is assigned, placeholder content is
                      shown.
                    </Alert>
                  ) : (
                    <>
                      <Button
                        component="label"
                        fullWidth
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        sx={{ mb: 1.5 }}
                      >
                        Upload Preview Source Screenshot
                        <input
                          hidden
                          multiple
                          accept="image/*"
                          type="file"
                          onChange={handleUploadScreenshots}
                        />
                      </Button>

                      <FormControl fullWidth sx={{ mb: 0.5 }}>
                        <InputLabel>Preview Source Image</InputLabel>
                        <Select
                          value={editorPreviewImageId}
                          label="Preview Source Image"
                          onChange={(event) =>
                            setEditorPreviewImageId(event.target.value)
                          }
                        >
                          {uploadedImages.length === 0 && (
                            <MenuItem value="">No uploaded images</MenuItem>
                          )}
                          {uploadedImages.map((image) => (
                            <MenuItem key={image.id} value={image.id}>
                              {image.file.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedTemplatePhases.background}
                onChange={(_, expanded) =>
                  setExpandedTemplatePhases((prev) => ({
                    ...prev,
                    background: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    3. Background Styling
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SettingLabel
                    title="Background Type"
                    onReset={() => resetStyleField("backgroundType")}
                  />
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Background Type</InputLabel>
                    <Select
                      value={editorStyle.backgroundType}
                      label="Background Type"
                      onChange={(event) =>
                        setStyleField(
                          "backgroundType",
                          event.target.value as
                            | "color"
                            | "pattern"
                            | "gradient",
                        )
                      }
                    >
                      <MenuItem value="color">Solid Color</MenuItem>
                      <MenuItem value="pattern">Pattern</MenuItem>
                      <MenuItem value="gradient">Gradient</MenuItem>
                    </Select>
                  </FormControl>

                  {editorStyle.backgroundType !== "gradient" && (
                    <>
                      <SettingLabel
                        title="Background Color"
                        onReset={() => resetStyleField("backgroundColor")}
                      />
                      <ColorField
                        value={editorStyle.backgroundColor}
                        onChange={(value) =>
                          setStyleField("backgroundColor", value)
                        }
                      />
                    </>
                  )}

                  {editorStyle.backgroundType === "gradient" && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1.5 }}
                      >
                        Select one of the curated high-conversion gradient
                        presets.
                      </Typography>
                      <SettingLabel
                        title="Gradient Preset"
                        onReset={() => resetStyleField("backgroundGradient")}
                      />
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Gradient Preset</InputLabel>
                        <Select
                          value={editorStyle.backgroundGradient}
                          label="Gradient Preset"
                          renderValue={(selected) => {
                            const current = BACKGROUND_GRADIENT_OPTIONS.find(
                              (option) => option.value === selected,
                            );
                            if (!current) {
                              return selected;
                            }
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    background: current.css,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="body2">
                                  {current.label}
                                </Typography>
                              </Box>
                            );
                          }}
                          onChange={(event) =>
                            setStyleField(
                              "backgroundGradient",
                              event.target.value as StoreGradientPresetId,
                            )
                          }
                        >
                          {BACKGROUND_GRADIENT_OPTIONS.map((gradient) => (
                            <MenuItem
                              key={gradient.value}
                              value={gradient.value}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    background: gradient.css,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="body2">
                                  {gradient.label}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {editorStyle.backgroundType === "pattern" && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1.5 }}
                      >
                        Pattern is rendered as an overlay on top of the selected
                        background color.
                      </Typography>
                      <SettingLabel
                        title="Background Pattern"
                        onReset={() => resetStyleField("backgroundPattern")}
                      />
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Pattern Preset</InputLabel>
                        <Select
                          value={editorStyle.backgroundPattern}
                          label="Pattern Preset"
                          onChange={(event) =>
                            setStyleField(
                              "backgroundPattern",
                              event.target.value as StoreBackgroundPattern,
                            )
                          }
                        >
                          {BACKGROUND_PATTERN_OPTIONS.map((pattern) => (
                            <MenuItem key={pattern.value} value={pattern.value}>
                              {pattern.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ mb: 2, px: 0.5 }}
                        onClick={() =>
                          applyPatternDefaults(editorStyle.backgroundPattern)
                        }
                      >
                        Reset Current Pattern Defaults
                      </Button>

                      <SettingLabel
                        title="Pattern Overlay Color"
                        onReset={() => resetStyleField("patternColor")}
                      />
                      <ColorField
                        value={editorStyle.patternColor}
                        onChange={(value) =>
                          setStyleField("patternColor", value)
                        }
                      />

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Quick pattern presets
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setStyleField("patternOpacity", 0.24);
                            setStyleField("patternScale", 1);
                            setStyleField("dotSizeScale", 1);
                          }}
                        >
                          Subtle
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setStyleField("patternOpacity", 0.42);
                            setStyleField("patternScale", 1);
                            setStyleField("dotSizeScale", 1);
                          }}
                        >
                          Balanced
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setStyleField("patternOpacity", 0.62);
                            setStyleField("patternScale", 0.86);
                            setStyleField("dotSizeScale", 1.35);
                          }}
                        >
                          Bold
                        </Button>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 0.2,
                        }}
                      >
                        <Tooltip title="Reset Pattern Opacity">
                          <IconButton
                            size="small"
                            onClick={() => resetStyleField("patternOpacity")}
                          >
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <PrecisionSliderField
                        label="Pattern Opacity"
                        min={0.12}
                        max={0.8}
                        step={0.02}
                        value={editorStyle.patternOpacity}
                        formatValue={(value) => `${Math.round(value * 100)}%`}
                        onChange={(value) =>
                          setStyleField("patternOpacity", value)
                        }
                        sx={{ mb: 0.4 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 2 }}
                      >
                        {Math.round(editorStyle.patternOpacity * 100)}%
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mb: 0.2,
                        }}
                      >
                        <Tooltip title="Reset Pattern Scale">
                          <IconButton
                            size="small"
                            onClick={() => resetStyleField("patternScale")}
                          >
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <PrecisionSliderField
                        label="Pattern Scale"
                        min={0.6}
                        max={1.8}
                        step={0.05}
                        value={editorStyle.patternScale}
                        formatValue={(value) => `${value.toFixed(2)}x`}
                        onChange={(value) =>
                          setStyleField("patternScale", value)
                        }
                        sx={{ mb: 0.4 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 2 }}
                      >
                        {editorStyle.patternScale.toFixed(2)}x
                      </Typography>

                      {editorStyle.backgroundPattern === "dot_grid" && (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mb: 0.2,
                            }}
                          >
                            <Tooltip title="Reset Dot Size">
                              <IconButton
                                size="small"
                                onClick={() => resetStyleField("dotSizeScale")}
                              >
                                <ResetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <PrecisionSliderField
                            label="Dot Size"
                            min={0.6}
                            max={2.6}
                            step={0.05}
                            value={editorStyle.dotSizeScale}
                            formatValue={(value) => `${value.toFixed(2)}x`}
                            onChange={(value) =>
                              setStyleField("dotSizeScale", value)
                            }
                            sx={{ mb: 0.4 }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 2 }}
                          >
                            {editorStyle.dotSizeScale.toFixed(2)}x
                          </Typography>
                        </>
                      )}

                      {editorStyle.backgroundPattern === "soft_waves" && (
                        <>
                          <SettingLabel
                            title="Wave Style"
                            onReset={() => resetStyleField("waveStyle")}
                          />
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Wave Style</InputLabel>
                            <Select
                              value={editorStyle.waveStyle}
                              label="Wave Style"
                              onChange={(event) =>
                                setStyleField(
                                  "waveStyle",
                                  event.target.value as WaveStyleVariant,
                                )
                              }
                            >
                              {WAVE_STYLE_OPTIONS.map((styleOption) => (
                                <MenuItem
                                  key={styleOption.value}
                                  value={styleOption.value}
                                >
                                  {styleOption.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      )}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedTemplatePhases.typography}
                onChange={(_, expanded) =>
                  setExpandedTemplatePhases((prev) => ({
                    ...prev,
                    typography: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    4. Text & Typography
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditorGroup
                    title="Color Palette"
                    description="Set text colors for heading and subheading."
                  >
                    <SettingLabel
                      title="Heading Color"
                      onReset={() => resetStyleField("headingColor")}
                    />
                    <ColorField
                      value={editorStyle.headingColor}
                      onChange={(value) => setStyleField("headingColor", value)}
                      swatches={BASIC_TEXT_COLOR_SWATCHES}
                    />

                    <SettingLabel
                      title="Subheading Color"
                      onReset={() => resetStyleField("subtitleColor")}
                    />
                    <ColorField
                      value={editorStyle.subtitleColor}
                      onChange={(value) =>
                        setStyleField("subtitleColor", value)
                      }
                      swatches={BASIC_TEXT_COLOR_SWATCHES}
                    />
                  </EditorGroup>

                  <EditorGroup
                    title="Font Family & Style"
                    description="Choose families and italic/normal style for both text layers."
                  >
                    <SettingLabel
                      title="Heading Font"
                      onReset={() => resetStyleField("headingFontFamily")}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Heading Font Family</InputLabel>
                      <Select
                        value={editorStyle.headingFontFamily}
                        label="Heading Font Family"
                        onChange={(event) =>
                          setStyleField(
                            "headingFontFamily",
                            event.target.value as string,
                          )
                        }
                      >
                        {POLISHED_FONT_OPTIONS.map((font) => (
                          <MenuItem
                            key={font.value}
                            value={font.value}
                            sx={{ fontFamily: font.value }}
                          >
                            {font.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <SettingLabel
                      title="Heading Font Style"
                      onReset={() => resetStyleField("headingFontStyle")}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Heading Font Style</InputLabel>
                      <Select
                        value={editorStyle.headingFontStyle}
                        label="Heading Font Style"
                        onChange={(event) =>
                          setStyleField(
                            "headingFontStyle",
                            event.target.value as StoreFontStyle,
                          )
                        }
                      >
                        {FONT_STYLE_OPTIONS.map((styleOption) => (
                          <MenuItem
                            key={styleOption.value}
                            value={styleOption.value}
                          >
                            {styleOption.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <SettingLabel
                      title="Subheading Font"
                      onReset={() => resetStyleField("subtitleFontFamily")}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Subheading Font Family</InputLabel>
                      <Select
                        value={editorStyle.subtitleFontFamily}
                        label="Subheading Font Family"
                        onChange={(event) =>
                          setStyleField(
                            "subtitleFontFamily",
                            event.target.value as string,
                          )
                        }
                      >
                        {POLISHED_FONT_OPTIONS.map((font) => (
                          <MenuItem
                            key={font.value}
                            value={font.value}
                            sx={{ fontFamily: font.value }}
                          >
                            {font.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <SettingLabel
                      title="Subheading Font Style"
                      onReset={() => resetStyleField("subtitleFontStyle")}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Subheading Font Style</InputLabel>
                      <Select
                        value={editorStyle.subtitleFontStyle}
                        label="Subheading Font Style"
                        onChange={(event) =>
                          setStyleField(
                            "subtitleFontStyle",
                            event.target.value as StoreFontStyle,
                          )
                        }
                      >
                        {FONT_STYLE_OPTIONS.map((styleOption) => (
                          <MenuItem
                            key={styleOption.value}
                            value={styleOption.value}
                          >
                            {styleOption.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </EditorGroup>

                  <EditorGroup
                    title="Visibility"
                    description="Turn subheading on or off for cleaner composition."
                  >
                    <SettingLabel
                      title="Show Subheading"
                      onReset={() => resetStyleField("showSubtitle")}
                    />
                    <FormControlLabel
                      sx={{ mb: 0.5 }}
                      control={
                        <Checkbox
                          checked={editorStyle.showSubtitle}
                          onChange={(event) =>
                            setStyleField("showSubtitle", event.target.checked)
                          }
                        />
                      }
                      label="Render subheading text"
                    />
                  </EditorGroup>

                  <EditorGroup
                    title="Weight"
                    description="Control text emphasis for hierarchy and readability."
                  >
                    <SettingLabel
                      title="Heading Weight"
                      onReset={() => resetStyleField("headingWeight")}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Heading Weight</InputLabel>
                      <Select
                        value={headingWeightPresetValue}
                        label="Heading Weight"
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next !== "custom") {
                            setStyleField("headingWeight", Number(next));
                          }
                        }}
                      >
                        {TEXT_WEIGHT_OPTIONS.map((weight) => (
                          <MenuItem
                            key={weight.value}
                            value={String(weight.value)}
                          >
                            {weight.label}
                          </MenuItem>
                        ))}
                        {headingWeightPresetValue === "custom" && (
                          <MenuItem value="custom">
                            Custom ({editorStyle.headingWeight})
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    <SettingLabel
                      title="Subheading Weight"
                      onReset={() => resetStyleField("subtitleWeight")}
                    />
                    <FormControl fullWidth sx={{ mb: 0.5 }}>
                      <InputLabel>Subheading Weight</InputLabel>
                      <Select
                        value={subtitleWeightPresetValue}
                        label="Subheading Weight"
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next !== "custom") {
                            setStyleField("subtitleWeight", Number(next));
                          }
                        }}
                      >
                        {TEXT_WEIGHT_OPTIONS.map((weight) => (
                          <MenuItem
                            key={weight.value}
                            value={String(weight.value)}
                          >
                            {weight.label}
                          </MenuItem>
                        ))}
                        {subtitleWeightPresetValue === "custom" && (
                          <MenuItem value="custom">
                            Custom ({editorStyle.subtitleWeight})
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </EditorGroup>

                  <EditorGroup
                    title="Size"
                    description="Set heading and subheading sizes with exact pixel values."
                  >
                    <SettingLabel
                      title="Heading Size"
                      onReset={() => resetStyleField("headingSizeRatio")}
                    />
                    <FormControl fullWidth sx={{ mb: 1.2 }}>
                      <InputLabel>Heading Size</InputLabel>
                      <Select
                        value={String(headingFontSizePx)}
                        label="Heading Size"
                        onChange={(event) =>
                          setStyleField(
                            "headingSizeRatio",
                            fontPxToRatio(
                              Number(event.target.value),
                              previewDimension.width,
                            ),
                          )
                        }
                      >
                        {FONT_SIZE_PX_OPTIONS.map((sizePx) => (
                          <MenuItem key={sizePx} value={String(sizePx)}>
                            {sizePx} px
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 2 }}
                    >
                      Based on the current preview width, heading renders at{" "}
                      {headingFontSizePx}px.
                    </Typography>

                    <SettingLabel
                      title="Subheading Size"
                      onReset={() => resetStyleField("subtitleSizeRatio")}
                    />
                    <FormControl fullWidth sx={{ mb: 1.2 }}>
                      <InputLabel>Subheading Size</InputLabel>
                      <Select
                        value={String(subtitleFontSizePx)}
                        label="Subheading Size"
                        onChange={(event) =>
                          setStyleField(
                            "subtitleSizeRatio",
                            fontPxToRatio(
                              Number(event.target.value),
                              previewDimension.width,
                            ),
                          )
                        }
                      >
                        {FONT_SIZE_PX_OPTIONS.map((sizePx) => (
                          <MenuItem key={sizePx} value={String(sizePx)}>
                            {sizePx} px
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Based on the current preview width, subheading renders at{" "}
                      {subtitleFontSizePx}px.
                    </Typography>
                  </EditorGroup>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expandedTemplatePhases.layout}
                onChange={(_, expanded) =>
                  setExpandedTemplatePhases((prev) => ({
                    ...prev,
                    layout: expanded,
                  }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    5. Positioning & Frame Layout
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 0.2,
                    }}
                  >
                    <Tooltip title="Reset Heading Vertical Position">
                      <IconButton
                        size="small"
                        onClick={() => resetStyleField("headingTopRatio")}
                      >
                        <ResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <PrecisionSliderField
                    label="Heading Vertical Position"
                    min={0.03}
                    max={0.14}
                    step={0.002}
                    value={editorStyle.headingTopRatio}
                    formatValue={(value) => value.toFixed(3)}
                    onChange={(value) =>
                      setStyleField("headingTopRatio", value)
                    }
                    sx={{ mb: 2 }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 0.2,
                    }}
                  >
                    <Tooltip title="Reset Subheading Vertical Offset">
                      <IconButton
                        size="small"
                        onClick={() => resetStyleField("subtitleOffsetRatio")}
                      >
                        <ResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <PrecisionSliderField
                    label="Subheading Vertical Offset"
                    min={0.02}
                    max={0.1}
                    step={0.002}
                    value={editorStyle.subtitleOffsetRatio}
                    formatValue={(value) => value.toFixed(3)}
                    onChange={(value) =>
                      setStyleField("subtitleOffsetRatio", value)
                    }
                    sx={{ mb: 2 }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 0.2,
                    }}
                  >
                    <Tooltip title="Reset Frame Width">
                      <IconButton
                        size="small"
                        onClick={() => resetStyleField("frameWidthRatio")}
                      >
                        <ResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <PrecisionSliderField
                    label="Frame Width"
                    min={0.62}
                    max={0.86}
                    step={0.01}
                    value={editorStyle.frameWidthRatio}
                    formatValue={(value) => `${Math.round(value * 100)}%`}
                    onChange={(value) =>
                      setStyleField("frameWidthRatio", value)
                    }
                    sx={{ mb: 2 }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 0.2,
                    }}
                  >
                    <Tooltip title="Reset Frame Top Position">
                      <IconButton
                        size="small"
                        onClick={() => resetStyleField("frameTopRatio")}
                      >
                        <ResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <PrecisionSliderField
                    label="Frame Top Position"
                    min={0.1}
                    max={0.24}
                    step={0.005}
                    value={editorStyle.frameTopRatio}
                    formatValue={(value) => value.toFixed(3)}
                    onChange={(value) => setStyleField("frameTopRatio", value)}
                  />
                  <FormControlLabel
                    sx={{ mt: 1.2 }}
                    control={
                      <Checkbox
                        checked={editorStyle.showDeviceFrame}
                        onChange={(event) =>
                          setStyleField("showDeviceFrame", event.target.checked)
                        }
                      />
                    }
                    label="Show device frame"
                  />
                  <FormControlLabel
                    sx={{ mt: 0.4, mb: 1 }}
                    control={
                      <Checkbox
                        checked={editorStyle.useAbsoluteTextPlacement}
                        onChange={(event) =>
                          setStyleField(
                            "useAbsoluteTextPlacement",
                            event.target.checked,
                          )
                        }
                      />
                    }
                    label="Use absolute text placement"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Absolute placement lets you place heading/subtitle anywhere.
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <PrecisionSliderField
                      label="Heading X"
                      min={0}
                      max={1}
                      step={0.01}
                      value={editorStyle.headingXRatio}
                      formatValue={(value) => value.toFixed(2)}
                      onChange={(value) =>
                        setStyleField("headingXRatio", value)
                      }
                    />
                    <PrecisionSliderField
                      label="Heading Y"
                      min={0}
                      max={1}
                      step={0.01}
                      value={editorStyle.headingYRatio}
                      formatValue={(value) => value.toFixed(2)}
                      onChange={(value) =>
                        setStyleField("headingYRatio", value)
                      }
                    />
                    <PrecisionSliderField
                      label="Heading Max Width"
                      min={0.2}
                      max={1}
                      step={0.01}
                      value={editorStyle.headingMaxWidthRatio}
                      formatValue={(value) => `${Math.round(value * 100)}%`}
                      onChange={(value) =>
                        setStyleField("headingMaxWidthRatio", value)
                      }
                    />
                    <FormControl fullWidth sx={{ mb: 1.2 }}>
                      <InputLabel>Heading Align</InputLabel>
                      <Select
                        value={editorStyle.headingTextAlign}
                        label="Heading Align"
                        onChange={(event) =>
                          setStyleField(
                            "headingTextAlign",
                            event.target.value as "left" | "center" | "right",
                          )
                        }
                      >
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="center">Center</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </Select>
                    </FormControl>
                    <PrecisionSliderField
                      label="Subtitle X"
                      min={0}
                      max={1}
                      step={0.01}
                      value={editorStyle.subtitleXRatio}
                      formatValue={(value) => value.toFixed(2)}
                      onChange={(value) =>
                        setStyleField("subtitleXRatio", value)
                      }
                    />
                    <PrecisionSliderField
                      label="Subtitle Y"
                      min={0}
                      max={1}
                      step={0.01}
                      value={editorStyle.subtitleYRatio}
                      formatValue={(value) => value.toFixed(2)}
                      onChange={(value) =>
                        setStyleField("subtitleYRatio", value)
                      }
                    />
                    <PrecisionSliderField
                      label="Subtitle Max Width"
                      min={0.2}
                      max={1}
                      step={0.01}
                      value={editorStyle.subtitleMaxWidthRatio}
                      formatValue={(value) => `${Math.round(value * 100)}%`}
                      onChange={(value) =>
                        setStyleField("subtitleMaxWidthRatio", value)
                      }
                    />
                    <FormControl fullWidth>
                      <InputLabel>Subtitle Align</InputLabel>
                      <Select
                        value={editorStyle.subtitleTextAlign}
                        label="Subtitle Align"
                        onChange={(event) =>
                          setStyleField(
                            "subtitleTextAlign",
                            event.target.value as "left" | "center" | "right",
                          )
                        }
                      >
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="center">Center</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Box
                sx={{
                  mt: 2,
                  pt: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {styleStatus === "loading" && "Loading style..."}
                  {styleStatus === "saved" && "Saved"}
                  {styleStatus === "error" && "Save error"}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSaveTemplate}
                  disabled={!styleReady || !selectedApp?.appId || !user?.email}
                >
                  Update Template
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            sx={{
              position: { md: "sticky" },
              top: { md: 88 },
              alignSelf: "flex-start",
              maxHeight: { md: "calc(100vh - 104px)" },
              overflow: { md: "auto" },
            }}
          >
            <Paper sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 1.5,
                }}
              >
                <Typography variant="h6">Live Preview</Typography>
                {isSlotDesignMode && activeTemplateSlot && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<NavigateBeforeIcon />}
                      onClick={() => moveActiveTemplateSlot(-1)}
                      disabled={
                        templateScreenSlots.findIndex(
                          (slot) => slot.slotId === activeTemplateSlotId,
                        ) <= 0
                      }
                    >
                      Previous
                    </Button>
                    <Chip
                      size="small"
                      label={`Slot ${
                        templateScreenSlots.findIndex(
                          (slot) => slot.slotId === activeTemplateSlotId,
                        ) + 1
                      }/${templateScreenSlots.length} • ${String(
                        activeTemplateSlot.order,
                      ).padStart(2, "0")}. ${activeTemplateSlot.name}`}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<NavigateNextIcon />}
                      onClick={() => moveActiveTemplateSlot(1)}
                      disabled={
                        templateScreenSlots.findIndex(
                          (slot) => slot.slotId === activeTemplateSlotId,
                        ) >=
                        templateScreenSlots.length - 1
                      }
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                Preview stays pinned while you edit style controls.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.8,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mb: 1.2,
                }}
              >
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label="Live render"
                />
                {isSlotDesignMode && activeTemplateSlot && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Editing slot ${String(activeTemplateSlot.order).padStart(2, "0")}`}
                  />
                )}
              </Box>
              {isSlotDesignMode && activeTemplateSlot && (
                <Alert severity="info" sx={{ mb: 1.2 }}>
                  Editing slot{" "}
                  {String(activeTemplateSlot.order).padStart(2, "0")} (
                  {activeTemplateSlot.name}). Style updates apply only to this
                  slot.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {previewDimension
                  ? `${previewDimension.platform.toUpperCase()} • ${
                      previewDimension.name
                    } (${previewDimension.width}x${previewDimension.height})`
                  : "No preview dimension"}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  minHeight: { xs: 320, md: 420 },
                  background:
                    "radial-gradient(80% 110% at 50% 8%, #1E293B 0%, #0B1220 60%, #05070B 100%)",
                  border: "1px solid #243045",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2.2,
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.03)",
                }}
              >
                {templatePreview ? (
                  <img
                    src={templatePreview}
                    alt="Template preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 700,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Adjust style settings to preview the template.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Create New Template
              </Typography>
              {renderRegisteredAppSelector()}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Creation Mode</InputLabel>
                <Select
                  value={templateCreateMode}
                  label="Creation Mode"
                  onChange={(event) =>
                    setTemplateCreateMode(event.target.value as "manual" | "ai")
                  }
                >
                  <MenuItem value="ai">AI Assisted</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Template Name"
                value={newTemplateName}
                onChange={(event) => setNewTemplateName(event.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Template Description"
                value={newTemplateDescription}
                onChange={(event) =>
                  setNewTemplateDescription(event.target.value)
                }
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Base Layout</InputLabel>
                <Select
                  value={newTemplateLayout}
                  label="Base Layout"
                  onChange={(event) =>
                    setNewTemplateLayout(
                      event.target.value as StoreTemplateLayoutId,
                    )
                  }
                >
                  <MenuItem value="simple_heading_device">
                    Simple Heading + Device
                  </MenuItem>
                  <MenuItem value="feature_spotlight_device">
                    Feature Spotlight
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Template Input Mode</InputLabel>
                <Select
                  value={newTemplateGenerationMode}
                  label="Template Input Mode"
                  onChange={(event) =>
                    setNewTemplateGenerationMode(
                      event.target.value as StoreScreenshotGenerationMode,
                    )
                  }
                >
                  <MenuItem value="single_source">Single image list</MenuItem>
                  <MenuItem value="screen_slots">
                    Template screen slots
                  </MenuItem>
                </Select>
              </FormControl>
              {newTemplateGenerationMode === "screen_slots" && (
                <TextField
                  fullWidth
                  type="number"
                  label="Screen Slot Count"
                  value={newTemplateSlotCount}
                  onChange={(event) =>
                    setNewTemplateSlotCount(
                      Math.max(
                        1,
                        Math.min(20, Number(event.target.value) || 8),
                      ),
                    )
                  }
                  sx={{ mb: 2 }}
                />
              )}

              {templateCreateMode === "ai" ? (
                <>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="AI Creative Direction (Optional)"
                    value={aiTemplatePrompt}
                    onChange={(event) =>
                      setAiTemplatePrompt(event.target.value)
                    }
                    sx={{ mb: 2 }}
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GenerateIcon />}
                    onClick={handleGenerateTemplateWithAI}
                    disabled={templateCreateLoading || !selectedApp}
                    sx={{ mb: 1.5 }}
                  >
                    {templateCreateLoading
                      ? "Generating Style..."
                      : "Generate Template Style with AI"}
                  </Button>
                </>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setDraftTemplateStyle({ ...editorStyle })}
                  sx={{ mb: 1.5 }}
                >
                  Use Current Editor Style
                </Button>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleCreateCustomTemplate}
                disabled={templateActionLoading || !selectedApp}
              >
                {templateActionLoading
                  ? "Saving Template..."
                  : "Save Custom Template"}
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Live Draft Preview
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                This preview updates as AI or manual draft style values change.
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  minHeight: 320,
                  bgcolor: "#0F172A",
                  border: "1px solid #1E293B",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                {templateCreatorPreview ? (
                  <img
                    src={templateCreatorPreview}
                    alt="Template creator preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 520,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Generate or edit template style to preview it here.
                  </Typography>
                )}
              </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Draft Style Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Generated draft style is ready. Save it, then open Template
                Editor for full fine-tuning.
              </Typography>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}
              >
                <Typography variant="caption">Background</Typography>
                <Typography variant="caption" color="text.secondary">
                  {draftTemplateStyle.backgroundType}
                </Typography>
                <Typography variant="caption">Gradient / Pattern</Typography>
                <Typography variant="caption" color="text.secondary">
                  {draftTemplateStyle.backgroundType === "gradient"
                    ? draftTemplateStyle.backgroundGradient
                    : draftTemplateStyle.backgroundPattern}
                </Typography>
                <Typography variant="caption">Heading Font</Typography>
                <Typography variant="caption" color="text.secondary">
                  {draftTemplateStyle.headingFontFamily}
                </Typography>
                <Typography variant="caption">Heading Size Ratio</Typography>
                <Typography variant="caption" color="text.secondary">
                  {draftTemplateStyle.headingSizeRatio.toFixed(3)}
                </Typography>
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Manage Saved Custom Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the Template Manager tab to rename, open, or delete your
                custom templates.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 3 && (
        <Paper sx={{ p: 3 }}>
          {renderRegisteredAppSelector()}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Template Manager
          </Typography>
          {customTemplates.length === 0 ? (
            <Alert severity="info">No custom templates yet for this app.</Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {customTemplates.map((template) => (
                <Card key={template.id} variant="outlined">
                  <CardContent sx={{ pb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Template Name"
                      value={templateRenameDrafts[template.id] ?? template.name}
                      onChange={(event) =>
                        setTemplateRenameDrafts((prev) => ({
                          ...prev,
                          [template.id]: event.target.value,
                        }))
                      }
                      sx={{ mb: 1.2 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {template.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ pt: 0 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedTemplate(cloneStoreTemplate(template));
                        setTabValue(1);
                      }}
                    >
                      Open In Editor
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleRenameCustomTemplate(template.id)}
                      disabled={templateManagerBusyId === template.id}
                    >
                      {templateManagerBusyId === template.id
                        ? "Saving..."
                        : "Rename"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleDeleteCustomTemplate(template.id)}
                      disabled={templateActionLoading}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {tabValue === 4 && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h6">Content Library</Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={loadLibraryAssets}
              disabled={libraryLoading}
            >
              {libraryLoading ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>

          {renderRegisteredAppSelector()}

          {libraryLoading ? (
            <Alert severity="info">Loading screenshot assets...</Alert>
          ) : libraryVersionGroups.length === 0 ? (
            <Alert severity="info">
              No saved screenshot assets for this app yet. Generate screenshots
              to auto-create versioned assets.
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {libraryVersionGroups.map((versionGroup) => (
                <Paper
                  key={versionGroup.version.versionId}
                  variant="outlined"
                  sx={{ p: 2.5 }}
                >
                  {(() => {
                    const zipJobState =
                      zipExportJobs[versionGroup.version.versionId];
                    const zipProgressPercent = Math.max(
                      0,
                      Math.min(
                        100,
                        Math.round(zipJobState?.progress.percent || 0),
                      ),
                    );

                    return (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 2,
                            mb: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 700 }}
                            >
                              Version v{versionGroup.version.versionNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {versionGroup.version.templateName ||
                                versionGroup.version.templateId}{" "}
                              • {versionGroup.totalAssets} assets
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created{" "}
                              {new Date(
                                versionGroup.version.createdAt,
                              ).toLocaleString()}
                            </Typography>
                            {versionGroup.version.zipCreatedAt && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Cloud ZIP ready since{" "}
                                {new Date(
                                  versionGroup.version.zipCreatedAt,
                                ).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                zipBusyVersionId ===
                                versionGroup.version.versionId ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DownloadIcon />
                                )
                              }
                              disabled={
                                zipBusyVersionId ===
                                versionGroup.version.versionId
                              }
                              onClick={() =>
                                handleDownloadVersionZip(versionGroup.version)
                              }
                            >
                              {zipBusyVersionId ===
                              versionGroup.version.versionId
                                ? `Preparing ZIP ${zipProgressPercent}%`
                                : versionGroup.version.zipDownloadUrl
                                  ? "Download Cloud ZIP"
                                  : "Create Cloud ZIP"}
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={
                                clientZipBusyVersionId ===
                                versionGroup.version.versionId ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DownloadIcon />
                                )
                              }
                              disabled={
                                clientZipBusyVersionId ===
                                versionGroup.version.versionId
                              }
                              onClick={() =>
                                handleDownloadVersionZipInBrowser(
                                  versionGroup.version,
                                )
                              }
                            >
                              {clientZipBusyVersionId ===
                              versionGroup.version.versionId
                                ? "Creating Browser ZIP..."
                                : "ZIP In Browser"}
                            </Button>
                          </Box>
                        </Box>

                        {zipBusyVersionId === versionGroup.version.versionId &&
                          zipJobState && (
                            <Box sx={{ mb: 2 }}>
                              <LinearProgress
                                variant="determinate"
                                value={zipProgressPercent}
                                sx={{ height: 6, borderRadius: 999, mb: 0.75 }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {zipJobState.progress.message}
                              </Typography>
                            </Box>
                          )}
                      </>
                    );
                  })()}

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                  >
                    {versionGroup.platforms.map((platformGroup) => (
                      <Box
                        key={`lib_${versionGroup.version.versionId}_${platformGroup.platform}`}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {platformGroup.platform.toUpperCase()}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.8,
                          }}
                        >
                          {platformGroup.locales.map((localeGroup) => (
                            <Box
                              key={`lib_${versionGroup.version.versionId}_${platformGroup.platform}_${localeGroup.locale}`}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 1 }}
                              >
                                Locale: {localeGroup.locale.toUpperCase()}
                              </Typography>
                              <Grid container spacing={2}>
                                {localeGroup.items.map((asset) => (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={asset.assetId}
                                  >
                                    <Card>
                                      <Box
                                        sx={{
                                          height: 330,
                                          overflow: "hidden",
                                          bgcolor: "action.hover",
                                        }}
                                      >
                                        <img
                                          src={asset.downloadUrl}
                                          alt={`${asset.platform}-${asset.dimension.name}`}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </Box>
                                      <CardContent sx={{ pb: 1 }}>
                                        <Typography variant="subtitle2">
                                          {formatScreenshotDeviceLabel(
                                            asset.dimension,
                                          )}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: "block" }}
                                        >
                                          {asset.dimension.width} x{" "}
                                          {asset.dimension.height}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 1, fontWeight: 700 }}
                                        >
                                          {asset.heading}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: "block" }}
                                        >
                                          {asset.subtitle}
                                        </Typography>
                                      </CardContent>
                                      <CardActions>
                                        <Button
                                          size="small"
                                          startIcon={<DownloadIcon />}
                                          onClick={() =>
                                            handleDownloadStoredAsset(asset)
                                          }
                                        >
                                          Download
                                        </Button>
                                        <Button
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleDeleteStoredAsset(
                                              asset.assetId,
                                            )
                                          }
                                        >
                                          Delete
                                        </Button>
                                      </CardActions>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {tabValue === 5 && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h6">Screenshot Jobs</Typography>
              <Typography variant="body2" color="text.secondary">
                Track screenshot batches that are queued, processing, completed,
                or failed.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (!selectedJobsAppFilter) return;
                setScreenshotJobs((prev) =>
                  prev.filter((job) =>
                    selectedJobsAppFilter === ALL_APPS_FILTER
                      ? false
                      : job.appId !== selectedJobsAppFilter,
                  ),
                );
              }}
              disabled={
                !selectedJobsAppFilter || visibleScreenshotJobs.length === 0
              }
            >
              {selectedJobsAppFilter === ALL_APPS_FILTER
                ? "Clear All Jobs"
                : "Clear App Jobs"}
            </Button>
          </Box>

          {renderScreenshotJobsAppSelector()}

          {!selectedJobsAppFilter ? (
            <Alert severity="info">
              Select a registered app, or choose All apps, to view screenshot
              jobs.
            </Alert>
          ) : persistedJobsLoading ? (
            <Alert severity="info">Loading screenshot jobs...</Alert>
          ) : visibleScreenshotJobs.length === 0 ? (
            <Alert severity="info">
              {selectedJobsAppFilter === ALL_APPS_FILTER
                ? "No screenshot jobs recorded across apps yet."
                : "No screenshot jobs recorded for this app yet."}
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {groupedVisibleScreenshotJobs.map((group) => {
                const groupPlatforms = Array.from(
                  new Set(
                    group.jobs.map((job) => job.targetPlatform.toUpperCase()),
                  ),
                );

                return (
                  <Accordion
                    key={group.key}
                    expanded={expandedScreenshotJobDates[group.key] ?? false}
                    onChange={(_, expanded) =>
                      setExpandedScreenshotJobDates((prev) => ({
                        ...prev,
                        [group.key]: expanded,
                      }))
                    }
                    disableGutters
                    elevation={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      overflow: "hidden",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 2.25,
                        py: 1.25,
                        bgcolor: "grey.50",
                        borderBottom: expandedScreenshotJobDates[group.key]
                          ? "1px solid"
                          : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800 }}
                          >
                            {group.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.jobs.length} screenshot job
                            {group.jobs.length === 1 ? "" : "s"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {groupPlatforms.map((platform) => (
                            <Chip
                              key={`${group.key}_${platform}`}
                              label={platform}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {group.jobs.map((job) => renderScreenshotJobCard(job))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </Paper>
      )}

      <Dialog
        open={publishDialogOpen}
        onClose={closePublishDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(180deg, rgba(232,245,233,0.92) 0%, rgba(255,255,255,1) 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}
              >
                <UploadIcon sx={{ color: "success.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {showPublishInProgressScreen
                    ? `Publishing to ${publishDestinationLabel}`
                    : `Publish Screenshots to ${publishDestinationLabel}`}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {showPublishInProgressScreen
                  ? `${publishDestinationLabel} publish is in progress. Keep this dialog open to monitor progress.`
                  : `Review current ${publishDestinationLabel} screenshots against the saved version before any screenshots are replaced.`}
              </Typography>
            </Box>
            <Chip
              label={
                showPublishInProgressScreen
                  ? screenshotPublishStatus === "queued"
                    ? "Queued"
                    : "Publishing"
                  : screenshotValidationStatus.label
              }
              color={
                showPublishInProgressScreen
                  ? "info"
                  : screenshotValidationStatus.color
              }
              variant={
                showPublishInProgressScreen
                  ? "filled"
                  : screenshotValidationStatus.color === "info"
                    ? "outlined"
                    : "filled"
              }
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#f8fafc" }}>
          {showPublishInProgressScreen ? (
            <Box
              sx={{
                mt: 1.25,
                minHeight: 380,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 1,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  width: "100%",
                  p: { xs: 2.25, sm: 3 },
                  borderRadius: 3,
                  borderColor: "info.light",
                  background:
                    "linear-gradient(180deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
                  boxShadow: "0 20px 42px rgba(15, 23, 42, 0.1)",
                }}
              >
                <Box
                  sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}
                >
                  <Box
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      position: "relative",
                    }}
                  >
                    <CircularProgress size={88} thickness={3.2} />
                    <Box
                      sx={{
                        position: "absolute",
                        width: 54,
                        height: 54,
                        borderRadius: "50%",
                        bgcolor: "rgba(30,136,229,0.14)",
                        animation: "publishPulse 1.8s ease-in-out infinite",
                        "@keyframes publishPulse": {
                          "0%": { transform: "scale(0.9)", opacity: 0.4 },
                          "50%": { transform: "scale(1.1)", opacity: 0.95 },
                          "100%": { transform: "scale(0.9)", opacity: 0.4 },
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, textAlign: "center", mb: 0.75 }}
                >
                  Publishing screenshots...
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mb: 2 }}
                >
                  {screenshotPublishResult?.message ||
                    `${publishDestinationLabel} locale batches are being processed.`}
                </Typography>

                <LinearProgress
                  variant={
                    screenshotPublishResult?.totalBatches
                      ? "determinate"
                      : "indeterminate"
                  }
                  value={
                    screenshotPublishResult?.totalBatches
                      ? screenshotPublishPercent
                      : 0
                  }
                  sx={{ height: 11, borderRadius: 999, mb: 1.25 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mb: 1.5 }}
                >
                  {screenshotPublishResult?.totalBatches
                    ? `${screenshotPublishResult.completedBatches || 0}/${screenshotPublishResult.totalBatches || 0} batches completed • ${screenshotPublishResult.processedLocales || 0}/${screenshotPublishResult.totalLocales || 0} locales processed`
                    : "Preparing publish job and validating locale batches..."}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}
                >
                  {screenshotPublishResult?.totalBatches ? (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={`${screenshotPublishPercent}% complete`}
                    />
                  ) : (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label="Initializing publish flow"
                    />
                  )}
                  {screenshotPublishEta && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={screenshotPublishEta}
                    />
                  )}
                  {activeScreenshotPublishBatch && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={`Active batch ${activeScreenshotPublishBatch.batchIndex + 1}`}
                    />
                  )}
                </Box>

                <Box
                  sx={{ display: "flex", justifyContent: "center", gap: 0.8 }}
                >
                  {[0, 1, 2].map((index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "info.main",
                        opacity: 0.4,
                        animation: "publishDotPulse 1.2s ease-in-out infinite",
                        animationDelay: `${index * 0.15}s`,
                        "@keyframes publishDotPulse": {
                          "0%, 100%": {
                            transform: "translateY(0px)",
                            opacity: 0.25,
                          },
                          "50%": { transform: "translateY(-5px)", opacity: 1 },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box
              sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Alert severity="info">
                Clicking `Publish` on the screenshot job does not publish
                immediately. You must validate the selected locale scope and
                explicitly confirm before the final publish call is enabled.
              </Alert>

              <Accordion
                expanded={publishSections.overview}
                onChange={handlePublishSectionToggle("overview")}
                sx={{ borderRadius: 2.5, overflow: "hidden" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Publish Overview
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The guarded 3-step flow before screenshots are replaced
                        on {publishDestinationLabel}.
                      </Typography>
                    </Box>
                    <Chip
                      label={`${publishTargetLocales.length} target locale${
                        publishTargetLocales.length === 1 ? "" : "s"
                      }`}
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
                    }}
                  >
                    <Grid container spacing={1.5}>
                      {[
                        {
                          step: "1",
                          title: "Choose target",
                          description:
                            "Pick all locales or one locale from the saved screenshot version.",
                          done: publishTargetLocales.length > 0,
                          active: publishTargetLocales.length > 0,
                        },
                        {
                          step: "2",
                          title: "Validate",
                          description: `Fetch current ${publishDestinationLabel} screenshots and compare them to the proposed screenshots.`,
                          done:
                            Boolean(screenshotValidationResult) &&
                            validationMatchesCurrentSelection &&
                            !validationHasErrors,
                          active: Boolean(screenshotValidationResult),
                        },
                        {
                          step: "3",
                          title: "Confirm",
                          description:
                            "Explicitly approve the reviewed screenshot replacement before publish.",
                          done: canPublishScreenshots,
                          active:
                            Boolean(screenshotValidationResult) &&
                            validationMatchesCurrentSelection &&
                            !validationHasErrors,
                        },
                      ].map((step) => (
                        <Grid item xs={12} md={4} key={step.step}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              height: "100%",
                              borderRadius: 2,
                              borderColor: step.done
                                ? "success.light"
                                : step.active
                                  ? "primary.light"
                                  : "divider",
                              bgcolor: step.done
                                ? "rgba(76, 175, 80, 0.08)"
                                : step.active
                                  ? "rgba(25, 118, 210, 0.08)"
                                  : "background.paper",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: step.done
                                    ? "success.main"
                                    : step.active
                                      ? "primary.main"
                                      : "grey.300",
                                  color:
                                    step.done || step.active
                                      ? "#fff"
                                      : "text.secondary",
                                  fontWeight: 800,
                                  fontSize: 13,
                                }}
                              >
                                {step.done ? (
                                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  step.step
                                )}
                              </Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700 }}
                              >
                                {step.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {step.description}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={publishSections.target}
                onChange={handlePublishSectionToggle("target")}
                sx={{ borderRadius: 2.5, overflow: "hidden" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Publish Target
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Verify the app, saved version, locale scope, and publish
                        destination.
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        publishMode === "single_locale"
                          ? "Single locale"
                          : publishMode === "selected_locales"
                            ? "Selected locales"
                            : "All locales"
                      }
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Accordion
                    expanded={publishSections.validate}
                    onChange={handlePublishSectionToggle("validate")}
                    sx={{ borderRadius: 2.5, overflow: "hidden" }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          pr: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800 }}
                          >
                            Validation Preview
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Review current {publishDestinationLabel} screenshots
                            against the proposed version.
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={
                            validationMatchesCurrentSelection &&
                            !validationHasErrors
                              ? "success"
                              : "warning"
                          }
                          variant="outlined"
                          label={
                            validationMatchesCurrentSelection &&
                            !validationHasErrors
                              ? "Validated"
                              : "Needs review"
                          }
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              App
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {publishTargetJob?.appName || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {publishDestination === "app_store_connect"
                                ? "Bundle ID / Store ID"
                                : "Package name"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, fontFamily: "monospace" }}
                            >
                              {screenshotPublishValidation.packageName ||
                                "Not configured"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Saved version
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {publishTargetJob?.savedVersionNumber
                                ? `v${publishTargetJob.savedVersionNumber}`
                                : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Last published
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {publishTargetLastPublishedDate ||
                                "Not yet published"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={publishSections.confirm}
                    onChange={handlePublishSectionToggle("confirm")}
                    sx={{ borderRadius: 2.5, overflow: "hidden" }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          pr: 1,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800 }}
                          >
                            Confirmation
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Explicit confirmation is required before publishing.
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={canPublishScreenshots ? "success" : "default"}
                          variant="outlined"
                          label={
                            canPublishScreenshots ? "Ready" : "Awaiting review"
                          }
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Grid container spacing={1.5}>
                          <Grid
                            item
                            xs={12}
                            md={publishMode === "all_locales" ? 12 : 6}
                          >
                            <FormControl fullWidth sx={{ mb: 1.5 }}>
                              <InputLabel>Destination</InputLabel>
                              <Select
                                value={publishDestination}
                                label="Destination"
                                onChange={(event) => {
                                  setPublishDestination(
                                    event.target
                                      .value as ScreenshotPublishDestination,
                                  );
                                  setScreenshotValidationResult(null);
                                  setScreenshotPublishResult(null);
                                  setPublishConfirmationChecked(false);
                                  setPublishOverrideChecked(false);
                                }}
                              >
                                <MenuItem value="google_play">
                                  Google Play
                                </MenuItem>
                                <MenuItem value="app_store_connect">
                                  App Store Connect
                                </MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth>
                              <InputLabel>Publish Mode</InputLabel>
                              <Select
                                value={publishMode}
                                label="Publish Mode"
                                onChange={(event) => {
                                  setPublishMode(
                                    event.target
                                      .value as GooglePlayScreenshotPublishMode,
                                  );
                                  setScreenshotValidationResult(null);
                                  setScreenshotPublishResult(null);
                                  setPublishConfirmationChecked(false);
                                }}
                              >
                                <MenuItem value="all_locales">
                                  Publish all locales in this screenshot version
                                </MenuItem>
                                <MenuItem value="single_locale">
                                  Publish one locale only
                                </MenuItem>
                                <MenuItem value="selected_locales">
                                  Publish selected locales
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {publishMode === "single_locale" && (
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Locale</InputLabel>
                                <Select
                                  value={publishLocale}
                                  label="Locale"
                                  onChange={(event) => {
                                    setPublishLocale(event.target.value);
                                    setScreenshotValidationResult(null);
                                    setScreenshotPublishResult(null);
                                    setPublishConfirmationChecked(false);
                                  }}
                                >
                                  {screenshotPublishValidation.publishableLocales.map(
                                    (locale) => (
                                      <MenuItem key={locale} value={locale}>
                                        {getLocaleDisplayLabel(locale)}
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                          {publishMode === "selected_locales" && (
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Locales</InputLabel>
                                <Select
                                  multiple
                                  value={publishSelectedLocales}
                                  label="Locales"
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setPublishSelectedLocales(
                                      Array.isArray(value)
                                        ? value
                                        : String(value).split(","),
                                    );
                                    setScreenshotValidationResult(null);
                                    setScreenshotPublishResult(null);
                                    setPublishConfirmationChecked(false);
                                  }}
                                  renderValue={(selected) =>
                                    Array.isArray(selected) &&
                                    selected.length > 0
                                      ? selected
                                          .map((locale) =>
                                            getLocaleDisplayLabel(locale),
                                          )
                                          .join(", ")
                                      : "Choose locales"
                                  }
                                >
                                  {screenshotPublishValidation.publishableLocales.map(
                                    (locale) => (
                                      <MenuItem key={locale} value={locale}>
                                        {getLocaleDisplayLabel(locale)}
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            mt: 1.5,
                          }}
                        >
                          {publishTargetLocales.map((locale) => (
                            <Chip
                              key={locale}
                              size="small"
                              variant="outlined"
                              label={getLocaleDisplayLabel(locale)}
                            />
                          ))}
                          {publishTargetLocales.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No target locales selected.
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>

                  {screenshotPublishValidation.blockers.length > 0 && (
                    <Alert severity="warning">
                      {screenshotPublishValidation.blockers.join(" ")}
                    </Alert>
                  )}

                  {screenshotPublishValidation.warnings.length > 0 && (
                    <Alert severity="info">
                      {screenshotPublishValidation.warnings.join(" ")}
                    </Alert>
                  )}

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800 }}
                        >
                          Validate Current {publishDestinationLabel} Screenshots
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fetch current {publishDestinationLabel} screenshot
                          counts from a draft edit and compare them with this
                          saved screenshot version.
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleValidateGooglePlayScreenshots}
                        disabled={
                          validatingGooglePlayPublish ||
                          screenshotPublishValidation.blockers.length > 0 ||
                          (publishMode === "single_locale" && !publishLocale) ||
                          (publishMode === "selected_locales" &&
                            publishTargetLocales.length === 0)
                        }
                      >
                        {validatingGooglePlayPublish ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          `Fetch Current ${publishDestinationLabel} Data`
                        )}
                      </Button>
                    </Box>
                  </Paper>

                  {screenshotValidationResult && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800 }}
                          >
                            Validation Preview
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Edit ID:{" "}
                            {screenshotValidationResult.editId || "N/A"}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={
                            validationMatchesCurrentSelection &&
                            !validationHasErrors
                              ? "success"
                              : "warning"
                          }
                          label={
                            validationMatchesCurrentSelection &&
                            !validationHasErrors
                              ? "Ready for confirmation"
                              : "Validation needs review"
                          }
                        />
                      </Box>

                      {validationOverviewItems.length > 0 && (
                        <Alert
                          severity={
                            validationOverviewErrors.length > 0
                              ? "error"
                              : "warning"
                          }
                          action={
                            <Button
                              size="small"
                              color="inherit"
                              startIcon={<CopyIcon />}
                              onClick={handleCopyValidationOverview}
                            >
                              {validationOverviewCopied ? "Copied" : "Copy"}
                            </Button>
                          }
                          sx={{ mb: 1.5, alignItems: "flex-start" }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, mb: 0.75 }}
                          >
                            Validation Overview
                          </Typography>
                          {validationOverviewErrors.length > 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                mb:
                                  validationOverviewMissing.length > 0 ? 1 : 0,
                              }}
                            >
                              {validationOverviewErrors.length} blocking issue
                              {validationOverviewErrors.length === 1
                                ? ""
                                : "s"}{" "}
                              found.
                            </Typography>
                          )}
                          {validationOverviewMissing.length > 0 && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {validationOverviewMissing.length} locale
                              {validationOverviewMissing.length === 1
                                ? ""
                                : "s"}{" "}
                              do not currently exist on{" "}
                              {publishDestinationLabel} yet. These are
                              create-on-publish states, not locale-map failures.
                            </Typography>
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.75,
                            }}
                          >
                            {validationOverviewItems.map((item) => (
                              <Typography
                                key={`overview-${item.locale}-${item.status}`}
                                variant="body2"
                              >
                                {getLocaleDisplayLabel(item.locale)}
                                {item.playLocale &&
                                item.playLocale !== item.locale
                                  ? ` -> ${item.playLocale}`
                                  : ""}
                                : {item.message}
                              </Typography>
                            ))}
                          </Box>
                        </Alert>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "stretch", sm: "center" },
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                          <InputLabel id="screenshot-validation-locale-filter-label">
                            Locale filter
                          </InputLabel>
                          <Select
                            labelId="screenshot-validation-locale-filter-label"
                            value={validationLocaleFilter}
                            label="Locale filter"
                            onChange={(event) =>
                              setValidationLocaleFilter(
                                String(event.target.value),
                              )
                            }
                          >
                            <MenuItem value={ALL_VALIDATION_LOCALES_FILTER}>
                              All validated locales
                            </MenuItem>
                            {screenshotValidationResult.results.map(
                              (result) => (
                                <MenuItem
                                  key={result.locale}
                                  value={result.locale}
                                >
                                  {getLocaleDisplayLabel(result.locale)}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary">
                          Expand only the locale and device groups you want to
                          review.
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                        }}
                      >
                        {filteredValidationResults?.map((result) => (
                          <Accordion
                            key={`validation-${result.locale}-${result.status}`}
                            expanded={
                              expandedValidationLocales[result.locale] ?? true
                            }
                            onChange={(_event, expanded) =>
                              setExpandedValidationLocales((prev) => ({
                                ...prev,
                                [result.locale]: expanded,
                              }))
                            }
                            sx={{ borderRadius: 1.75, overflow: "hidden" }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: {
                                    xs: "flex-start",
                                    sm: "center",
                                  },
                                  flexDirection: { xs: "column", sm: "row" },
                                  gap: 1,
                                  width: "100%",
                                  pr: 1,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 800 }}
                                  >
                                    {getLocaleDisplayLabel(result.locale)}
                                    {result.playLocale &&
                                    result.playLocale !== result.locale
                                      ? ` -> ${result.playLocale}`
                                      : ""}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {result.message}
                                  </Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  color={
                                    result.status === "success"
                                      ? "success"
                                      : result.status === "missing"
                                        ? "warning"
                                        : result.status === "skipped"
                                          ? "warning"
                                          : "error"
                                  }
                                  label={result.status}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Box
                                sx={{
                                  mb: result.assetGroups.length > 0 ? 1 : 0,
                                }}
                              >
                                {result.assetGroups.length > 0 ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1.25,
                                    }}
                                  >
                                    {result.assetGroups.map((group) => (
                                      <Accordion
                                        key={`${result.locale}-${group.imageType}`}
                                        expanded={
                                          expandedValidationGroups[
                                            `${result.locale}:${group.imageType}`
                                          ] ?? true
                                        }
                                        onChange={(_event, expanded) =>
                                          setExpandedValidationGroups(
                                            (prev) => ({
                                              ...prev,
                                              [`${result.locale}:${group.imageType}`]:
                                                expanded,
                                            }),
                                          )
                                        }
                                        sx={{
                                          borderRadius: 1.75,
                                          bgcolor: "rgba(248, 250, 252, 0.8)",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <AccordionSummary
                                          expandIcon={<ExpandMoreIcon />}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: {
                                                xs: "flex-start",
                                                sm: "center",
                                              },
                                              flexDirection: {
                                                xs: "column",
                                                sm: "row",
                                              },
                                              gap: 1,
                                              width: "100%",
                                              pr: 1,
                                            }}
                                          >
                                            <Box>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 800 }}
                                              >
                                                {group.label}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                              >
                                                {group.assetCount} proposed
                                                {typeof group.currentCount ===
                                                "number"
                                                  ? ` / ${group.currentCount} current`
                                                  : ""}
                                              </Typography>
                                            </Box>
                                            {group.currentImages &&
                                            group.proposedImages &&
                                            (group.currentImages.length ===
                                              group.proposedImages.length ||
                                              group.currentImages.length ===
                                                0) ? (
                                              <Chip
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                icon={<CheckCircleIcon />}
                                                label={
                                                  group.currentImages.length ===
                                                  0
                                                    ? "Ready to create"
                                                    : "Counts aligned"
                                                }
                                              />
                                            ) : (
                                              <Chip
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                label="Review counts"
                                              />
                                            )}
                                          </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0 }}>
                                          <Grid container spacing={1.25}>
                                            <Grid item xs={12} md={6}>
                                              {renderScreenshotGroupPreview(
                                                `Current on ${publishDestinationLabel}`,
                                                group.currentImages,
                                                `No current screenshots found on ${publishDestinationLabel} for this group.`,
                                                getLocaleDisplayLabel(
                                                  result.locale,
                                                ),
                                              )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                              {renderScreenshotGroupPreview(
                                                "Proposed from saved version",
                                                group.proposedImages,
                                                "No proposed screenshots are available for this group.",
                                                getLocaleDisplayLabel(
                                                  result.locale,
                                                ),
                                                true,
                                              )}
                                            </Grid>
                                          </Grid>
                                        </AccordionDetails>
                                      </Accordion>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No device groups returned for this locale.
                                  </Typography>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    </Paper>
                  )}

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={publishConfirmationChecked}
                          onChange={(event) =>
                            setPublishConfirmationChecked(event.target.checked)
                          }
                          disabled={
                            !screenshotValidationResult ||
                            !validationMatchesCurrentSelection ||
                            validationHasErrors
                          }
                        />
                      }
                      label={`I reviewed the validation results and want to publish ${publishTargetLocales.length} locale${
                        publishTargetLocales.length === 1 ? "" : "s"
                      } to ${publishDestinationLabel}.`}
                    />
                  </Paper>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={publishSections.danger}
                onChange={handlePublishSectionToggle("danger")}
                sx={{
                  borderRadius: 2.5,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "error.light",
                  bgcolor: "rgba(211, 47, 47, 0.04)",
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Danger
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Use this only when you intentionally want to publish
                        without fetching current {publishDestinationLabel} data.
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={
                        canOverridePublishScreenshots ? "warning" : "error"
                      }
                      variant="outlined"
                      label={
                        canOverridePublishScreenshots
                          ? "Override armed"
                          : "High risk"
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Alert severity="error" variant="outlined">
                      This path skips the current {publishDestinationLabel} data
                      validation step entirely. Use it only when you want a
                      direct publish and accept the risk of replacing
                      screenshots without reviewing the current state first.
                    </Alert>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: "error.light",
                        bgcolor: "rgba(211, 47, 47, 0.03)",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={publishOverrideChecked}
                            onChange={(event) => {
                              setPublishOverrideChecked(event.target.checked);
                              if (!event.target.checked) {
                                setPublishConfirmationChecked(false);
                              }
                            }}
                          />
                        }
                        label={`Skip Fetch Current ${publishDestinationLabel} Data validation and unlock direct publish.`}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 4.5, mt: 0.5 }}
                      >
                        This bypass is independent of validation. You can
                        publish straight away once this override is armed.
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {screenshotPublishResult && (
                <Accordion
                  expanded={publishSections.result}
                  onChange={handlePublishSectionToggle("result")}
                  sx={{ borderRadius: 2.5, overflow: "hidden" }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        pr: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800 }}
                        >
                          Publish Result
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Review the batch progress and per-locale outcome as
                          the {publishDestinationLabel} publish job runs.
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={
                          isScreenshotPublishRunning
                            ? "info"
                            : screenshotPublishResult.commitStatus ===
                                "committed"
                              ? "success"
                              : "warning"
                        }
                        variant="outlined"
                        label={
                          isScreenshotPublishRunning
                            ? screenshotPublishStatus === "queued"
                              ? "Queued"
                              : "Processing"
                            : screenshotPublishResult.commitStatus ===
                                "committed"
                              ? "Committed"
                              : "Not committed"
                        }
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {isScreenshotPublishRunning && (
                        <Paper
                          variant="outlined"
                          sx={{ p: 2.5, borderRadius: 2.5 }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, mb: 0.75 }}
                          >
                            Screenshot publish is running in the background
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1.5 }}
                          >
                            {screenshotPublishResult.message ||
                              `${publishDestinationLabel} locale batches are being processed in the background.`}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={screenshotPublishPercent}
                            sx={{ height: 10, borderRadius: 999, mb: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {screenshotPublishResult.completedBatches || 0}/
                            {screenshotPublishResult.totalBatches || 0} batches
                            completed
                            {" • "}
                            {screenshotPublishResult.processedLocales || 0}/
                            {screenshotPublishResult.totalLocales || 0} locales
                            processed
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              label={`${screenshotPublishPercent}% complete`}
                            />
                            {screenshotPublishEta && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={screenshotPublishEta}
                              />
                            )}
                          </Box>
                          {activeScreenshotPublishBatch && (
                            <Paper
                              variant="outlined"
                              sx={{
                                mt: 1.5,
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "info.50",
                                borderColor: "info.main",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  mb: 0.75,
                                }}
                              >
                                <CircularProgress size={16} />
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  Active batch
                                </Typography>
                                <Chip
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  label={`Batch ${activeScreenshotPublishBatch.batchIndex + 1}`}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {activeScreenshotPublishBatch.message ||
                                  "Processing the current locale batch."}
                              </Typography>
                            </Paper>
                          )}
                          {screenshotPublishBatchStates.length > 0 && (
                            <Paper
                              variant="outlined"
                              sx={{ mt: 1.5, p: 1.5, borderRadius: 2 }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, mb: 1.25 }}
                              >
                                Publish activity
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {screenshotPublishBatchStates.map((batch) => (
                                  <Box
                                    key={batch.batchIndex}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 1.5,
                                      p: 1.25,
                                      borderRadius: 1.5,
                                      bgcolor:
                                        batch.status === "completed"
                                          ? "success.50"
                                          : batch.status === "partial"
                                            ? "warning.50"
                                            : batch.status === "failed"
                                              ? "error.50"
                                              : batch.status === "processing"
                                                ? "info.50"
                                                : "grey.50",
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700, mb: 0.25 }}
                                      >
                                        Batch {batch.batchIndex + 1}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 0.5 }}
                                      >
                                        {(batch.locales || []).join(", ") ||
                                          "No locales"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {batch.message ||
                                          "Waiting for batch update."}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-end",
                                        gap: 0.5,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        color={
                                          batch.status === "completed"
                                            ? "success"
                                            : batch.status === "partial"
                                              ? "warning"
                                              : batch.status === "failed"
                                                ? "error"
                                                : batch.status === "processing"
                                                  ? "info"
                                                  : "default"
                                        }
                                        variant="outlined"
                                        label={
                                          batch.status === "queued"
                                            ? "Queued"
                                            : batch.status === "processing"
                                              ? "Processing"
                                              : batch.status === "completed"
                                                ? "Completed"
                                                : batch.status === "partial"
                                                  ? "Partial"
                                                  : "Failed"
                                        }
                                      />
                                      {batch.updatedAt && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {new Date(
                                            batch.updatedAt,
                                          ).toLocaleTimeString()}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            </Paper>
                          )}
                        </Paper>
                      )}

                      {isScreenshotPublishCommitted && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2.5,
                            borderRadius: 2.5,
                            bgcolor: "success.50",
                            borderColor: "success.main",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box sx={{ maxWidth: 620 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: "success.dark",
                                  mb: 0.75,
                                }}
                              >
                                Screenshots published to{" "}
                                {publishDestinationLabel}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {publishTargetJob?.appName || "This app"} was
                                updated for {successfulScreenshotPublishCount}{" "}
                                locale
                                {successfulScreenshotPublishCount === 1
                                  ? ""
                                  : "s"}
                                {skippedScreenshotPublishCount > 0
                                  ? `, with ${skippedScreenshotPublishCount} skipped locale${
                                      skippedScreenshotPublishCount === 1
                                        ? ""
                                        : "s"
                                    }.`
                                  : "."}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              color="success"
                              onClick={handleReturnToScreenshotJobs}
                            >
                              Back to Screenshot Jobs
                            </Button>
                          </Box>
                          <Grid container spacing={1.25} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={4}>
                              <Paper
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 2, height: "100%" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {publishDestination === "app_store_connect"
                                    ? "Bundle ID"
                                    : "Package"}
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {screenshotPublishResult.packageName}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Paper
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 2, height: "100%" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Saved version
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {publishTargetJob?.savedVersionNumber
                                    ? `Version ${publishTargetJob.savedVersionNumber}`
                                    : screenshotPublishResult.versionId}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Paper
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 2, height: "100%" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Locale outcome
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {successfulScreenshotPublishCount} succeeded
                                  {failedScreenshotPublishCount > 0
                                    ? `, ${failedScreenshotPublishCount} failed`
                                    : ""}
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Paper>
                      )}

                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800, mb: 1.5 }}
                        >
                          Publish Result
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.25,
                          }}
                        >
                          {screenshotPublishResult.results.map((result) => (
                            <Alert
                              key={`publish-${result.locale}-${result.status}`}
                              severity={
                                result.status === "success"
                                  ? "success"
                                  : result.status === "skipped"
                                    ? "warning"
                                    : "error"
                              }
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700 }}
                              >
                                {getLocaleDisplayLabel(result.locale)}
                                {result.playLocale &&
                                result.playLocale !== result.locale
                                  ? ` -> ${result.playLocale}`
                                  : ""}
                              </Typography>
                              <Typography variant="body2">
                                {result.message}
                              </Typography>
                            </Alert>
                          ))}
                        </Box>
                      </Paper>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {isScreenshotPublishRunning
              ? "Publish is running in the background. This dialog can stay open while batch progress updates."
              : isScreenshotPublishCommitted
                ? "Publish completed. You can return to Screenshot Jobs or review the detailed locale results."
                : canPublishScreenshots
                  ? "Validation is current and confirmation is complete."
                  : canOverridePublishScreenshots
                    ? "Override is enabled. Publish will continue even though validation still needs review."
                    : "Validation and explicit confirmation are required before publish."}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={closePublishDialog}>Close</Button>
            {showPublishInProgressScreen ? null : isScreenshotPublishFinished &&
              isScreenshotPublishCommitted ? (
              <Button
                variant="contained"
                color="success"
                onClick={handleReturnToScreenshotJobs}
              >
                Back to Screenshot Jobs
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handlePublishGooglePlayScreenshots}
                  disabled={
                    publishingToGooglePlay || !canOverridePublishScreenshots
                  }
                >
                  {publishingToGooglePlay ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Override and Publish"
                  )}
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePublishGooglePlayScreenshots}
                  disabled={publishingToGooglePlay || !canPublishScreenshots}
                >
                  {publishingToGooglePlay ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    `Publish to ${publishDestinationLabel}`
                  )}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={screenshotPreviewViewer.open}
        onClose={handleCloseScreenshotPreviewViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {screenshotPreviewViewer.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {screenshotPreviewViewer.localeLabel}
              {screenshotPreviewViewer.images.length > 0
                ? ` • ${screenshotPreviewViewer.currentIndex + 1} of ${screenshotPreviewViewer.images.length}`
                : ""}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseScreenshotPreviewViewer}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#0f172a", p: { xs: 1.5, md: 2 } }}>
          {screenshotPreviewViewer.images.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "40px 1fr 40px",
                  md: "56px 1fr 56px",
                },
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <IconButton
                onClick={() => handleShiftScreenshotPreviewViewer(-1)}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>

              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#111827",
                  border: "1px solid rgba(255,255,255,0.12)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: { xs: 360, md: 640 },
                }}
              >
                <img
                  src={
                    screenshotPreviewViewer.images[
                      screenshotPreviewViewer.currentIndex
                    ]?.url
                  }
                  alt={
                    screenshotPreviewViewer.images[
                      screenshotPreviewViewer.currentIndex
                    ]?.sourceName || screenshotPreviewViewer.title
                  }
                  style={{
                    maxWidth: "100%",
                    maxHeight: "76vh",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>

              <IconButton
                onClick={() => handleShiftScreenshotPreviewViewer(1)}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new Error(`Failed to read file ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function dimensionKey(
  dimension: Pick<DeviceDimension, "platform" | "name" | "width" | "height">,
) {
  return `${dimension.platform}:${dimension.name}:${dimension.width}x${dimension.height}`;
}

function isZipJobStale(zipJob?: StoreScreenshotZipExportJob): boolean {
  if (
    !zipJob ||
    (zipJob.status !== "queued" && zipJob.status !== "processing")
  ) {
    return false;
  }

  const lastUpdated = Date.parse(zipJob.updatedAt || zipJob.createdAt || "");
  if (!Number.isFinite(lastUpdated)) {
    return false;
  }

  return Date.now() - lastUpdated > ZIP_JOB_STALE_MS;
}

function isZipJobInProgress(zipJob?: StoreScreenshotZipExportJob): boolean {
  return (
    !!zipJob &&
    !isZipJobStale(zipJob) &&
    (zipJob.status === "queued" || zipJob.status === "processing")
  );
}

function createPlaceholderDataUrl(width: number, height: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(400, Math.min(width, 1200));
  canvas.height = Math.max(700, Math.min(height, 2200));
  const context = canvas.getContext("2d");
  if (!context) return "";
  context.fillStyle = "#D0D5DD";
  context.fillRect(0, 0, canvas.width, canvas.height);

  const message = "Upload a screenshot for realistic preview";
  const maxWidth = Math.round(canvas.width * 0.82);
  const minFontSize = 14;
  let fontSize = Math.max(20, Math.round(canvas.width * 0.048));
  let lines: string[] = [];

  while (fontSize >= minFontSize) {
    context.font = `600 ${fontSize}px "Inter", "Segoe UI", sans-serif`;
    lines = wrapTextToLines(context, message, maxWidth);
    if (lines.length <= 3) break;
    fontSize -= 2;
  }

  if (lines.length > 3) {
    lines = lines.slice(0, 3);
    const last = lines[2];
    lines[2] = `${last.slice(0, Math.max(0, last.length - 3)).trim()}...`;
  }

  const lineHeight = Math.round(fontSize * 1.28);
  const textBlockHeight = lines.length * lineHeight;
  const contentPadding = Math.round(canvas.width * 0.06);
  const boxWidth = maxWidth + contentPadding;
  const boxHeight = textBlockHeight + Math.round(contentPadding * 0.72);
  const boxX = Math.round((canvas.width - boxWidth) / 2);
  const boxY = Math.round((canvas.height - boxHeight) / 2);

  context.fillStyle = "rgba(15, 23, 42, 0.22)";
  context.beginPath();
  context.roundRect(
    boxX,
    boxY,
    boxWidth,
    boxHeight,
    Math.max(12, Math.round(canvas.width * 0.02)),
  );
  context.fill();

  context.fillStyle = "#334155";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = `600 ${fontSize}px "Inter", "Segoe UI", sans-serif`;

  let currentY =
    Math.round(canvas.height / 2) -
    Math.round(((lines.length - 1) * lineHeight) / 2);
  lines.forEach((line) => {
    context.fillText(line, Math.round(canvas.width / 2), currentY);
    currentY += lineHeight;
  });

  return canvas.toDataURL("image/png");
}

function getScreenshotJobStatusColor(
  status: ScreenshotJobRecord["status"],
): "default" | "info" | "success" | "error" | "warning" {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "queued") return "warning";
  if (status === "processing") return "info";
  return "default";
}

function wrapTextToLines(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function cloneStoreTemplate(
  template: StoreScreenshotTemplate,
): StoreScreenshotTemplate {
  const sanitizedStyle = sanitizeImportedStyle(template.style) || {
    ...DEFAULT_STORE_TEMPLATE_STYLE,
  };
  const sanitizedSlots = sanitizeImportedScreenSlots(template.screenSlots);
  return {
    ...template,
    style: cloneTemplateStyle(sanitizedStyle),
    generationMode: template.generationMode || "single_source",
    screenSlots: sanitizedSlots,
  };
}

function sanitizeImportedScreenSlots(
  input: unknown,
): StoreScreenshotTemplateScreenSlot[] {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .map((item, index) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const raw = item as Partial<StoreScreenshotTemplateScreenSlot>;
      const style = sanitizeImportedStyle(raw.style);
      if (!style) {
        return null;
      }
      const slotId =
        typeof raw.slotId === "string" && raw.slotId.trim()
          ? raw.slotId.trim()
          : `slot_${index + 1}`;
      return {
        slotId,
        order: clampNumber(Number(raw.order) || index + 1, 1, 200),
        name:
          typeof raw.name === "string" && raw.name.trim()
            ? raw.name.trim()
            : `Screen ${index + 1}`,
        enabled: raw.enabled !== false,
        style: cloneTemplateStyle(style),
        defaultHeading:
          typeof raw.defaultHeading === "string" ? raw.defaultHeading : "",
        defaultSubtitle:
          typeof raw.defaultSubtitle === "string" ? raw.defaultSubtitle : "",
      } as StoreScreenshotTemplateScreenSlot;
    })
    .filter(Boolean)
    .sort(
      (left, right) => (left!.order || 0) - (right!.order || 0),
    ) as StoreScreenshotTemplateScreenSlot[];
}

function buildDefaultScreenSlots(
  baseStyle: StoreScreenshotTemplateStyle,
  count = 8,
): StoreScreenshotTemplateScreenSlot[] {
  const safeCount = Math.max(1, Math.min(20, Number(count) || 8));
  return Array.from({ length: safeCount }, (_, index) => ({
    slotId: `slot_${index + 1}`,
    order: index + 1,
    name: `Screen ${index + 1}`,
    enabled: true,
    style: cloneTemplateStyle(baseStyle),
    defaultHeading: "",
    defaultSubtitle: "",
  }));
}

function cloneTemplateStyle(
  style: StoreScreenshotTemplateStyle,
): StoreScreenshotTemplateStyle {
  return {
    ...style,
    mainImageMask: { ...style.mainImageMask },
    mainImageTransform: { ...style.mainImageTransform },
    layerStack: Array.isArray(style.layerStack)
      ? style.layerStack.map((layer) => ({ ...layer }))
      : [],
  };
}

function SettingLabel({
  title,
  onReset,
}: {
  title: string;
  onReset: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 0.5,
      }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      <Tooltip title={`Reset ${title}`}>
        <IconButton size="small" onClick={onReset}>
          <ResetIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function EditorGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      square={false}
      sx={{
        border: "1px solid #E4E7EC",
        borderRadius: 1.5,
        mb: 1.6,
        bgcolor: "#FCFCFD",
        overflow: "hidden",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.6,
          py: 0.2,
          "& .MuiAccordionSummary-content": {
            my: 1.2,
          },
        }}
      >
        <Box>
          <Typography variant="subtitle2">{title}</Typography>
          {description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.2 }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.6, pb: 1.6, pt: 0.2 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function PrecisionSliderField({
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
  sx,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  sx?: any;
}) {
  const safeStep = Number(step) > 0 ? Number(step) : 0.01;
  const decimals = String(safeStep).includes(".")
    ? String(safeStep).split(".")[1].length
    : 0;
  const clamp = (next: number) =>
    Math.min(
      max,
      Math.max(min, Number(next.toFixed(Math.min(6, decimals + 2)))),
    );

  return (
    <Box sx={{ mb: 1.2, ...sx }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.25,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {formatValue ? formatValue(value) : value.toFixed(decimals)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          size="small"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(clamp(value - safeStep))}
          disabled={value <= min}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Slider
          min={min}
          max={max}
          step={safeStep}
          value={value}
          onChange={(_, nextValue) => onChange(clamp(Number(nextValue)))}
          sx={{ flex: 1 }}
        />
        <IconButton
          size="small"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(clamp(value + safeStep))}
          disabled={value >= max}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

function ColorField({
  value,
  onChange,
  swatches,
}: {
  value: string;
  onChange: (value: string) => void;
  swatches?: string[];
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1.2,
          alignItems: "center",
          mb: swatches?.length ? 1 : 0,
        }}
      >
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={{
            width: 44,
            height: 36,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 6,
            background: "transparent",
            padding: 0,
          }}
        />
        <TextField
          fullWidth
          value={value}
          onChange={(event) => onChange(event.target.value)}
          size="small"
        />
      </Box>
      {swatches && swatches.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {swatches.map((swatch) => (
            <Tooltip key={swatch} title={swatch}>
              <Box
                onClick={() => onChange(swatch)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: swatch,
                  border:
                    value.toLowerCase() === swatch.toLowerCase()
                      ? "2px solid #111827"
                      : "1px solid #D0D5DD",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
}

function sanitizeImportedStyle(
  input: unknown,
): StoreScreenshotTemplateStyle | null {
  if (!input || typeof input !== "object") {
    return null;
  }
  const raw = input as Partial<StoreScreenshotTemplateStyle>;
  const safe = {
    ...DEFAULT_STORE_TEMPLATE_STYLE,
    ...raw,
  } as StoreScreenshotTemplateStyle;

  safe.headingFontFamily = normalizeFontFamilyValue(safe.headingFontFamily);
  safe.subtitleFontFamily = normalizeFontFamilyValue(safe.subtitleFontFamily);

  if (
    (safe.backgroundType !== "color" &&
      safe.backgroundType !== "pattern" &&
      safe.backgroundType !== "gradient") ||
    typeof safe.backgroundColor !== "string" ||
    ![
      "dot_grid",
      "diagonal_lines",
      "soft_waves",
      "aurora_glow",
      "diamond_weave",
    ].includes(safe.backgroundPattern) ||
    ![
      "sunset_blush",
      "hot_pink_glow",
      "ocean_depth",
      "mint_fresh",
      "royal_plum",
      "citrus_pop",
      "midnight_steel",
      "sky_lagoon",
      "rose_gold",
      "emerald_glow",
      "twilight_berry",
      "amber_sky",
      "violet_dream",
      "coral_reef",
      "arctic_mist",
      "lava_glow",
      "forest_canopy",
      "peach_bloom",
      "neon_night",
      "cobalt_sunrise",
      "lavender_haze",
    ].includes(safe.backgroundGradient) ||
    typeof safe.patternColor !== "string" ||
    !["gentle", "balanced", "dramatic"].includes(safe.waveStyle) ||
    typeof safe.headingColor !== "string" ||
    typeof safe.subtitleColor !== "string" ||
    typeof safe.headingFontFamily !== "string" ||
    typeof safe.subtitleFontFamily !== "string" ||
    !["normal", "italic"].includes(safe.headingFontStyle) ||
    !["normal", "italic"].includes(safe.subtitleFontStyle) ||
    !["left", "center", "right"].includes(safe.headingTextAlign) ||
    !["left", "center", "right"].includes(safe.subtitleTextAlign) ||
    !ANDROID_DEVICE_FRAME_OPTIONS.some(
      (device) => device.id === safe.androidDeviceFrameId,
    )
  ) {
    return null;
  }

  safe.showSubtitle = Boolean(safe.showSubtitle);
  safe.showDeviceFrame = safe.showDeviceFrame !== false;
  safe.useAbsoluteTextPlacement = Boolean(safe.useAbsoluteTextPlacement);
  safe.headingWeight = clampNumber(safe.headingWeight, 500, 900);
  safe.subtitleWeight = clampNumber(safe.subtitleWeight, 400, 900);
  safe.patternOpacity = clampNumber(safe.patternOpacity, 0.12, 0.8);
  safe.patternScale = clampNumber(safe.patternScale, 0.6, 1.8);
  safe.dotSizeScale = clampNumber(safe.dotSizeScale, 0.6, 2.6);
  safe.headingSizeRatio = clampNumber(safe.headingSizeRatio, 0.04, 0.09);
  safe.subtitleSizeRatio = clampNumber(safe.subtitleSizeRatio, 0.02, 0.05);
  safe.headingXRatio = clampNumber(safe.headingXRatio, 0, 1);
  safe.headingYRatio = clampNumber(safe.headingYRatio, 0, 1);
  safe.subtitleXRatio = clampNumber(safe.subtitleXRatio, 0, 1);
  safe.subtitleYRatio = clampNumber(safe.subtitleYRatio, 0, 1);
  safe.headingMaxWidthRatio = clampNumber(safe.headingMaxWidthRatio, 0.2, 1);
  safe.subtitleMaxWidthRatio = clampNumber(safe.subtitleMaxWidthRatio, 0.2, 1);
  safe.headingTopRatio = clampNumber(safe.headingTopRatio, 0.03, 0.14);
  safe.subtitleOffsetRatio = clampNumber(safe.subtitleOffsetRatio, 0.02, 0.1);
  safe.frameWidthRatio = clampNumber(safe.frameWidthRatio, 0.62, 0.86);
  safe.frameHeightRatio = clampNumber(safe.frameHeightRatio, 0.6, 0.88);
  safe.frameTopRatio = clampNumber(safe.frameTopRatio, 0.1, 0.24);
  safe.mainImageMask = {
    type: ["rounded_rect", "circle", "squircle", "arch_wave_top"].includes(
      safe.mainImageMask?.type as string,
    )
      ? (safe.mainImageMask.type as StoreCompositionMaskType)
      : "rounded_rect",
    radiusRatio: clampNumber(
      Number(safe.mainImageMask?.radiusRatio ?? 0.08),
      0,
      0.5,
    ),
    insetRatio: clampNumber(
      Number(safe.mainImageMask?.insetRatio ?? 0),
      0,
      0.28,
    ),
    waveDepthRatio: clampNumber(
      Number(safe.mainImageMask?.waveDepthRatio ?? 0.1),
      0,
      0.3,
    ),
  };
  safe.mainImageTransform = {
    xRatio: clampNumber(Number(safe.mainImageTransform?.xRatio ?? 0.5), 0, 1),
    yRatio: clampNumber(Number(safe.mainImageTransform?.yRatio ?? 0.5), 0, 1),
    scale: clampNumber(Number(safe.mainImageTransform?.scale ?? 1), 0.2, 2.4),
    rotationDeg: clampNumber(
      Number(safe.mainImageTransform?.rotationDeg ?? 0),
      -180,
      180,
    ),
    opacity: clampNumber(Number(safe.mainImageTransform?.opacity ?? 1), 0, 1),
  };
  safe.layerStack = Array.isArray(safe.layerStack)
    ? safe.layerStack
        .map((layer, index) => {
          const kind = layer?.kind;
          if (
            kind !== "background" &&
            kind !== "main_image" &&
            kind !== "overlay_image" &&
            kind !== "text_heading" &&
            kind !== "text_subtitle"
          ) {
            return null;
          }
          return {
            id:
              typeof layer?.id === "string" && layer.id.trim()
                ? layer.id.trim()
                : `layer_${index + 1}`,
            kind,
            visible: layer?.visible !== false,
            zIndex: typeof layer?.zIndex === "number" ? layer.zIndex : index,
            refId: typeof layer?.refId === "string" ? layer.refId : undefined,
          };
        })
        .filter(Boolean)
        .sort((left, right) => (left!.zIndex || 0) - (right!.zIndex || 0))
        .map((layer, index) => ({
          ...layer!,
          zIndex: index,
        }))
    : DEFAULT_STORE_TEMPLATE_STYLE.layerStack.map((layer) => ({ ...layer }));
  if (isLegacyDefaultLayerStack(safe.layerStack)) {
    safe.layerStack = [];
  }

  return safe;
}

function isLegacyDefaultLayerStack(
  layers: StoreScreenshotTemplateStyle["layerStack"],
): boolean {
  if (!Array.isArray(layers)) return true;
  const normalized = layers
    .filter((layer) => layer.kind !== "overlay_image")
    .sort((left, right) => (left.zIndex || 0) - (right.zIndex || 0));
  if (normalized.length === 0) return true;
  if (normalized.length !== 4) return false;
  const kinds = normalized.map((layer) => layer.kind);
  const expected: StoreCompositionLayerKind[] = [
    "background",
    "main_image",
    "text_heading",
    "text_subtitle",
  ];
  return (
    kinds.every((kind, index) => kind === expected[index]) &&
    normalized.every((layer) => layer.visible !== false)
  );
}

function formatSlotSourceName(order: number, name: string): string {
  const safeOrder = Math.max(1, Number(order) || 1);
  const label = (name || "screen")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return `${String(safeOrder).padStart(2, "0")}_${label || "screen"}`;
}

function clampNumber(value: number, min: number, max: number): number {
  const num = Number(value);
  if (Number.isNaN(num)) return min;
  return Math.min(max, Math.max(min, num));
}

function getPresetSelectValue(current: number, options: number[]): string {
  const hit = options.find((option) => Math.abs(option - current) < 0.0001);
  return hit !== undefined ? String(hit) : "custom";
}

function ratioToFontPx(ratio: number, previewWidth: number): number {
  const resolvedWidth = Math.max(320, Number(previewWidth) || 0);
  const rawPx = Math.round(ratio * resolvedWidth);
  const nearest = FONT_SIZE_PX_OPTIONS.reduce((closest, current) =>
    Math.abs(current - rawPx) < Math.abs(closest - rawPx) ? current : closest,
  );
  return nearest;
}

function fontPxToRatio(fontPx: number, previewWidth: number): number {
  const resolvedWidth = Math.max(320, Number(previewWidth) || 0);
  return fontPx / resolvedWidth;
}

function normalizeFontFamilyValue(value: string): string {
  const fallback = '"Inter", "Segoe UI", sans-serif';
  const raw = (value || "").trim();
  if (!raw) return fallback;

  const exact = POLISHED_FONT_OPTIONS.find((font) => font.value === raw);
  if (exact) return exact.value;

  const lower = raw.toLowerCase();
  const aliasMap: Array<{ includes: string; value: string }> = [
    {
      includes: "sf pro display",
      value: '"SF Pro Display", "Inter", "Segoe UI", sans-serif',
    },
    {
      includes: "sf pro text",
      value: '"SF Pro Text", "Inter", "Segoe UI", sans-serif',
    },
    {
      includes: "plus jakarta sans",
      value: '"Plus Jakarta Sans", "Inter", "Segoe UI", sans-serif',
    },
    {
      includes: "general sans",
      value: '"General Sans", "Inter", "Segoe UI", sans-serif',
    },
    {
      includes: "manrope",
      value: '"Manrope", "Inter", "Segoe UI", sans-serif',
    },
    { includes: "sora", value: '"Sora", "Inter", "Segoe UI", sans-serif' },
    { includes: "inter", value: '"Inter", "Segoe UI", sans-serif' },
  ];

  const aliasHit = aliasMap.find((item) => lower.includes(item.includes));
  return aliasHit?.value || fallback;
}
